import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, useId } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Badge, Accordion, Table } from 'react-bootstrap';
import { Plus, Trash2, Download, AlertCircle, Layers3, Columns2, Paperclip, FileText, Check } from 'lucide-react';
import CreatableSelect from 'react-select/creatable';
import {
  FormFieldSelect,
  MultiValueSelect,
  SingleSearchSelect,
  getUniqueValues,
  materialSelectStyles,
  optionsFromChildren,
  splitStoredMultiValue,
} from './common/select';
import { isIsccStandard, isIsccPlusStandard } from '../utils/isccStandards';
import {
  ANNEX_1_COLUMNS,
  buildEmptyMaterialRow,
  isAnnex1Active,
  resolveMaterialColumns,
  supportsAnnex1,
} from '../utils/isccMaterialSchemes';
import ReactQuill from 'react-quill';
import api from '../api';
import { saveDocumentFormData, getStandardRequirements, getStandards } from '../services/auditFindingsService';
import { deleteEvidence, downloadEvidence, getEvidenceByProjectAndScope } from '../services/auditFormEvidenceService';
import { exportFormToPDF } from '../utils/pdfExporter';
import { useAutoSaveForm } from '../hooks/useAutoSaveForm';
import { useProjectAuditData } from '../hooks/useProjectAuditData';

// optionsFromChildren now lives in ./common/select; re-exported here because
// DocumentFormModal.fapp02.test.js requires it from this module.
export { optionsFromChildren };
import { useFormDefaults } from '../hooks/useFormDefaults';
import AuditEvidenceModal from './AuditEvidenceModal';
import { shouldAutoSaveDocumentForm } from './documentFormPersistenceUtils';
import { buildScopeRequestKey, shouldLoadScopeRequest } from './documentFormScopeRequestUtils';
import { extractCertificateNumberDefault, resolveActiveCycleAuditType } from './auditCycleContextUtils';
import { buildAuditObjective } from './auditObjectiveUtils';
import { shouldShowFinalCertificationDecision } from './documentFormStageUtils';
import { getDocumentFormFieldErrors, getDocumentFormValidationError, getFirstInvalidDocumentFormField } from './documentFormValidationUtils';
import {
  buildMethodologyContext,
  buildSplitPersistedForm,
  buildSplitStandardFormMap,
  getMethodologyType,
  METHODOLOGY_TYPES,
  OUTPUT_MODES,
  readSplitStandardForms,
  resolveSplitFormForMember,
  resolveSplitStandardTargets,
  seedSplitStandardForm,
  stripSplitStandardForms,
  supportsMethodologyOutputChoice,
  supportsMethodologyOutputMode,
} from '../pages/AuditProcess/auditMethodologyUtils';
import '../styles/pdfExport.css';
import '../styles/formEnhancements.css';
import 'react-quill/dist/quill.snow.css';

// ── Doc codes that support structured form input (not file upload) ─────────
export const FORM_DOC_CODES = new Set([
  'FAPP-01 Audit Plan',
  'Audit Summary Report',
  'FAPP-06 Certificate Information Form',
  'FAPP-06 CIF (signed by client)',
  'FAPP-07 Finding Form',
  'FAPP-02 Three Years Audit Program',
  'FAPP-03 Attendance List',
  'FAPP-04 Audit Notes',
  'FAPP-05 Audit Report',
  'FAPP-08 Change Notice Form',
  'FAPP-07 Finding Form (accepted)',
  'FTRD-01 Technical Review and Certification Decision',
  'FTRD-02 Certificate Review and Production',
]);

// FTRD-01's own "Stage 1 Decision" dropdown (inside the form, TechnicalReviewForm above) is
// the single source of truth for the Save button's behavior on a Stage 1 scope — see
// handleSave below. Kept as one constant since both the form body and the modal's save
// routing need to agree on the exact document code string.
const FTRD_01_DOC_CODE = 'FTRD-01 Technical Review and Certification Decision';

const toDateInputValue = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : '';
};

const formatDateInputValue = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const todayDateInputValue = () => formatDateInputValue(new Date());

const addYearsToDateInputValue = (value, years) => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setFullYear(date.getFullYear() + years);
  return formatDateInputValue(date);
};

const applyCertificateDateDefaults = (formData = {}) => {
  const today = todayDateInputValue();
  const expiry = addYearsToDateInputValue(today, 3);
  const next = { ...formData };
  const fill = (field, value) => {
    if (!String(next[field] || '').trim()) next[field] = value;
  };
  fill('initialDate', today);
  fill('latestCertificationDate', today);
  fill('issueDate', today);
  fill('expiredDate', expiry);
  fill('validFrom', today);
  fill('validTo', expiry);
  return next;
};

// ── Doc codes that also support the Evidence attach/preview/download panel ─
// FAPP-06 needs this for the signed CIF scan (client + lead auditor
// signature evidence); the backend evidence API is generic per documentCode.
export const EVIDENCE_ENABLED_DOC_CODES = new Set([
  'FAPP-04 Audit Notes',
  'FAPP-06 Certificate Information Form',
  'FAPP-06 CIF (signed by client)',
]);

const AUDIT_PLAN_DEFAULT_REQUESTS = [
  'Let us know if we should include any other additional processes or activities in this audit plan',
  'Notify the Lead Auditor of any significant organizational / management system changes since the last audit',
  'Let us know any issue related to the health and safety of the audit teams, including any eventual personal protective equipment required',
];

const AUDIT_PLAN_DEFAULT_NOTES = [
  'Audit Times and sequence may be adjusted in the light of findings and audit needs',
  'Process Owners will be completed and/or confirmed during the opening meeting',
  'Audit of all area could include Objectives, Improvement, Data collecting, Infrastructure and Work Environment, as appropriate.',
  "The company is requested to provide guides to accompany the auditor during the visit. The guide should be authorized to confirm audit findings on behalf of the company.",
  "The company is requested to make available a suitable workspace for the auditor's administration.",
  'Use of Mark, verification of effectiveness of corrective actions from last audit, treatment of complaint and risks/changes will be included during the audit.',
];

// Controlled values transcribed from the official FAPP-07 Finding Form
// workbook ("Acuan" sheet). The value keys are intentionally stable so existing
// saved forms keep rendering even if display labels are refined later.
const FAPP07_FINDING_CATEGORY_OPTIONS = [
  ['MAJOR_AOC_STAGE_II', 'Major Area of concern for Stage II', 'No response required. Action to be taken prior to Stage II'],
  ['MINOR_AOC_STAGE_II', 'Minor Area of concern for Stage II', 'No response required. Action to be taken prior to Stage II'],
  ['MINOR_NONCONFORMITY', 'Minor Nonconformity', 'Submit corrective action / CAP by: (date)'],
  ['MAJOR_NONCONFORMITY', 'Major Nonconformity', 'Submit corrective action plan (CAP) by: (date)'],
  ['OPPORTUNITY_FOR_IMPROVEMENT', 'Opportunity for improvement', 'No response required'],
];

const FAPP08_SCOPE_CHANGE_OPTIONS = [
  'Change of scope statement',
  'Change/revision of audit criteria(s)',
  'Surveillance interval',
  'Change in site(s)',
  'Product line changes',
  'Modification of activities',
  'Change of name/ownership',
  'Number of employees',
  'Suspension of certification',
  'Withdrawal of certification (by CBQA)',
];

const FAPP08_ADMINISTRATIVE_CHANGE_OPTIONS = [
  "Voluntary withdrawal / transfer of certification (Please attach client's request/notification)",
  'Non-renewal of certification',
];

export const normalizeFapp02FormData = (value = {}) => {
  const normalized = { ...value };
  // Older saves can contain programRows as an object keyed by row index. Keep
  // those records editable instead of letting the form crash on `.map()`.
  const rawProgramRows = normalized.programRows;
  const programRows = Array.isArray(rawProgramRows)
    ? rawProgramRows
    : rawProgramRows && typeof rawProgramRows === 'object'
      ? Object.keys(rawProgramRows)
        .sort((left, right) => Number(left) - Number(right))
        .map((key) => rawProgramRows[key])
      : [];
  normalized.programRows = programRows.map((row = {}) => {
      const rc = row.rc ?? row.recertification ?? row.sv3 ?? row.sv4 ?? row.sv5 ?? row.rc2 ?? '';
      return {
        ...row,
        rc,
      };
    });
  if (normalized.requirementCoverage && typeof normalized.requirementCoverage === 'object') {
    normalized.requirementCoverage = Object.fromEntries(
      Object.entries(normalized.requirementCoverage).map(([rowKey, coverage = {}]) => {
        const rc = coverage.rc ?? coverage.recertification ?? coverage.sv3 ?? coverage.sv4 ?? coverage.sv5 ?? coverage.rc2 ?? false;
        return [rowKey, { ...coverage, rc }];
      })
    );
  }
  if (normalized.cycleMeta && typeof normalized.cycleMeta === 'object') {
    const legacyRecert = normalized.cycleMeta.rc || normalized.cycleMeta.RC || normalized.cycleMeta.recertification || normalized.cycleMeta.SV3 || normalized.cycleMeta.sv3 || normalized.cycleMeta.SV4 || normalized.cycleMeta.sv4 || normalized.cycleMeta.SV5 || normalized.cycleMeta.sv5 || {};
    normalized.cycleMeta = {
      ...normalized.cycleMeta,
      rc: {
        ...legacyRecert,
        ...(normalized.cycleMeta.rc || {}),
      },
    };
  }
  return normalized;
};

export const toIsoDateString = (value) => {
  if (!value) return '';
  const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
};

export const buildDateRange = (startDate, endDate) => {
  const start = toIsoDateString(startDate);
  const end = toIsoDateString(endDate);
  if (!start || !end) return [];

  const startObj = new Date(`${start}T00:00:00Z`);
  const endObj = new Date(`${end}T00:00:00Z`);
  if (startObj > endObj) return [];

  const rows = [];
  const cursor = new Date(startObj.getTime());
  while (cursor <= endObj) {
    rows.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return rows;
};

const stringifyLines = (values = []) => values.filter(Boolean).join('\n');

const stripRichText = (html = '') => {
  if (!html) return '';
  if (typeof document === 'undefined') return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const node = document.createElement('div');
  node.innerHTML = String(html);
  return String(node.textContent || node.innerText || '').replace(/\u00a0/g, ' ').trim();
};

const buildDocumentMetadata = ({ docCode, projectId, projectMeta, scopeLabel, scopeContext, stages }) => ({
  documentCode: docCode,
  documentNumber: `${String(docCode || 'AUDIT').split(' ')[0]}-${projectMeta?.projectNumber || projectId || 'DRAFT'}`,
  projectId,
  projectNumber: projectMeta?.projectNumber || projectMeta?.projectCode || '',
  clientName: projectMeta?.clientName || projectMeta?.companyName || projectMeta?.locationName || '',
  scopeLabel: scopeLabel || '',
  stage: scopeContext?.stageKey || scopeContext?.auditType || '',
  standards: (Array.isArray(stages) ? stages : []).map((stage) => stage?.standardName || stage?.standardCode).filter(Boolean),
  generatedAt: new Date().toISOString(),
});

const looksLikeAccountIdentifier = (value) => {
  const text = String(value || '').trim();
  return text.includes('@') || /^[a-z0-9._-]+$/i.test(text) && (text.includes('.') || text.includes('_') || text.includes('-'));
};

const humanizeAccountIdentifier = (value) => String(value || '')
  .split('@')[0]
  .replace(/[._-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase())
  .trim();

export const getAuditorDisplayName = (auditor = {}) => {
  const firstName = auditor?.firstName || auditor?.first_name || auditor?.givenName || '';
  const lastName = auditor?.lastName || auditor?.last_name || auditor?.surname || '';
  const firstLast = `${firstName} ${lastName}`.trim();
  if (firstLast) return firstLast;

  const candidates = [auditor?.fullName, auditor?.employeeName, auditor?.label, auditor?.name];
  const humanName = candidates.find((candidate) => candidate && !looksLikeAccountIdentifier(candidate));
  if (humanName) return String(humanName).trim();

  return humanizeAccountIdentifier(auditor?.username || auditor?.email || auditor?.name || '');
};

const getAuditorIdentity = (auditor = {}) => ({
  id: String(auditor?.userId || auditor?.id || auditor?.employeeId || '').trim(),
  name: getAuditorDisplayName(auditor),
});

const getAuditorOptionsMap = (auditorOptions = []) => new Map(
  (Array.isArray(auditorOptions) ? auditorOptions : [])
    .map((auditor) => {
      const normalized = getAuditorIdentity(auditor);
      return normalized.id ? [normalized.id, { ...auditor, ...normalized }] : null;
    })
    .filter(Boolean)
);

const resolveSiteLocationName = (siteLocationIds = [], siteLocations = [], fallback = '') => {
  const names = (Array.isArray(siteLocationIds) ? siteLocationIds : [])
    .map((id) => siteLocations.find((site) => String(site?.id) === String(id)))
    .filter(Boolean)
    .map((site) => String(site?.siteName || site?.address || '').trim())
    .filter(Boolean);

  return names.length > 0 ? names.join(', ') : fallback;
};

const getScheduleDates = (schedule = {}) => {
  // The schedule's top-level start/end dates are the authoritative dates
  // selected by the scheduler. Detail timestamps may be serialized in UTC and
  // otherwise render one day earlier in the audit plan rundown.
  const scheduledRange = buildDateRange(schedule?.startDate, schedule?.endDate).filter(Boolean);
  if (scheduledRange.length > 0) return scheduledRange;

  const detailDates = [
    ...(Array.isArray(schedule?.mandayDetails) ? schedule.mandayDetails.map((row) => row?.date) : []),
    ...(Array.isArray(schedule?.siteLocationDetails) ? schedule.siteLocationDetails.map((row) => row?.date) : []),
  ]
    .map(toIsoDateString)
    .filter(Boolean);

  if (detailDates.length > 0) {
    return Array.from(new Set(detailDates)).sort();
  }

  return buildDateRange(schedule?.startDate, schedule?.endDate);
};

const buildAuditPlanScheduleRows = (auditSchedule = {}, auditorOptions = [], fallbackLocation = '') => {
  const scheduleRows = Array.isArray(auditSchedule?.auditSchedules) ? auditSchedule.auditSchedules : [];
  const siteLocations = Array.isArray(auditSchedule?.siteLocations) ? auditSchedule.siteLocations : [];
  const auditorMap = getAuditorOptionsMap(auditorOptions);
  const dateMap = new Map();

  scheduleRows.forEach((schedule) => {
    const dates = getScheduleDates(schedule);
    const auditorId = String(schedule?.employeeId || '').trim()
      || [...auditorMap.values()].find((auditor) => auditor.name && auditor.name.toLowerCase() === String(schedule?.employeeName || '').trim().toLowerCase())?.id
      || '';

    dates.forEach((date) => {
      if (!dateMap.has(date)) {
        dateMap.set(date, {
          auditorIds: new Set(),
          siteNames: new Set(),
          hasOnsite: false,
          hasOffsite: false,
        });
      }

      const current = dateMap.get(date);
      if (auditorId) current.auditorIds.add(auditorId);

      const siteDetail = (Array.isArray(schedule?.siteLocationDetails) ? schedule.siteLocationDetails : [])
        .find((row) => toIsoDateString(row?.date) === date);
      const siteName = resolveSiteLocationName(siteDetail?.siteLocationIds, siteLocations, fallbackLocation);
      if (siteName) current.siteNames.add(siteName);

      if (String(schedule?.auditType || '').trim().toLowerCase() === 'online') {
        current.hasOffsite = true;
      } else {
        current.hasOnsite = true;
      }
    });
  });

  if (dateMap.size === 0) {
    return [{
      type: 'Event',
      date: toIsoDateString(auditSchedule?.startDate || auditSchedule?.endDate || ''),
      timeFrom: '',
      timeTo: '',
      locationSite: fallbackLocation || 'On site',
      auditorIds: (Array.isArray(auditorOptions) ? auditorOptions : [])
        .filter((auditor) => auditor?.isScheduled)
        .map((auditor) => String(auditor?.userId || auditor?.id))
        .filter(Boolean),
      clientProcesses: '',
      processOwner: '',
      clausulAnnex: [],
    }];
  }

  return Array.from(dateMap.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, value]) => ({
      type: 'Event',
      date,
      timeFrom: value.hasOnsite && !value.hasOffsite ? '09:00' : '',
      timeTo: value.hasOnsite && !value.hasOffsite ? '17:00' : '',
      locationSite: Array.from(value.siteNames).join(', ') || fallbackLocation || (value.hasOffsite && !value.hasOnsite ? 'Off site' : 'On site'),
      auditorIds: Array.from(value.auditorIds),
      clientProcesses: '',
      processOwner: '',
      clausulAnnex: [],
    }));
};

const calculateAuditPlanMandays = (auditSchedule = {}) => {
  const scheduleRows = Array.isArray(auditSchedule?.auditSchedules) ? auditSchedule.auditSchedules : [];
  let onSite = 0;
  let offSite = 0;

  scheduleRows.forEach((schedule) => {
    const detailRows = Array.isArray(schedule?.mandayDetails) && schedule.mandayDetails.length > 0
      ? schedule.mandayDetails
      : [{ mandays: schedule?.mandays }];
    const total = detailRows.reduce((sum, row) => {
      const parsed = Number(row?.mandays);
      return Number.isFinite(parsed) && parsed > 0 ? sum + parsed : sum;
    }, 0);

    if (String(schedule?.auditType || '').trim().toLowerCase() === 'online') {
      offSite += total;
    } else {
      onSite += total;
    }
  });

  return {
    onSiteAuditDays: onSite > 0 ? String(onSite) : '',
    offSiteAuditDays: offSite > 0 ? String(offSite) : '',
  };
};

const resolveAuditSites = (projectMeta = {}, auditSchedule = {}, scopeLabel = '') => {
  const rawSites = [
    ...(Array.isArray(auditSchedule?.siteLocations) ? auditSchedule.siteLocations : []),
    ...(Array.isArray(auditSchedule?.sites) ? auditSchedule.sites : []),
    ...(Array.isArray(projectMeta?.sites) ? projectMeta.sites : []),
    ...(Array.isArray(projectMeta?.projectSites) ? projectMeta.projectSites : []),
  ];
  const seen = new Set();
  const sites = rawSites
    .map((site, index) => {
      const siteName = String(site?.siteName || site?.name || site?.siteFunction || site?.label || `Site ${index + 1}`).trim();
      const address = String(site?.siteAddress || site?.address || site?.location || '').trim();
      const scope = String(site?.scope || site?.scopeOfCertification || scopeLabel || '').trim();
      const key = `${siteName.toLowerCase()}::${address.toLowerCase()}`;
      if (!siteName && !address) return null;
      if (seen.has(key)) return null;
      seen.add(key);
      return {
        siteFunction: siteName || address,
        scope,
        address,
        source: 'multi-site',
      };
    })
    .filter(Boolean);

  if (sites.length > 0) return sites;

  const fallbackAddress = String(projectMeta?.address || projectMeta?.locationName || '').trim();
  if (!fallbackAddress) return [];
  return [{
    siteFunction: projectMeta?.locationName || 'Head Office',
    scope: scopeLabel || '',
    address: fallbackAddress,
    source: 'project',
  }];
};

const hasMeaningfulCertificateSite = (site = {}) => [
  site?.siteName,
  site?.siteFunction,
  site?.name,
  site?.label,
  site?.address,
  site?.siteAddress,
  site?.location,
].some((value) => String(value || '').trim());

export const normalizeCertificateSiteLocations = (siteLocations = [], scopeLabel = '') => (
  Array.isArray(siteLocations) ? siteLocations : []
)
  .filter(hasMeaningfulCertificateSite)
  .map((site, index) => {
    const siteName = String(site?.siteName || site?.siteFunction || site?.name || site?.label || `Site ${index + 1}`).trim();
    const address = String(site?.address || site?.siteAddress || site?.location || '').trim();
    const siteScope = String(site?.siteScope || site?.scope || site?.scopeOfCertification || scopeLabel || '').trim();
    const siteAdditionDate = toDateInputValue(
      site?.siteAdditionDate
      || site?.additionDate
      || site?.siteAddedDate
      || site?.site_added_date
      || '',
    );

    return {
      ...site,
      siteName,
      siteFunction: site?.siteFunction || siteName,
      address,
      siteScope,
      scope: site?.scope || siteScope,
      siteAdditionDate,
    };
  });

/**
 * Normalises FTRD-02 form data.
 *
 * `seedDefaults` separates two jobs that used to be one, which made a cleared field
 * refill itself: SEEDING (filling Client Name / Client Address / Certificate Scope /
 * materials from the proposal when the form has none) versus NORMALISING (reshaping
 * site rows). Seeding must happen once, when a form session opens -- it must NOT run on
 * every re-render, or clearing the last Certificate Scope chip immediately re-selects
 * the proposal scope and the operator cannot empty the field at all.
 */
export const normalizeFtrd02FormData = (
  formData = {},
  projectMeta = {},
  auditSchedule = {},
  scopeLabel = '',
  { seedDefaults = true } = {},
) => {
  const fallbackSites = normalizeCertificateSiteLocations(
    resolveAuditSites(projectMeta, auditSchedule, scopeLabel),
    scopeLabel,
  );
  const currentSites = normalizeCertificateSiteLocations(formData?.siteLocations, scopeLabel);
  const seed = (current, fallback) => (String(current || '').trim() || !seedDefaults
    ? current
    : fallback);
  const addressClient = seed(formData?.addressClient,
    projectMeta?.address || projectMeta?.companyAddress || '');
  const clientName = seed(formData?.clientName,
    projectMeta?.clientName || projectMeta?.companyName || projectMeta?.locationName || '');
  const certificateScope = seed(formData?.certificateScope,
    projectMeta?.proposalScope || projectMeta?.scope || scopeLabel || '');

  const toMaterialList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).map((item) => (
      typeof item === 'object' ? item?.name || item?.material || item : { name: item }
    ));
    if (value && typeof value === 'object') return Object.values(value).filter(Boolean);
    if (typeof value === 'string' && value.trim()) return value.split(',').map((name) => ({ name: name.trim() })).filter((item) => item.name);
    return [];
  };
  const existingMaterials = Array.isArray(formData?.isccMaterials)
    ? formData.isccMaterials
    : (Array.isArray(formData?.iscc_materials) ? formData.iscc_materials : []);
  const materialInputs = toMaterialList(projectMeta?.proposalInputMaterial);
  const materialOutputs = toMaterialList(projectMeta?.proposalOutputMaterial);
  const initialMaterials = (existingMaterials.length > 0 || !seedDefaults)
    ? existingMaterials
    : Array.from({ length: Math.max(materialInputs.length, materialOutputs.length) }, (_, index) => ({
      inputMaterial: materialInputs[index]?.name || materialInputs[index]?.material || '',
      outputMaterial: materialOutputs[index]?.name || materialOutputs[index]?.material || '',
      ghgOption: projectMeta?.ghgCalculation || '',
      inputScope: scopeLabel || '',
      outputScope: scopeLabel || '',
    }));

  return {
    ...formData,
    clientName,
    addressClient,
    certificateScope,
    isccMaterials: initialMaterials,
    siteLocations: currentSites.length > 0 ? currentSites : fallbackSites,
  };
};

const getAuditScheduleDateRange = (auditSchedule = {}) => {
  const dates = Array.from(new Set((Array.isArray(auditSchedule?.auditSchedules) ? auditSchedule.auditSchedules : [])
    .flatMap((schedule) => getScheduleDates(schedule))
    .filter(Boolean)))
    .sort();
  if (dates.length > 0) return { from: dates[0], to: dates[dates.length - 1], dates };

  const fallbackDates = buildDateRange(auditSchedule?.startDate, auditSchedule?.endDate);
  return {
    from: fallbackDates[0] || toIsoDateString(auditSchedule?.startDate || ''),
    to: fallbackDates[fallbackDates.length - 1] || toIsoDateString(auditSchedule?.endDate || ''),
    dates: fallbackDates,
  };
};

const isIso27Family = (values = []) => values
  .map((value) => String(value || '').toLowerCase())
  .some((value) => /\biso(?:\/iec)?\s*27\d{3}\b/.test(value.replace(/[-_:]+/g, ' ')) || /\b27\d{3}\b/.test(value));

const buildAuditReportDefaultNotes = (auditSchedule = {}, auditType = '') => {
  const { from, to } = getAuditScheduleDateRange(auditSchedule);
  const dateText = from && to && from !== to ? `${from} to ${to}` : (from || to || '');
  return [
    auditType ? `Audit activity: ${auditType}.` : '',
    dateText ? `Audit was performed according to the approved audit scheduling on ${dateText}.` : '',
    'Audit evidence, findings, and conclusions are documented based on records reviewed during the audit.',
  ].filter(Boolean).join('\n');
};

const buildAuditPlanTeamRole = (auditorOptions = [], auditSchedule = {}) => {
  const scheduledRows = Array.isArray(auditSchedule?.auditSchedules) ? auditSchedule.auditSchedules : [];
  const fromSchedule = scheduledRows
    .map((row) => {
      const name = String(row?.employeeName || '').trim();
      const role = String(row?.role || '').trim();
      if (!name) return '';
      return role ? `${name} (${role})` : name;
    })
    .filter(Boolean);

  if (fromSchedule.length > 0) {
    return Array.from(new Set(fromSchedule)).join('\n');
  }

  return (Array.isArray(auditorOptions) ? auditorOptions : [])
    .filter((auditor) => auditor?.isScheduled)
    .map((auditor) => {
      const normalized = getAuditorIdentity(auditor);
      const role = String(auditor?.role || '').trim();
      if (!normalized.name) return '';
      return role ? `${normalized.name} (${role})` : normalized.name;
    })
    .filter(Boolean)
    .join('\n');
};

const getAuditPlanInitialData = (projectMeta, stages, auditorOptions, auditSchedule, scopeLabel = '') => {
  const location = projectMeta?.locationName || '';
  const standards = filterStandardsToScope(
    stages?.map((stage) => stage?.standardName || stage?.standardCode || stage?.eaCode || '').filter(Boolean) || [],
    scopeLabel,
  );
  const scope = projectMeta?.proposalScope || projectMeta?.scope
    || stages?.map((stage) => stage?.scope).filter((value) => value && value !== '-').join(', ')
    || '';
  const clientCompany = projectMeta?.clientName || projectMeta?.companyName || projectMeta?.locationName || '';
  // Temporary fallback: seed PIC / Position from the lead's contact name so users
  // aren't retyping it, until FAPP-01 gets a proper contact/PIC picker of its own.
  const picPosition = projectMeta?.picName || '';
  const auditSite = projectMeta?.address || location || '';
  const auditStartDate = auditSchedule?.startDate ? new Date(auditSchedule.startDate).toISOString().split('T')[0] : '';
  const auditEndDate = auditSchedule?.endDate ? new Date(auditSchedule.endDate).toISOString().split('T')[0] : '';
  // A new audit plan must always have a document issue date. Seed it from the
  // project date when available, otherwise today, while still allowing the
  // auditor to change it before saving.
  const issueDate = toDateInputValue(projectMeta?.projectDate)
    || new Date().toISOString().slice(0, 10);
  const { onSiteAuditDays, offSiteAuditDays } = calculateAuditPlanMandays(auditSchedule);

  return {
    issueDate,
    revDate: '',
    executed: false,
    clientCompany,
    picPosition,
    auditSite,
    addressSameAsHeadOffice: 'yes',
    preparedBy: '',
    auditLanguage: 'Bahasa Indonesia',
    noOfShift: projectMeta?.shiftCount || '',
    shiftToBeAudited: '',
    auditStartDate,
    auditEndDate,
    onSiteAuditDays,
    offSiteAuditDays,
    auditType: '',
    applicableStandards: standards,
    auditCriteriaDetails: 'Processes and documentation of the management system developed by the company',
    scopeOfCertification: scope,
    naceCode: projectMeta?.eaCode || projectMeta?.sectorName || '',
    auditObjectives: '',
    auditTeamRole: buildAuditPlanTeamRole(auditorOptions, auditSchedule),
    preAuditRequests: stringifyLines(AUDIT_PLAN_DEFAULT_REQUESTS),
    additionalInformation: stringifyLines(AUDIT_PLAN_DEFAULT_NOTES),
    rundowns: buildAuditPlanScheduleRows(auditSchedule, auditorOptions, auditSite || location || 'On site'),
  };
};

const normalizeAuditPlanFormData = (formData = {}, {
  projectMeta,
  stages,
  auditorOptions,
  auditSchedule,
  scopeLabel,
  autoAuditType,
  standardSelectOptions,
  auditMethodology,
  methodologyGroup,
  scopeContext,
}, forceScheduleSync = false) => {
  const base = getAuditPlanInitialData(projectMeta, stages, auditorOptions, auditSchedule, scopeLabel);
  const next = { ...base, ...formData };
  let changed = false;

  if (!String(next.clientCompany || '').trim() && base.clientCompany) {
    next.clientCompany = base.clientCompany;
    changed = true;
  }
  if (!String(next.auditSite || '').trim() && base.auditSite) {
    next.auditSite = base.auditSite;
    changed = true;
  }
  if (!String(next.auditLanguage || '').trim()) {
    next.auditLanguage = 'Bahasa Indonesia';
    changed = true;
  }
  if (!String(next.onSiteAuditDays || '').trim() && base.onSiteAuditDays) {
    next.onSiteAuditDays = base.onSiteAuditDays;
    changed = true;
  }
  if (!String(next.offSiteAuditDays || '').trim() && base.offSiteAuditDays) {
    next.offSiteAuditDays = base.offSiteAuditDays;
    changed = true;
  }
  if ((!Array.isArray(next.rundowns) || next.rundowns.length === 0 || next.rundowns.every((row) => !String(row?.date || '').trim()) || forceScheduleSync) && base.rundowns.length > 0) {
    next.rundowns = base.rundowns;
    changed = true;
  }
  // Keep saved rundown dates aligned with the confirmed scheduler dates. A
  // prior UTC serialization bug could leave the form's top-level dates correct
  // while the first rundown displayed the previous calendar day.
  if (Array.isArray(next.rundowns) && base.rundowns.length > 0) {
    const scheduleDates = base.rundowns.map((row) => row.date || '');
    const rundownDates = next.rundowns.map((row) => row?.date || '');
    const datesMismatch = rundownDates.some((date, index) => (
      index < scheduleDates.length && date !== scheduleDates[index]
    ));
    if (datesMismatch) {
      // Keep operator-added events (for example one event per audited site).
      // Only synchronize the scheduler-owned date for rows that already have a
      // corresponding base schedule row; replacing the whole array here used
      // to drop extra multisite rundown rows during a parent refresh.
      next.rundowns = next.rundowns.map((row, index) => (
        index < base.rundowns.length
          ? { ...row, date: base.rundowns[index].date }
          : row
      ));
      changed = true;
    }
  }
  if (splitStoredMultiValue(next.applicableStandards).length === 0 && Array.isArray(standardSelectOptions) && standardSelectOptions.length > 0) {
    next.applicableStandards = standardSelectOptions.map((option) => option.label).filter(Boolean);
    changed = true;
  }
  if (!String(next.scopeOfCertification || '').trim() && base.scopeOfCertification) {
    next.scopeOfCertification = base.scopeOfCertification;
    changed = true;
  }
  if (!String(next.auditType || '').trim() && autoAuditType) {
    next.auditType = autoAuditType;
    changed = true;
  }
  if (!String(next.auditObjectives || '').trim()) {
    next.auditObjectives = buildAuditObjective({
      methodology: auditMethodology || methodologyGroup?.methodologyType,
      auditType: next.auditType || autoAuditType || scopeContext?.serviceItemName,
      stageKey: scopeContext?.stageKey,
      standards: splitStoredMultiValue(next.applicableStandards).length > 0
        ? splitStoredMultiValue(next.applicableStandards)
        : standardSelectOptions?.map((option) => option.label),
    });
    changed = true;
  }
  const activeScopeStandard = getActiveScopeStandard(scopeLabel, scopeContext);
  const outOfScopeStandards = (stages || [])
    .map((stage) => stage?.standardName || stage?.standardCode || '')
    .filter((standard) => standard && activeScopeStandard && !standardsMatchScope(standard, activeScopeStandard));
  const objectiveText = normalizeStandardToken(next.auditObjectives);
  if (activeScopeStandard && outOfScopeStandards.some((standard) => (
    objectiveText.includes(normalizeStandardToken(standard))
  ))) {
    next.auditObjectives = buildAuditObjective({
      methodology: auditMethodology || methodologyGroup?.methodologyType,
      auditType: next.auditType || autoAuditType || scopeContext?.serviceItemName,
      stageKey: scopeContext?.stageKey,
      standards: splitStoredMultiValue(next.applicableStandards).filter((standard) => standardsMatchScope(standard, activeScopeStandard)),
    });
    changed = true;
  }
  if (!String(next.auditTeamRole || '').trim()) {
    next.auditTeamRole = buildAuditPlanTeamRole(auditorOptions, auditSchedule);
    changed = true;
  }
  if (!String(next.preAuditRequests || '').trim()) {
    next.preAuditRequests = stringifyLines(AUDIT_PLAN_DEFAULT_REQUESTS);
    changed = true;
  }
  if (!String(next.additionalInformation || '').trim()) {
    next.additionalInformation = stringifyLines(AUDIT_PLAN_DEFAULT_NOTES);
    changed = true;
  }
  if (!String(next.naceCode || '').trim() && projectMeta?.eaCode) {
    next.naceCode = projectMeta.eaCode;
    changed = true;
  }

  return changed ? next : formData;
};

// ── Initial form state builders ────────────────────────────────────────────
const getInitialData = (docCode, projectMeta, stages, auditorOptions, auditSchedule, scopeLabel = '') => {
  const location = projectMeta?.locationName || '';
  const scope = projectMeta?.proposalScope || projectMeta?.scope
    || stages?.map((stage) => stage?.scope).filter((value) => value && value !== '-').join(', ')
    || '';
  // EA code identifies the sector/category, not the normative standard. Using it
  // here populated Applicable Standards with values such as "EAC-33L" and hid the
  // actual ISO standards on FAPP-02/FAPP-05 for grouped methodology projects.
  const standards = filterStandardsToScope(
    stages?.map((s) => s.standardName || s.standardCode || '').filter(Boolean) || [],
    scopeLabel,
  ).join(', ');
  const scheduleDates = getAuditScheduleDateRange(auditSchedule);
  const auditSites = resolveAuditSites(projectMeta, auditSchedule, scopeLabel || scope);
  const standardsForIsoCheck = [
    standards,
    scopeLabel,
    ...(Array.isArray(stages) ? stages.map((s) => s.standardName || s.standardCode || s.eaCode) : []),
  ];

  switch (docCode) {
    case 'FAPP-01 Audit Plan':
      return getAuditPlanInitialData(projectMeta, stages, auditorOptions, auditSchedule, scopeLabel);

    case 'Audit Summary Report':
      return {
        auditDate: '',
        auditType: '',
        applicableStandards: standards,
        auditLeadId: '',
        auditTeam: [{ name: '' }],
        majorNC: 0,
        minorNC: 0,
        ofi: 0,
        observations: 0,
        conformities: 0,
        summaryNarrative: '',
        conclusion: 'Recommend Certification',
        recommendations: '',
      };

    case 'FAPP-06 Certificate Information Form':
    case 'FAPP-06 CIF (signed by client)':
      return {
        clientName: projectMeta?.clientName || '',
        contact: '',
        position: '',
        phone: '',
        email: '',
        direction: '',
        address: projectMeta?.address || '',
        additionalLocations: auditSites,
        statementOfApplicability: '',
        soa: '',
        requiresSoa: isIso27Family(standardsForIsoCheck),
        exceptionsRequirement: '',
        exceptionsLocation: '',
        exceptionsActivities: '',
        exceptionsAdditionalComment: '',
        acknowledgeAuditor: '',
        acknowledgeDateOfAudit: '',
        leadAuditorSignature: '',
        clientRepresentativeName: '',
        clientRepresentativeSignature: '',
      };

    case 'FAPP-07 Finding Form':
      return {
        findingNumber: '',
        findingType: 'MAJOR',
        findingDate: scheduleDates.from || '',
        clauseReference: '',
        requirement: '',
        findingDetails: '',
        objectiveEvidence: '',
        auditorName: '',
        evidenceFinding: '',
        auditeeResponse: '',
        correction: '',
        rootCause: '',
        correctiveAction: '',
        planVerification: '',
        acceptanceComment: '',
        acceptedBy: '',
        acceptedDate: '',
        correctiveActionImplemented: '',
        effectiveness: '',
        nonconformityClosed: '',
        verifiedBy: '',
        targetDate: '',
        responsiblePerson: '',
        verifiedDate: '',
        status: 'OPEN',
      };

    case 'FAPP-02 Three Years Audit Program':
      return {
        schemaVersion: 'auditq-v2',
        programReference: '',
        clientCompany: projectMeta?.clientName || '',
        applicableStandards: standards,
        surveillanceInterval: '12',
        piiController: false,
        piiProcessor: false,
        programRows: [{
          clauseAnnex: '', name: scope || '', st1: '', st2: '',
          sv1: '', sv2: '', rc: '',
        }],
        siteSamplingRows: [{
          site: '', branchHierarchy: '', type: '', ia: '', sv1: '', sv2: '', rc: '', supplementary: '', extension: '',
        }],
        year1: { auditType: 'Initial Certification', tentativeDateFrom: '', tentativeDateTo: '', mandays: '', scope },
        year2: { auditType: 'Surveillance 1', tentativeDateFrom: '', tentativeDateTo: '', mandays: '', scope },
        year3: { auditType: 'Surveillance 2', tentativeDateFrom: '', tentativeDateTo: '', mandays: '', scope },
        preparedBy: '',
        amendedBy: '',
        approvedBy: '',
        notes: '',
      };

    case 'FAPP-03 Attendance List':
      return {
        date: scheduleDates.from || '',
        attendanceDates: scheduleDates.dates,
        auditType: 'Initial Certification',
        meetingType: 'Opening Meeting',
        location: auditSites.map((site) => site.siteFunction || site.address).filter(Boolean).join(', ') || location,
        attendees: [{
          name: '', position: '', email: '', phoneNumber: '',
          openingSignature: '', closingSignature: '',
        }],
      };

    case 'FAPP-04 Audit Notes':
      return {
        auditDate: scheduleDates.from || '',
        auditorName: '',
        processArea: '',
        notesHtml: '',
        notesText: '',
        notes: [{ clause: '', observation: '', evidence: '', conformity: 'CONFORM' }],
      };

    case 'FAPP-05 Audit Report':
      return {
        scopeOfCertification: scope,
        reportNumber: String(projectMeta?.clientId || projectMeta?.projectCode || ''),
        auditDateFrom: scheduleDates.from || '',
        auditDateTo: scheduleDates.to || '',
        auditType: 'Initial Certification',
        applicableStandards: standards,
        location: auditSites.map((site) => site.siteFunction || site.address).filter(Boolean).join(', ') || location,
        leadAuditorRecommendation: 'rec1',
        executiveSummary: 'We would like to thank all the audit participants for their assistance and cooperation that enable the audit to proceed smoothly and on schedule...',
        strengths: '',
        weakness: '',
        findingsSummary: { majorIssued: 0, majorClosed: 0, minorIssued: 0, minorClosed: 0, openNCs: 0 },
        previousActivity: { majorIssued: 0, majorClosed: 0, minorIssued: 0, minorClosed: 0, openNCs: 0 },
        showPreviousActivityInExport: true,
        auditResult: '',
        notesToNonConformity: 'Nonconformities identified during the audit must be addressed through correction, root cause analysis, and corrective action. Evidence of implementation and effectiveness is subject to auditor verification before closure.',
        statusOfPreviousFindings: 'Follow-up on findings issued at previous audit: All findings identified in previous audits have been closed...',
        auditSummary: '',
        complianceToISMS: '',
        verificationOfScope: '',
        verificationOfStatement: '',
        policyCommitment: '',
        conclusion: '',
        riskManagement: '',
        statementOfApplicability: '',
        objectives: '',
        notes: buildAuditReportDefaultNotes(auditSchedule, 'Initial Certification'),
        clientRepName: '',
        clientRepPosition: '',
        clientPhone: '',
        clientEmail: '',
        clientAddressContact: '',
        reportAcknowledgedByDifferent: '',
        part8FollowUp: '',
        part8Distribution: '',
      };

    case 'FAPP-08 Change Notice Form':
      return {
        schemaVersion: 'auditq-v2',
        noticeNumber: '',
        version: '',
        noticeDate: '',
        clientCompany: projectMeta?.clientName || '',
        changeType: 'Scope Change',
        changeDescription: '',
        effectiveDate: '',
        impactAssessment: '',
        requiredDocuments: '',
        previousScope: scope,
        newScope: '',
        changesAffecting: [],
        otherChange: '',
        notificationAuthorities: '',
        resultsReviewDecision: '',
        originator: '',
        completedAdministrativelyBy: '',
        approvedTechnicalManagerBy: '',
        requestedBy: '',
        approvedBy: '',
        remarks: '',
      };

    case 'FAPP-07 Finding Form (accepted)':
      return {
        findingReference: '',
        findingType: 'MAJOR',
        clauseReference: '',
        findingDescription: '',
        correctiveActionPlanned: '',
        correctiveActionImplemented: '',
        evidenceOfCompletion: '',
        targetDate: '',
        completionDate: '',
        verifiedBy: '',
        status: 'IN_PROGRESS',
        remarks: '',
      };

    case 'FTRD-01 Technical Review and Certification Decision': {
      const mc = () => ({ value: '', details: '' });
      return {
        reviewer: '',
        reviewDate: '',
        applicableStandards: standards,
        scopeOfCertification: scope,
        decision: '',
        certificationDecision: '',
        part1: {
          packageComplete: mc(),
          technicalReviewWaived: mc(),
          auditMandaysExecuted: mc(),
          auditTeamAssigned: mc(),
          gradingOfFindings: mc(),
          writingOfFindings: mc(),
          verificationOfNC: mc(),
          completenessRecertification: mc(),
          auditRecords: mc(),
          validityOfCertification: mc(),
          auditMandatoryElements: mc(),
          auditPlanning: mc(),
          exclusions: mc(),
        },
        part2: {
          capMinorAccepted: mc(),
          caMajorAccepted: mc(),
          approvalLeadAuditor: mc(),
        },
        part3: {
          acceptabilityOfResults: mc(),
          recertificationAudits: mc(),
          currentCertValid: mc(),
          leadAuditorApproved: mc(),
        },
      };
    }

    case 'FTRD-02 Certificate Review and Production':
      return applyCertificateDateDefaults(normalizeFtrd02FormData({
        certificateNumber: '',
        clientName: projectMeta?.clientName || '',
        addressClient: projectMeta?.address || '',
        naceCode: projectMeta?.eaCode || '',
        certificateScope: scope,
        siteLocations: auditSites,
        applicableForSubScope: 'No',
        verificationOfScope: '',
        applicableStandards: standards,
        initialDate: '',
        latestCertificationDate: '',
        issueDate: '',
        expiredDate: '',
        validFrom: '',
        validTo: '',
        certificationDecision: 'GRANT',
        producedBy: '',
        reviewedBy: '',
        approvedBy: '',
        signatureTitle: 'Certification Manager',
        remarks: '',
      }, projectMeta, auditSchedule, scopeLabel));

    default:
      return {};
  }
};

const isEmptyValue = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const mergeDefaultsIntoForm = (currentValue, defaultValue) => {
  if (Array.isArray(defaultValue)) {
    return isEmptyValue(currentValue) ? defaultValue : currentValue;
  }

  if (defaultValue && typeof defaultValue === 'object') {
    const currentObject = currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
      ? currentValue
      : {};
    const merged = { ...currentObject };
    Object.entries(defaultValue).forEach(([key, value]) => {
      merged[key] = mergeDefaultsIntoForm(currentObject[key], value);
    });
    return merged;
  }

  return isEmptyValue(currentValue) ? defaultValue : currentValue;
};

const normalizeStandardToken = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[:()]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const stripStandardVersionSuffix = (value) =>
  String(value || '')
    .replace(/\b(19|20)\d{2}\b/g, '')
    .replace(/[:()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const extractStandardFromScopeLabel = (scopeLabel) => {
  const parts = String(scopeLabel || '')
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts[1] : '';
};

// A project can contain several standards, but each audit document is opened
// for one selected scope. Keep the document standard set scoped to that audit
// instead of inheriting every standard on the project.
const getActiveScopeStandard = (scopeLabel = '', scopeContext = {}) => String(
  scopeContext?.standardName
  || scopeContext?.standard_name
  || extractStandardFromScopeLabel(scopeLabel)
  || ''
).trim();

const standardsMatchScope = (standard, scopeStandard) => {
  const candidate = normalizeStandardToken(stripStandardVersionSuffix(getStandardLabel(standard)));
  const target = normalizeStandardToken(stripStandardVersionSuffix(scopeStandard));
  return Boolean(candidate && target && (candidate === target || candidate.includes(target) || target.includes(candidate)));
};

const filterStandardsToScope = (standards = [], scopeLabel = '', scopeContext = {}) => {
  const values = Array.isArray(standards) ? standards.filter(Boolean) : [];
  const scopeStandard = getActiveScopeStandard(scopeLabel, scopeContext);
  if (!scopeStandard) return values;

  const filtered = values.filter((standard) => standardsMatchScope(standard, scopeStandard));
  return filtered.length > 0 ? filtered : [scopeStandard];
};

const normalizeFormStandardsToScope = (formData = {}, scopeLabel = '', scopeContext = {}) => {
  const scopeStandard = getActiveScopeStandard(scopeLabel, scopeContext);
  if (!scopeStandard || !formData || formData.applicableStandards == null) return formData;

  const current = splitStoredMultiValue(formData.applicableStandards);
  const scoped = current.filter((standard) => standardsMatchScope(standard, scopeStandard));
  const nextStandards = scoped.length > 0 ? scoped : [scopeStandard];
  const normalized = Array.isArray(formData.applicableStandards)
    ? nextStandards
    : nextStandards.join(', ');

  return JSON.stringify(formData.applicableStandards) === JSON.stringify(normalized)
    ? formData
    : { ...formData, applicableStandards: normalized };
};

const getStandardLabel = (standard) => {
  if (typeof standard === 'string') {
    const label = standard.trim();
    return /^\d+$/.test(label) ? '' : label;
  }
  return String(
    standard?.name
    || standard?.standardName
    || standard?.label
    || standard?.code
    || standard?.standardCode
    || ''
  ).trim();
};

const getStandardValue = (standard) => {
  if (typeof standard === 'string') return standard.trim();
  return String(
    standard?.id
    || standard?.standardId
    || standard?.code
    || standard?.standardCode
    || standard?.value
    || standard?.name
    || standard?.standardName
    || standard?.label
    || ''
  ).trim();
};

const buildSplitMemberFormData = (formData, methodologyContext, member) => {
  const standardName = String(member?.standardName || member?.standardId || '').trim();
  const memberContext = {
    ...methodologyContext,
    includedStandardIds: member?.standardId == null ? [] : [member.standardId],
    includedServiceItemIds: member?.serviceItemId == null ? [] : [member.serviceItemId],
    includedProjectServiceStandardIds: member?.projectServiceStandardId == null ? [] : [member.projectServiceStandardId],
    primaryStandardId: member?.standardId ?? null,
    memberStandards: [{
      standardId: member?.standardId ?? null,
      standardName,
      projectServiceStandardId: member?.projectServiceStandardId ?? null,
    }],
  };
  return {
    ...formData,
    ...(standardName ? { applicableStandards: Array.isArray(formData?.applicableStandards) ? [standardName] : standardName } : {}),
    methodologyContext: memberContext,
  };
};

const ScopeContextBanner = ({ scopeLabel, standards, autoAuditType, auditors }) => {
  const visibleStandards = getUniqueValues((standards || []).map(getStandardLabel).filter(Boolean));
  const assignedAuditors = getUniqueValues((auditors || [])
    .filter((auditor) => auditor?.isScheduled)
    .map((auditor) => getAuditorIdentity(auditor).name)
    .filter(Boolean));

  return (
    <div className="audit-scope-banner mb-4">
      <div className="audit-scope-banner__header">
        <div>
          <div className="audit-scope-banner__eyebrow">Current audit selected</div>
          <h6 className="audit-scope-banner__title mb-1">{scopeLabel || 'General project scope'}</h6>
          <p className="audit-scope-banner__subtitle mb-0">
            Auditor, standard, and requirement data follow the active audit process scope.
          </p>
        </div>
      </div>

      <div className="audit-scope-banner__meta">
        <span className="audit-scope-pill">Audit team: {assignedAuditors.length > 0 ? assignedAuditors.join(', ') : 'Not assigned'}</span>
        <span className="audit-scope-pill">Audit Type: {autoAuditType || 'Not defined'}</span>
      </div>

      {visibleStandards.length > 0 && (
        <div className="audit-scope-banner__chips">
          {visibleStandards.map((label) => (
            <span key={label} className="audit-scope-chip">{label}</span>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const DocumentFormModal = ({
  show,
  onHide,
  docCode,
  category,
  projectId,
  projectMeta,
  stages,
  auditorOptions,
  auditSchedule,
  existingData,
  onSaved,
  inline = false,
  onCancel,
  onDraftChange,
  buildPersistedFormData,
  formInstanceKey = '',
  scopeKey = '',
  scopeLabel = '',
  scopeContext = null,
  auditMethodology = '',
  methodologyGroup = null,
  readOnly = false,
}) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Save-time per-field validation messages, keyed by form field name.
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [selectedScope, setSelectedScope] = useState('');
  const [availableStandards, setAvailableStandards] = useState([]);
  const [availableAuditors, setAvailableAuditors] = useState(auditorOptions || []);
  const [autoAuditType, setAutoAuditType] = useState('');
  const [loadingScope, setLoadingScope] = useState(false);
  const [outputMode, setOutputMode] = useState(OUTPUT_MODES.COMBINED);
  // Split output edits one standard per tab inside the same form session; every
  // standard's complete form is held here and written to the document together.
  const [splitStandardForms, setSplitStandardForms] = useState({});
  const [activeSplitStandardKey, setActiveSplitStandardKey] = useState('');
  const [evidenceItems, setEvidenceItems] = useState([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [fapp02Template, setFapp02Template] = useState(null);
  // FTRD-01 Stage 1 Decision → Save routing (formerly a separate always-visible TR
  // decision panel outside the form; now driven by the form's own dropdown).
  const [showTrRevisionModal, setShowTrRevisionModal] = useState(false);
  const [trRevisionNotes, setTrRevisionNotes] = useState('');
  const [submittingTrRevision, setSubmittingTrRevision] = useState(false);
  const [trRevisionError, setTrRevisionError] = useState(null);
  // The shared content a standard tab starts from, captured when split output is
  // chosen (or loaded). Untouched tabs seed from this, never from another
  // standard's answers.
  const splitBaseFormRef = useRef({});
  const defaultsAppliedKeyRef = useRef('');
  const scopeDefaultsAppliedKeyRef = useRef('');
  const certificateNumberDefaultKeyRef = useRef('');
  const certificateDatesKeyRef = useRef('');
  const scopeRequestKeyRef = useRef('');
  const auditorOptionsRef = useRef(auditorOptions || []);

  useEffect(() => { auditorOptionsRef.current = auditorOptions || []; }, [auditorOptions]);

  // Load FAPP-02 Three Years Audit Program template (applicable standards + default mandatory rows + cycle columns)
  useEffect(() => {
    if (docCode === 'FAPP-02' && selectedScope && projectId) {
      api.get(`/audit/projects/${projectId}/scope/${selectedScope}/fapp02-template`)
        .then((res) => setFapp02Template(res.data?.data || null))
        .catch((err) => console.warn('Failed to load FAPP-02 template:', err));
    }
  }, [docCode, selectedScope, projectId]);

  // ── FAPP-02 nested-field helpers (persisted inside formData JSON) ──
  const getCycleMeta = (cycleKey, field) => formData?.cycleMeta?.[cycleKey]?.[field] || '';
  const setCycleMeta = (cycleKey, field, value) => {
    if (readOnly) return;
    setFormData((prev) => {
      const cycleMeta = { ...(prev.cycleMeta || {}) };
      cycleMeta[cycleKey] = { ...(cycleMeta[cycleKey] || {}), [field]: value };
      return { ...prev, cycleMeta };
    });
  };
  const getCoverage = (rowIdx, cycleKey) => Boolean(formData?.requirementCoverage?.[rowIdx]?.[cycleKey]);
  const toggleCoverage = (rowIdx, cycleKey) => {
    if (readOnly) return;
    setFormData((prev) => {
      const requirementCoverage = { ...(prev.requirementCoverage || {}) };
      const row = { ...(requirementCoverage[rowIdx] || {}) };
      row[cycleKey] = !row[cycleKey];
      requirementCoverage[rowIdx] = row;
      return { ...prev, requirementCoverage };
    });
  };
  const getRowNotes = (rowIdx) => formData?.requirementCoverage?.[rowIdx]?.notes || '';
  const setRowNotes = (rowIdx, value) => {
    if (readOnly) return;
    setFormData((prev) => {
      const requirementCoverage = { ...(prev.requirementCoverage || {}) };
      requirementCoverage[rowIdx] = { ...(requirementCoverage[rowIdx] || {}), notes: value };
      return { ...prev, requirementCoverage };
    });
  };

  const seededInstanceKeyRef = useRef('');
  const standardsCatalogRef = useRef(null);
  const standardsCatalogPromiseRef = useRef(null);
  const requirementOptionsCacheRef = useRef(new Map());

  // Smart data fetching for forms - reduces manual input
  const auditData = useProjectAuditData(projectId, projectMeta, stages);
  const formDefaults = useFormDefaults(docCode, projectMeta, auditData, auditData.auditors);

  // When inline=true, the form is "active" whenever docCode is provided
  const active = inline ? !!docCode : show;
  const effectiveScopeKey = selectedScope || scopeKey;
  const isFtrd01Stage1Scope = docCode === FTRD_01_DOC_CODE && String(effectiveScopeKey || '').endsWith(':s1');
  const autoSaveEnabled = shouldAutoSaveDocumentForm(docCode);
  const supportsOutputMode = supportsMethodologyOutputMode(docCode)
    && getMethodologyType(auditMethodology || methodologyGroup?.methodologyType) !== METHODOLOGY_TYPES.SINGLE
    && Boolean(scopeContext?.isMethodologyGroup);
  // Only a COMBINED audit may choose its output shape. An integrated audit is
  // required to produce ONE integrated document, so it is shown the rule instead
  // of a radio that buildMethodologyContext would silently overrule.
  const canChooseOutputMode = supportsMethodologyOutputChoice(
    docCode,
    auditMethodology || methodologyGroup?.methodologyType,
  ) && Boolean(scopeContext?.isMethodologyGroup);
  const splitStandardTargets = useMemo(() => (
    canChooseOutputMode ? resolveSplitStandardTargets(scopeContext) : []
  ), [canChooseOutputMode, scopeContext]);
  const isSplitOutput = canChooseOutputMode
    && outputMode === OUTPUT_MODES.SPLIT
    && splitStandardTargets.length > 0;
  const activeSplitTarget = (isSplitOutput
    && (splitStandardTargets.find((target) => target.standardKey === activeSplitStandardKey)
      || splitStandardTargets[0])) || null;
  const effectiveSplitStandardKey = activeSplitTarget?.standardKey || '';
  const methodologyContext = useMemo(() => (
    supportsOutputMode
      ? buildMethodologyContext({
        methodology: auditMethodology,
        methodologyGroup,
        scopeContext,
        outputMode,
        projectId,
      })
      : null
  ), [supportsOutputMode, auditMethodology, methodologyGroup, scopeContext, outputMode, projectId]);

  // Grouped FTRD-02 keeps several standards inside one saved document, so the parent decides
  // how the active standard's form is merged into that document before it is written. The
  // builder is read through a ref so a new closure on every parent render cannot restart the
  // auto-save debounce (which would then never fire).
  const persistedFormDataBuilderRef = useRef(buildPersistedFormData);
  const onDraftChangeRef = useRef(onDraftChange);
  const persistFormDataRef = useRef(null);
  useEffect(() => {
    persistedFormDataBuilderRef.current = buildPersistedFormData;
    onDraftChangeRef.current = onDraftChange;
  });
  // Every standard's form as it stands right now: the tabs already stored, with the
  // tab being edited overriding its own entry. This is what makes a save from one
  // standard's tab keep — never overwrite — what the other standards hold.
  const splitStandardFormMap = useMemo(() => (
    isSplitOutput
      ? buildSplitStandardFormMap(
        splitStandardTargets,
        splitStandardForms,
        effectiveSplitStandardKey,
        formData,
      )
      : null
  ), [isSplitOutput, splitStandardTargets, splitStandardForms, effectiveSplitStandardKey, formData]);

  // What the document is written with: the live tabs when the form is filled per
  // standard, otherwise whatever per-standard forms it already carried — a merged
  // document must not erase the answers a previous split pass stored. Gated on
  // canChooseOutputMode because grouped FTRD-02 stores its certificate tabs under
  // the same key and builds that container itself (buildPersistedFormData); this
  // must never write over it.
  const persistedSplitStandardForms = splitStandardFormMap
    || ((canChooseOutputMode && Object.keys(splitStandardForms).length > 0)
      ? splitStandardForms
      : null);

  // Rebuilt when the fields change AND when the form switches to another standard, so the
  // auto-save payload can never be attributed to the standard whose tab was open before.
  const formDataToPersist = useMemo(() => {
    const base = persistedFormDataBuilderRef.current
      ? persistedFormDataBuilderRef.current(formData)
      : formData;
    return persistedSplitStandardForms
      ? buildSplitPersistedForm(base, persistedSplitStandardForms)
      : base;
  // eslint-disable-next-line
  }, [formData, formInstanceKey, persistedSplitStandardForms]);

  const { isSaving: autoSaving, error: autoSaveError } = useAutoSaveForm(
    projectId,
    docCode,
    category,
    formDataToPersist,
    2000,
    autoSaveEnabled && !readOnly && active && Object.keys(formData).length > 0,
    effectiveScopeKey,
  );

  const instanceKey = `${scopeKey || ''}::${docCode || ''}`;
  // Which standard the certificate lookups (suggested number, certificate dates) are about. A
  // grouped scope key carries the METHODOLOGY GROUP, not a standard, so the standard whose tab
  // is open is passed in as formInstanceKey — without it every tab of an integrated audit asked
  // for, and displayed, the same standard's certificate.
  const certificateStandardId = String(formInstanceKey || '').trim()
    || String(effectiveScopeKey || '').replace(/:s[12]$/, '').split(':')[1]
    || '';
  const stageDerivedStandards = useMemo(() => {
    const standardsByValue = new Map();

    const appendStandard = (id, label) => {
      const normalizedId = String(id || '').trim();
      const normalizedLabel = String(label || '').trim();
      if (!normalizedLabel || /^\d+$/.test(normalizedLabel)) return;
      const value = normalizedId || normalizedLabel;
      standardsByValue.set(value, { id: value, name: normalizedLabel });
    };

    const activeScopeStandard = getActiveScopeStandard(scopeLabel, scopeContext);
    const scopedStages = activeScopeStandard
      ? (stages || []).filter((stage) => standardsMatchScope(stage?.standardName || stage?.standardCode, activeScopeStandard))
      : (stages || []);

    scopedStages.forEach((stage) => {
      appendStandard(stage?.standardId, stage?.standardName || stage?.standardCode);
    });

    appendStandard(
      scopeContext?.standardId,
      scopeContext?.standardName || extractStandardFromScopeLabel(scopeLabel),
    );
    const scopedMembers = activeScopeStandard
      ? (methodologyGroup?.memberStandards || []).filter((member) => standardsMatchScope(member?.standardName || member?.standardId, activeScopeStandard))
      : (methodologyGroup?.memberStandards || []);
    scopedMembers.forEach((member) => {
      appendStandard(member?.standardId, member?.standardName);
    });

    return Array.from(standardsByValue.values());
  }, [stages, scopeContext?.standardId, scopeContext?.standardName, scopeLabel, methodologyGroup?.memberStandards]);
  const stageDerivedStandardsRef = useRef(stageDerivedStandards);

  useEffect(() => { stageDerivedStandardsRef.current = stageDerivedStandards; }, [stageDerivedStandards]);

  const standardSelectOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    const activeScopeStandard = getActiveScopeStandard(scopeLabel, scopeContext);

    [...(Array.isArray(availableStandards) ? availableStandards : []), ...stageDerivedStandards].forEach((standard) => {
      const value = getStandardValue(standard);
      const label = getStandardLabel(standard);
      if (activeScopeStandard && !standardsMatchScope({ label, value }, activeScopeStandard)) return;
      if (!value || !label || seen.has(value)) return;
      seen.add(value);
      options.push({ value, label: label || value });
    });

    return options;
  }, [availableStandards, stageDerivedStandards, scopeLabel, scopeContext]);

  // The fields this form opens with. Kept as a function because a grouped FTRD-02 re-seeds it
  // when the operator switches standard, without starting a new editing session.
  const resolveSeededFormData = () => {
    const buildFromDefaults = () => {
      const initialData = getInitialData(docCode, projectMeta, stages, auditorOptions, auditSchedule, scopeLabel);
      return docCode === 'FAPP-02 Three Years Audit Program'
        ? normalizeFapp02FormData(initialData)
        : docCode === 'FTRD-02 Certificate Review and Production'
          ? applyCertificateDateDefaults(initialData)
          : initialData;
    };
    if (!existingData) {
      const initialData = getInitialData(docCode, projectMeta, stages, auditorOptions, auditSchedule, scopeLabel);
      return docCode === 'FAPP-02 Three Years Audit Program' ? normalizeFapp02FormData(initialData) : initialData;
    }
    try {
      const parsed = typeof existingData === 'string' ? JSON.parse(existingData) : existingData;
      const scopedParsed = normalizeFormStandardsToScope(parsed, scopeLabel, scopeContext);
      return docCode === 'FAPP-02 Three Years Audit Program'
        ? normalizeFapp02FormData(scopedParsed)
        : docCode === 'FTRD-02 Certificate Review and Production'
          ? applyCertificateDateDefaults(normalizeFtrd02FormData(scopedParsed, projectMeta, auditSchedule, scopeLabel))
          : scopedParsed;
    } catch {
      return buildFromDefaults();
    }
  };

  // Which output the saved document was written with. An explicitly persisted
  // methodologyContext always wins — including when it says `combined`, so a form
  // switched back to one merged document does not spring back to tabs because an
  // earlier split attempt left its per-standard forms behind. Only a document that
  // has never been through an explicit Save lacks that context (auto-save writes
  // the fields alone); there, the presence of per-standard forms is the signal.
  const resolveRestoredOutputMode = (storedSplitForms) => {
    const persistedMode = (() => {
      if (!existingData) return '';
      try {
        const parsed = typeof existingData === 'string' ? JSON.parse(existingData) : existingData;
        return parsed?.methodologyContext?.outputMode || '';
      } catch (_) {
        return '';
      }
    })();
    if (persistedMode === OUTPUT_MODES.SPLIT) return OUTPUT_MODES.SPLIT;
    if (persistedMode === OUTPUT_MODES.COMBINED) return OUTPUT_MODES.COMBINED;
    return Object.keys(storedSplitForms || {}).length > 0
      ? OUTPUT_MODES.SPLIT
      : OUTPUT_MODES.COMBINED;
  };

  useEffect(() => {
    if (active && docCode) {
      setError(null);
      setSuccess(false);
      // A new session must not inherit the previous document's validation
      // markers or its "Saved HH:MM" stamp.
      setFieldErrors({});
      setLastAutoSavedAt(null);
      setSelectedScope(scopeKey || '');
      setAvailableAuditors(auditorOptions || []);
      setAvailableStandards([]);
      setAutoAuditType('');
      defaultsAppliedKeyRef.current = '';
      scopeDefaultsAppliedKeyRef.current = '';
      seededInstanceKeyRef.current = `${docCode}::${scopeKey || ''}::${formInstanceKey || ''}`;

      const seeded = resolveSeededFormData();
      // Only a document that may actually be filled per standard reads the
      // per-standard container. Grouped FTRD-02 stores its certificate tabs under
      // the same key but owns them through buildPersistedFormData, so its data is
      // left exactly as the parent handed it over.
      const targets = supportsMethodologyOutputChoice(
        docCode,
        auditMethodology || methodologyGroup?.methodologyType,
      ) && scopeContext?.isMethodologyGroup
        ? resolveSplitStandardTargets(scopeContext)
        : [];

      if (targets.length === 0) {
        setOutputMode(OUTPUT_MODES.COMBINED);
        setSplitStandardForms({});
        setActiveSplitStandardKey('');
        splitBaseFormRef.current = seeded;
        setFormData(seeded);
        return;
      }

      const storedSplitForms = readSplitStandardForms(seeded);
      // The container is stripped from the edited fields and re-attached on save,
      // so no form body can ever bind or clear it by accident.
      const sharedSeed = stripSplitStandardForms(seeded);
      const restoredMode = resolveRestoredOutputMode(storedSplitForms);

      setOutputMode(restoredMode);
      splitBaseFormRef.current = sharedSeed;

      if (restoredMode === OUTPUT_MODES.SPLIT) {
        const seededForms = targets.reduce((result, target) => ({
          ...result,
          [target.standardKey]: storedSplitForms[target.standardKey]
            || seedSplitStandardForm(target, sharedSeed),
        }), {});
        const firstKey = targets[0].standardKey;
        setSplitStandardForms(seededForms);
        setActiveSplitStandardKey(firstKey);
        setFormData(seededForms[firstKey]);
      } else {
        // Kept, not dropped: a document saved as split before being switched back to
        // one merged document still carries its per-standard forms, so the choice
        // stays reversible instead of the next save silently discarding them.
        setSplitStandardForms(storedSplitForms);
        setActiveSplitStandardKey('');
        setFormData(sharedSeed);
      }
    }
  // eslint-disable-next-line
  }, [active, docCode, scopeKey]);

  // Switching between one merged document and one document per standard. Entering
  // split seeds every standard from what the merged form already held, so the
  // shared header data is not retyped; leaving it keeps the tabs so the choice can
  // be reversed without losing anything.
  const handleOutputModeChange = (nextMode) => {
    if (readOnly || nextMode === outputMode) return;
    setOutputMode(nextMode);
    if (nextMode !== OUTPUT_MODES.SPLIT || splitStandardTargets.length === 0) return;
    const base = stripSplitStandardForms(formData);
    splitBaseFormRef.current = base;
    const seededForms = splitStandardTargets.reduce((result, target) => ({
      ...result,
      [target.standardKey]: splitStandardForms[target.standardKey]
        || seedSplitStandardForm(target, base),
    }), {});
    const firstKey = splitStandardTargets[0].standardKey;
    // The tab is a new set of fields, so the form defaults get another pass at it —
    // they only ever fill blanks, and a tab opened before they ran would otherwise
    // keep those blanks forever.
    defaultsAppliedKeyRef.current = '';
    scopeDefaultsAppliedKeyRef.current = '';
    setSplitStandardForms(seededForms);
    setActiveSplitStandardKey(firstKey);
    setFormData(seededForms[firstKey]);
  };

  // Standard tabs swap the fields in place — the tab being left is stored in this
  // session's map (and written by the next save, which always carries every
  // standard), so switching costs no request and cannot lose keystrokes.
  const switchSplitStandard = (nextStandardKey) => {
    if (!nextStandardKey || nextStandardKey === effectiveSplitStandardKey) return;
    const target = splitStandardTargets.find((candidate) => candidate.standardKey === nextStandardKey);
    if (!target) return;
    const leavingKey = effectiveSplitStandardKey;
    const leavingForm = stripSplitStandardForms(formData);
    const nextForm = splitStandardForms[nextStandardKey]
      || seedSplitStandardForm(target, splitBaseFormRef.current);
    defaultsAppliedKeyRef.current = '';
    scopeDefaultsAppliedKeyRef.current = '';
    setSplitStandardForms((previous) => ({
      ...previous,
      ...(leavingKey ? { [leavingKey]: leavingForm } : {}),
      [nextStandardKey]: nextForm,
    }));
    setActiveSplitStandardKey(nextStandardKey);
    setFormData(nextForm);
  };

  // A grouped FTRD-02 edits one standard per tab inside the SAME form session. Remounting the
  // form for each standard blanked every field for a frame and restarted the auditor/scope/
  // certificate lookups, which reads as a flicker — so only the fields are swapped here.
  useEffect(() => {
    if (!active || !docCode) return;
    const instanceKeyForSeed = `${docCode}::${scopeKey || ''}::${formInstanceKey || ''}`;
    if (seededInstanceKeyRef.current === instanceKeyForSeed) return;
    const leavingInstance = seededInstanceKeyRef.current;
    seededInstanceKeyRef.current = instanceKeyForSeed;
    // persistFormDataRef still holds the previous commit's closure, i.e. the fields and the
    // target of the standard being left — so the outgoing tab is stored as it stands before its
    // fields are replaced. Without this, the last edits of a tab lived only in this component
    // and the pending auto-save was thrown away by the switch.
    if (leavingInstance && !readOnly && Object.keys(formData).length > 0) {
      Promise.resolve(persistFormDataRef.current?.())
        .then((result) => { if (result) onSaved?.(docCode, result, { silent: true }); })
        .catch((error) => console.warn('Failed to store the standard being left:', error?.message || error));
    }
    defaultsAppliedKeyRef.current = '';
    scopeDefaultsAppliedKeyRef.current = '';
    // These messages belong to the standard being left; the incoming tab's
    // fields would otherwise render red under another standard's errors.
    setFieldErrors({});
    setFormData(resolveSeededFormData());
  // eslint-disable-next-line
  }, [active, docCode, scopeKey, formInstanceKey]);
  // auditSchedule excluded: it's only used for initial data population.
  // Including it causes the form to reset on every parent re-render (e.g. from onDraftChange).
  // auditorOptions is also intentionally excluded: AuditProcess enriches/recreates this
  // array while FTRD-02 is being edited, and treating that refresh as a new session
  // cleared the operator's unsaved keystrokes.

  // A modal close starts a new editing session. Do not reset this key when
  // auditorOptions changes: that array is recreated by the parent while FTRD-02
  // is edited and used to restart the three scope requests before this guard.
  useEffect(() => {
    if (!active) {
      scopeRequestKeyRef.current = '';
      certificateNumberDefaultKeyRef.current = '';
      certificateDatesKeyRef.current = '';
    }
  }, [active]);

  // Active cycle metadata is already carried by the selected scheduling scope.
  // Keep the display value in sync independently of data fetching, so a parent
  // scope refresh cannot turn the banner into "Not defined" or trigger a refetch.
  useEffect(() => {
    if (!active || !docCode) return;
    const activeCycleAuditType = resolveActiveCycleAuditType({ scopeContext, scopeLabel });
    if (!activeCycleAuditType) return;
    setAutoAuditType((previous) => previous === activeCycleAuditType ? previous : activeCycleAuditType);
  }, [active, docCode, scopeContext, scopeLabel]);

  // Backfill scheduled auditors when options are enriched after initial form boot.
  useEffect(() => {
    if (!active || !docCode || existingData || docCode !== 'FAPP-01 Audit Plan') return;

    const scheduledAuditorIds = (availableAuditors || [])
      .filter((auditor) => auditor?.isScheduled)
      .map((auditor) => String(auditor?.userId || auditor?.id))
      .filter(Boolean);

    if (scheduledAuditorIds.length === 0) return;

    setFormData((prev) => {
      const rundowns = Array.isArray(prev?.rundowns) ? prev.rundowns : [];
      if (rundowns.length === 0) return prev;

      let changed = false;
      const nextRundowns = rundowns.map((row) => {
        const currentIds = Array.isArray(row?.auditorIds) ? row.auditorIds : [];
        if (currentIds.length > 0) return row;
        changed = true;
        return {
          ...row,
          auditorIds: scheduledAuditorIds,
        };
      });

      return changed ? { ...prev, rundowns: nextRundowns } : prev;
    });
  }, [active, docCode, existingData, availableAuditors, instanceKey]);

  useEffect(() => {
    if (!active || !methodologyContext) return;
    setFormData((prev) => {
      const previous = prev?.methodologyContext || {};
      if (JSON.stringify(previous) === JSON.stringify(methodologyContext)) return prev;
      return { ...prev, methodologyContext };
    });
  }, [active, methodologyContext]);

  useEffect(() => {
    if (!active || docCode !== 'FAPP-01 Audit Plan' || Object.keys(formData).length === 0) return;
    setFormData((prev) => normalizeAuditPlanFormData(prev, {
      projectMeta,
      stages,
      auditorOptions: availableAuditors,
      auditSchedule,
      scopeLabel,
      autoAuditType,
      standardSelectOptions,
      auditMethodology,
      methodologyGroup,
      scopeContext,
    }));
  // eslint-disable-next-line
  }, [active, docCode, projectMeta, stages, availableAuditors, auditSchedule, scopeLabel, autoAuditType, standardSelectOptions, auditMethodology, methodologyGroup, scopeContext]);
  // formData excluded: this effect updates formData via setFormData(prev => ...) — including it would create a loop.

  useEffect(() => {
    if (!active || docCode !== 'FTRD-02 Certificate Review and Production') return;
    setFormData((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const normalized = normalizeFtrd02FormData(prev, projectMeta, auditSchedule, scopeLabel,
        { seedDefaults: false });
      return JSON.stringify(normalized) === JSON.stringify(prev) ? prev : normalized;
    });
  }, [active, docCode, projectMeta, auditSchedule, scopeLabel]);

  useEffect(() => {
    if (!active || !docCode || existingData || auditData.loading || defaultsAppliedKeyRef.current === instanceKey) {
      return;
    }

    if (Object.keys(formData).length === 0) {
      return;
    }

    setFormData((prev) => mergeDefaultsIntoForm(prev, formDefaults));
    defaultsAppliedKeyRef.current = instanceKey;
  // eslint-disable-next-line
  }, [active, docCode, existingData, auditData.loading, formDefaults, instanceKey]);

  // Preview PDF is controlled by AuditProcess, outside this form. Keep its payload
  // current so FTRD-02 site data cannot be lost. Use a short debounce (not useLayoutEffect)
  // to avoid calling onDraftChange synchronously during React's commit phase, which would
  // trigger a setState in the parent on every keystroke and cause an infinite update loop.
  useEffect(() => {
    if (!active) return;
    const delay = docCode === 'FTRD-02 Certificate Review and Production' ? 100 : 500;
    const timer = setTimeout(() => { onDraftChangeRef.current?.(formData); }, delay);
    return () => clearTimeout(timer);
  }, [active, docCode, formData]);

  // Auto-fill lead auditor fields across ALL forms when auditorOptions loads
  useEffect(() => {
    if (!active || !docCode || existingData || Object.keys(formData).length === 0) return;
    // Priority 1: explicitly designated lead (role contains 'lead', or first scheduled auditor)
    // Priority 2: any scheduled auditor who has a role assignment (mapped to auditor group in personnel qual)
    // Priority 3: any scheduled auditor (fallback)
    const lead =
      availableAuditors?.find(a => a.isLead) ||
      availableAuditors?.find(a => a.isScheduled && a.role) ||
      availableAuditors?.find(a => a.isScheduled) ||
      null;
    if (!lead) return;
    const leadName = getAuditorDisplayName(lead);
    const leadId = String(lead.userId || lead.id || '');
    setFormData(prev => ({
      ...prev,
      ...(prev.preparedBy === '' && leadName && { preparedBy: leadName }),
      ...(prev.auditorName === '' && leadName && { auditorName: leadName }),
      ...(prev.leadAuditor === '' && leadId && { leadAuditor: leadId }),
      ...(prev.auditLeadId === '' && leadId && { auditLeadId: leadId }),
      ...(prev.leadAuditorName === '' && leadName && { leadAuditorName: leadName }),
      ...(prev.producedBy === '' && leadName && { producedBy: leadName }),
      ...(prev.reviewedBy === '' && leadName && { reviewedBy: leadName }),
      ...(prev.approvedBy === '' && leadName && { approvedBy: leadName }),
      ...(prev.reviewer === '' && leadName && { reviewer: leadName }),
      ...(prev.verifiedBy === '' && leadId && { verifiedBy: leadId }),
    }));
  // eslint-disable-next-line
  }, [availableAuditors, active, docCode, existingData, instanceKey]);

  // Older certificate drafts may contain an account username/email in the
  // issuance-role fields. Resolve those values back to the person's display
  // name when the auditor list is available.
  useEffect(() => {
    if (!active || docCode !== 'FTRD-02 Certificate Review and Production' || availableAuditors.length === 0) return;
    const roleFields = ['producedBy', 'reviewedBy', 'approvedBy'];
    const resolveDisplayName = (value) => {
      const current = String(value || '').trim();
      if (!current) return current;
      const match = availableAuditors.find((auditor) => [
        auditor?.userId,
        auditor?.id,
        auditor?.employeeId,
        auditor?.username,
        auditor?.email,
        auditor?.name,
        auditor?.fullName,
        auditor?.employeeName,
      ].some((candidate) => String(candidate || '').trim().toLowerCase() === current.toLowerCase()));
      return match ? getAuditorDisplayName(match) : (looksLikeAccountIdentifier(current) ? humanizeAccountIdentifier(current) : current);
    };

    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;
      roleFields.forEach((field) => {
        const resolved = resolveDisplayName(prev[field]);
        if (resolved && resolved !== prev[field]) {
          next[field] = resolved;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [availableAuditors, active, docCode, instanceKey]);

  // Load scope-aware data once per document/scope. Parent rerenders replace
  // auditor/standard arrays frequently; they must not retrigger these API calls
  // while the user is filling FTRD-02.
  useEffect(() => {
    if (!projectId || !docCode) {
      setLoadingScope(false);
      return undefined;
    }

    if (!selectedScope) {
      setAvailableStandards(stageDerivedStandardsRef.current);
      setAvailableAuditors(auditorOptionsRef.current);
      setAutoAuditType(resolveActiveCycleAuditType({
        scopeContext,
        scopeLabel,
        apiAuditType: (stages || []).find((stage) => stage?.auditType)?.auditType,
      }));
      setLoadingScope(false);
      return undefined;
    }

    const requestKey = buildScopeRequestKey({ projectId, scopeKey: selectedScope, documentCode: docCode });
    if (!shouldLoadScopeRequest(scopeRequestKeyRef.current, requestKey)) return undefined;
    scopeRequestKeyRef.current = requestKey;

    let cancelled = false;
    setLoadingScope(true);

    Promise.all([
      api.get(`/audit/projects/${projectId}/scope/${selectedScope}/standards`),
      api.get(`/audit/projects/${projectId}/scope/${selectedScope}/auditors`),
      api.get(`/audit/projects/${projectId}/scope/${selectedScope}/audit-type`)
    ]).then(([stdRes, audRes, typeRes]) => {
      if (cancelled) return;
      const standardsFromScope = stdRes?.data?.data;
      const auditorsFromScope = audRes?.data?.data;
      setAvailableStandards(Array.isArray(standardsFromScope) && standardsFromScope.length > 0 ? standardsFromScope : stageDerivedStandardsRef.current);
      setAvailableAuditors(Array.isArray(auditorsFromScope) && auditorsFromScope.length > 0 ? auditorsFromScope : auditorOptionsRef.current);
      setAutoAuditType(
        resolveActiveCycleAuditType({
          scopeContext,
          scopeLabel,
          apiAuditType: typeRes?.data?.data?.auditType || typeRes?.data?.auditType,
        }),
      );
      setLoadingScope(false);
    }).catch((err) => {
      if (cancelled) return;
      console.warn('Failed to load scope data:', err);
      setLoadingScope(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedScope, projectId, docCode, stages]);

  const [requirementOptions, setRequirementOptions] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);

  const requirementDocs = useMemo(() => new Set([
    'FAPP-01 Audit Plan',
    'FAPP-04 Audit Notes',
    'FAPP-07 Finding Form',
    'FAPP-07 Finding Form (accepted)',
  ]), []);

  const standardIdsForRequirements = useMemo(() => {
    const ids = new Set();

    const scopeHints = [
      scopeContext?.standardId,
      scopeContext?.standard_id,
      scopeContext?.standardName,
      scopeContext?.standard_name,
      extractStandardFromScopeLabel(scopeLabel),
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    scopeHints.forEach((value) => ids.add(value));

    const fromScopeStandards = Array.isArray(availableStandards) ? availableStandards : [];

    if (fromScopeStandards.length > 0) {
      // Scope standards are explicitly loaded from the API — use ONLY those.
      // Excluding stageDerivedStandards avoids polluting the candidate set with
      // stage names / service-item labels that never match catalog standard IDs,
      // which previously caused the fallback to load ALL catalog clauses.
      fromScopeStandards.forEach((std) => {
        if (typeof std === 'string') {
          const v = std.trim();
          if (v) ids.add(v);
          return;
        }
        const candidates = [
          std?.id,
          std?.standardId,
          std?.standard_id,
          std?.code,
          std?.standardCode,
          std?.standard_code,
          std?.value,
          std?.label,
          std?.name,
          std?.standardName,
          std?.standard_name,
        ];
        candidates.forEach((candidate) => {
          const v = String(candidate || '').trim();
          if (v) ids.add(v);
        });
      });
    } else {
      if (scopeHints.length > 0) {
        scopeHints.forEach((value) => ids.add(value));
      } else {
        // No explicit scope metadata — fall back to form-level and stage-derived hints.
        String(formData?.applicableStandards || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((s) => ids.add(s));

        stageDerivedStandards.forEach((standard) => {
          const value = getStandardValue(standard);
          if (value) ids.add(value);
        });

        String(auditData?.standards || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((s) => ids.add(s));
      }
    }

    return Array.from(ids);
  }, [availableStandards, formData?.applicableStandards, stageDerivedStandards, auditData?.standards, scopeContext?.standardId, scopeContext?.standardName, scopeLabel]);

  useEffect(() => {
    if (!active || !docCode || !requirementDocs.has(docCode)) {
      setRequirementOptions([]);
      return;
    }

    let cancelled = false;
    setRequirementsLoading(true);

    const standardsCatalogPromise = standardsCatalogRef.current
      ? Promise.resolve(standardsCatalogRef.current)
      : (standardsCatalogPromiseRef.current || getStandards(true)
          .then((catalog) => {
            standardsCatalogRef.current = Array.isArray(catalog) ? catalog : [];
            return standardsCatalogRef.current;
          })
          .finally(() => {
            standardsCatalogPromiseRef.current = null;
          }));

    if (!standardsCatalogRef.current && !standardsCatalogPromiseRef.current) {
      standardsCatalogPromiseRef.current = standardsCatalogPromise;
    }

    standardsCatalogPromise
      .then((catalog) => {
        const catalogRows = (Array.isArray(catalog) ? catalog : [])
          .map((item) => {
            if (typeof item === 'string') {
              const id = item.trim();
              return { id, aliases: [id] };
            }

            const id = String(item?.id || item?.standardId || item?.value || item?.code || '').trim();
            const aliases = [
              id,
              item?.version,
              item?.standardVersion,
              item?.name,
              item?.standardName,
              item?.label,
              item?.code,
            ]
              .map((value) => String(value || '').trim())
              .filter(Boolean);

            return { id, aliases };
          })
          .filter((row) => row.id);

        const catalogIds = catalogRows.map((row) => row.id).filter(Boolean);

        if (catalogIds.length === 0) {
          const directFallbackIds = Array.from(
            new Set(
              standardIdsForRequirements
                .map((value) => String(value || '').trim())
                .filter(Boolean)
            )
          );

          if (directFallbackIds.length === 0) {
            return { results: [], resolvedStandardIds: [] };
          }

          return Promise.allSettled(
            directFallbackIds.map((standardId) => getStandardRequirements(standardId))
          ).then((results) => ({ results, resolvedStandardIds: directFallbackIds }));
        }

        // Build normalized lookup map for fuzzy matching.
        const catalogMap = new Map();
        catalogRows.forEach((row) => {
          row.aliases.forEach((alias) => {
            const normalized = normalizeStandardToken(alias);
            if (normalized) catalogMap.set(normalized, row.id);

            const stripped = stripStandardVersionSuffix(alias);
            const normalizedStripped = normalizeStandardToken(stripped);
            if (normalizedStripped) catalogMap.set(normalizedStripped, row.id);
          });
        });

        // Longest catalog key first so most-specific standard wins (ISO 9001:2015 > ISO 9001)
        const catalogEntries = [...catalogMap.entries()].sort((a, b) => b[0].length - a[0].length);

        // Try to resolve stage-derived candidates against the catalog
        const matched = new Set();
        standardIdsForRequirements.forEach((candidate) => {
          const raw = String(candidate || '').trim();
          if (!raw) return;
          if (catalogIds.includes(raw)) { matched.add(raw); return; }
          const normalized = normalizeStandardToken(raw);
          if (catalogMap.has(normalized)) { matched.add(catalogMap.get(normalized)); return; }
          const stripped = normalizeStandardToken(stripStandardVersionSuffix(raw));
          if (catalogMap.has(stripped)) { matched.add(catalogMap.get(stripped)); return; }
          // Reverse substring: candidate text contains a catalog key (e.g. "ISO 9001 - Initial Cert" contains "iso 9001")
          for (const [catalogNormKey, catalogId] of catalogEntries) {
            if (normalized.includes(catalogNormKey)) { matched.add(catalogId); return; }
          }
        });

        // ── KEY FALLBACK: if nothing matched project stages, show ALL catalog standards ──
        // This ensures clauses are always visible even if stage label doesn't match catalog
        const resolvedStandardIds = matched.size > 0 ? Array.from(matched) : catalogIds;

        const cacheKey = resolvedStandardIds.slice().sort().join('|');
        if (requirementOptionsCacheRef.current.has(cacheKey)) {
          return { results: null, resolvedStandardIds, cached: requirementOptionsCacheRef.current.get(cacheKey) };
        }

        return Promise.allSettled(
          resolvedStandardIds.map((standardId) => getStandardRequirements(standardId))
        ).then((results) => ({ results, resolvedStandardIds }));
      })
      .then(({ results, resolvedStandardIds, cached }) => {
        if (cancelled) return;

        if (cached) {
          setRequirementOptions(cached);
          setRequirementsLoading(false);
          return;
        }

        const nextOptions = [];
        const seen = new Set();

        (results || []).forEach((result, idx) => {
          if (result.status !== 'fulfilled') return;
          const data = result.value || {};
          const standardId = String(data?.standardId || resolvedStandardIds[idx] || '').trim();

          const append = (req, type) => {
            const code = String(req?.code || '').trim();
            const name = String(req?.name || req?.title || '').trim();
            if (!code) return;
            const key = `${standardId}::${type}::${code}`;
            if (seen.has(key)) return;
            seen.add(key);
            nextOptions.push({
              value: key,
              code,
              label: `${code}${name ? ` — ${name}` : ''}${resolvedStandardIds.length > 1 && standardId ? ` (${standardId})` : ''}`,
              description: String(req?.description || '').trim(),
              type,
              standardId,
            });
          };

          (data?.clauses || []).forEach((req) => append(req, 'clause'));
          (data?.annexes || []).forEach((req) => append(req, 'annex'));
        });

        const cacheKey = resolvedStandardIds.slice().sort().join('|');
        requirementOptionsCacheRef.current.set(cacheKey, nextOptions);
        setRequirementOptions(nextOptions);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Failed to resolve standard requirements:', err);
          setRequirementOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setRequirementsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active, docCode, requirementDocs, standardIdsForRequirements]);

  const requirementLookup = useMemo(() => {
    const map = {};
    requirementOptions.forEach((opt) => {
      map[opt.value] = opt;
    });
    return map;
  }, [requirementOptions]);

  useEffect(() => {
    if (!active || !docCode) return;

    const scopeStandardLabels = standardSelectOptions.map((option) => option.label).filter(Boolean);
    const proposalScope = String(projectMeta?.proposalScope || projectMeta?.scope || '').trim();
    // Scope resolution is asynchronous.  It may finish after a user begins
    // typing, so defaults are applied once for a brand-new form only; existing
    // saved data and user-entered certificate fields are never overwritten.
    const shouldSyncScopeDefaults = !existingData && scopeDefaultsAppliedKeyRef.current !== instanceKey;
    if (scopeStandardLabels.length === 0 && !autoAuditType && !proposalScope) return;

    setFormData((prev) => {
      let changed = false;
      const next = { ...prev };

      if (scopeStandardLabels.length > 0 && ['FAPP-01 Audit Plan', 'FAPP-05 Audit Report', 'FAPP-06 Certificate Information Form', 'FAPP-06 CIF (signed by client)', 'FTRD-01 Technical Review and Certification Decision', 'FTRD-02 Certificate Review and Production'].includes(docCode)) {
        const currentStandards = splitStoredMultiValue(prev.applicableStandards);
        if (shouldSyncScopeDefaults && currentStandards.length === 0) {
          next.applicableStandards = scopeStandardLabels;
          changed = true;
        }
      }

      if (autoAuditType && shouldSyncScopeDefaults && !String(prev.auditType || '').trim() && ['FAPP-01 Audit Plan', 'FAPP-03 Attendance List', 'FAPP-05 Audit Report'].includes(docCode)) {
        next.auditType = autoAuditType;
        changed = true;
      }

      if (proposalScope && shouldSyncScopeDefaults && !String(prev.scopeOfCertification || '').trim() && ['FAPP-01 Audit Plan', 'FAPP-05 Audit Report', 'FTRD-01 Technical Review and Certification Decision'].includes(docCode)) {
        next.scopeOfCertification = proposalScope;
        changed = true;
      }

      if (proposalScope && docCode === 'FTRD-02 Certificate Review and Production' && shouldSyncScopeDefaults && !String(prev.certificateScope || '').trim()) {
        next.certificateScope = proposalScope;
        changed = true;
      }

      if (proposalScope && docCode === 'FAPP-02 Three Years Audit Program') {
        ['year1', 'year2', 'year3'].forEach((yearKey) => {
          const currentScope = String(prev?.[yearKey]?.scope || '').trim();
          if (shouldSyncScopeDefaults && !currentScope) {
            next[yearKey] = {
              ...prev?.[yearKey],
              scope: proposalScope,
            };
            changed = true;
          }
        });
      }

      return changed ? next : prev;
    });
    if (shouldSyncScopeDefaults) scopeDefaultsAppliedKeyRef.current = instanceKey;
  }, [active, docCode, autoAuditType, standardSelectOptions, projectMeta?.proposalScope, projectMeta?.scope, existingData]);

  // Existing document data may arrive after the initial form boot. Enforce the
  // selected audit scope again so a multi-standard project's other standards
  // cannot remain in a single-standard document.
  useEffect(() => {
    if (!active || !docCode || !String(formData?.applicableStandards || '').trim()) return;
    const scoped = normalizeFormStandardsToScope(formData, scopeLabel, scopeContext);
    if (scoped !== formData) setFormData(scoped);
  }, [active, docCode, formData?.applicableStandards, scopeLabel, scopeContext]);

  const set = (field, value) => {
    if (readOnly) return;
    // Clear this field's save-time error the moment it is edited, so the
    // message never outlives the problem it describes.
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  const setNested = (parent, indexOrField, fieldOrValue, arrayValue) =>
    readOnly ? undefined :
    setFormData((prev) => {
      // FAPP-02 uses an array row/index/field/value shape. Preserve object
      // nesting support for any future forms that use field/value instead.
      if (arrayValue !== undefined) {
        const rows = Array.isArray(prev[parent]) ? [...prev[parent]] : [];
        rows[indexOrField] = { ...(rows[indexOrField] || {}), [fieldOrValue]: arrayValue };
        return { ...prev, [parent]: rows };
      }
      return { ...prev, [parent]: { ...(prev[parent] || {}), [indexOrField]: fieldOrValue } };
    });

  useEffect(() => {
    if (!active || docCode !== 'FTRD-02 Certificate Review and Production' || !projectId) return;
    const projectServiceStandardId = certificateStandardId;
    const requestKey = `${projectId}:${projectServiceStandardId || ''}`;
    if (certificateNumberDefaultKeyRef.current === requestKey) return;

    let cancelled = false;
    certificateNumberDefaultKeyRef.current = requestKey;
    api.get(`/audit/projects/${projectId}/certificate-number-default`, {
      params: projectServiceStandardId ? { projectServiceStandardId } : {},
    }).then((response) => {
      const certificateNumber = extractCertificateNumberDefault(response);
      if (cancelled || !certificateNumber) return;
      setFormData((prev) => (
        String(prev?.certificateNumber || '').trim()
          ? prev
          : { ...prev, certificateNumber }
      ));
    }).catch((err) => {
      // The certificate number remains manually editable when a suggestion is unavailable.
      console.warn('Certificate number default unavailable:', err?.message || err);
    });
    return () => { cancelled = true; };
  }, [active, docCode, projectId, effectiveScopeKey, certificateStandardId]);

  // The PDF renderer applies project/certificate defaults at render time. Keep the
  // editable and read-only FTRD-02 views aligned with those same dates instead of
  // leaving the saved form's blank date fields visible.
  useEffect(() => {
    if (!active || docCode !== 'FTRD-02 Certificate Review and Production' || !projectId) return;
    const projectServiceStandardId = certificateStandardId;
    const requestKey = `${projectId}:${projectServiceStandardId || ''}`;
    if (certificateDatesKeyRef.current === requestKey) return;

    let cancelled = false;
    certificateDatesKeyRef.current = requestKey;
    const today = todayDateInputValue();
    const defaultExpiryDate = addYearsToDateInputValue(today, 3);
    // Seed immediately from the loaded project metadata. This also keeps the form
    // responsive while the certificate endpoint is being fetched.
    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;
      const fill = (field, value) => {
        if (value && !String(next[field] || '').trim()) {
          next[field] = value;
          changed = true;
        }
      };
      fill('initialDate', today);
      fill('issueDate', today);
      fill('latestCertificationDate', today);
      fill('expiredDate', defaultExpiryDate);
      fill('validFrom', today);
      fill('validTo', defaultExpiryDate);
      return changed ? next : prev;
    });
    api.get(`/audit/projects/${projectId}/certificate`)
      .then((response) => {
        if (cancelled) return;
        const certificates = Array.isArray(response?.data?.certificates) ? response.data.certificates : [];
        const certificate = certificates.find((item) => (
          projectServiceStandardId
          && String(item?.projectServiceStandardId || '') === String(projectServiceStandardId)
        )) || certificates[0] || {};
        // The endpoint's top-level object is project-defaulted; individual rows
        // may still be blank when a certificate has not been issued yet.
        const endpointDefaults = response?.data || {};
        const issueDate = toDateInputValue(
          certificate.issueDate || certificate.issuedDate
            || endpointDefaults.issueDate || endpointDefaults.issuedDate
          || projectMeta?.projectDate
        );
        const expiryDate = toDateInputValue(
          certificate.expiryDate || certificate.expiredDate
            || endpointDefaults.expiryDate || endpointDefaults.expiredDate
            || projectMeta?.validUntil
        );
        const fallbackToday = todayDateInputValue();
        const fallbackExpiryDate = addYearsToDateInputValue(fallbackToday, 3);
        const initialDate = toDateInputValue(certificate.initialCertificationDate)
          || toDateInputValue(endpointDefaults.initialCertificationDate)
          || issueDate
          || fallbackToday;
        const latestDate = toDateInputValue(certificate.latestCertificationDate)
          || toDateInputValue(endpointDefaults.latestCertificationDate)
          || fallbackToday;

        setFormData((prev) => {
          const next = { ...prev };
          let changed = false;
          const fill = (field, value) => {
            if (value && !String(next[field] || '').trim()) {
              next[field] = value;
              changed = true;
            }
          };

          fill('initialDate', initialDate);
          fill('latestCertificationDate', latestDate);
          fill('issueDate', issueDate || fallbackToday);
          fill('expiredDate', expiryDate || fallbackExpiryDate);
          fill('validFrom', issueDate || fallbackToday);
          fill('validTo', expiryDate || fallbackExpiryDate);
          return changed ? next : prev;
        });
      })
      .catch((error) => {
        // Certificate dates are supplemental; keep the form usable if the endpoint
        // is unavailable and let the PDF endpoint continue applying its defaults.
        console.warn('Certificate dates unavailable for FTRD-02:', error?.message || error);
        const issueDate = todayDateInputValue();
        const expiryDate = addYearsToDateInputValue(issueDate, 3);
        setFormData((prev) => ({
          ...prev,
          initialDate: prev.initialDate || issueDate,
          latestCertificationDate: prev.latestCertificationDate || issueDate,
          issueDate: prev.issueDate || issueDate,
          expiredDate: prev.expiredDate || expiryDate,
          validFrom: prev.validFrom || issueDate,
          validTo: prev.validTo || expiryDate,
        }));
      });

    return () => { cancelled = true; };
  }, [active, docCode, projectId, effectiveScopeKey, certificateStandardId, projectMeta?.projectDate, projectMeta?.validUntil]);

  const refreshEvidence = useCallback(async () => {
    if (!active || !EVIDENCE_ENABLED_DOC_CODES.has(docCode) || !projectId || !effectiveScopeKey) {
      setEvidenceItems([]);
      return;
    }
    setEvidenceLoading(true);
    const result = await getEvidenceByProjectAndScope(projectId, effectiveScopeKey, docCode);
    setEvidenceItems(Array.isArray(result) ? result : []);
    setEvidenceLoading(false);
  }, [active, docCode, projectId, effectiveScopeKey]);

  useEffect(() => {
    refreshEvidence();
  }, [refreshEvidence]);

  // Persists the current form data as-is. Extracted so both the normal Save flow and the
  // FTRD-01 Stage 1 "Need Revision" submit flow (below) share the exact same save behavior
  // (including the split-per-standard output mode), instead of duplicating it.
  const persistFormData = async () => {
    const documentMetadata = buildDocumentMetadata({ docCode, projectId, projectMeta, scopeLabel, scopeContext, stages });
    const persistedBase = buildPersistedFormData ? buildPersistedFormData(formData) : formData;
    const normalizedFormData = docCode === 'FAPP-04 Audit Notes'
      ? { ...persistedBase, notesText: stripRichText(formData.notesHtml || formData.notesText) }
      : persistedBase;
    const persistedFormData = {
      // The grouped document keeps every standard's form side by side, so the tabs
      // survive a reload and a save from one tab cannot drop the others.
      ...(persistedSplitStandardForms
        ? buildSplitPersistedForm(normalizedFormData, persistedSplitStandardForms)
        : normalizedFormData),
      documentMetadata,
      ...(methodologyContext ? { methodologyContext } : {}),
    };
    const result = await saveDocumentFormData(projectId, {
      documentCode: docCode,
      category,
      formDataJson: JSON.stringify(persistedFormData),
      scopeKey: effectiveScopeKey,
    });
    if (methodologyContext?.outputMode === OUTPUT_MODES.SPLIT) {
      const members = Array.isArray(scopeContext?.memberScopes) ? scopeContext.memberScopes : [];
      await Promise.all(members.map((member) => {
        // Each member scope gets ITS OWN standard's answers. Before the per-standard
        // tabs existed this wrote the same form to every member, so a split audit
        // ended up with N identical documents under N different standards.
        const memberForm = splitStandardFormMap
          ? resolveSplitFormForMember(member, splitStandardTargets, splitStandardFormMap, persistedFormData)
          : persistedFormData;
        const memberFormData = buildSplitMemberFormData(
          { ...memberForm, documentMetadata },
          methodologyContext,
          member,
        );
        return saveDocumentFormData(projectId, {
          documentCode: docCode,
          category,
          formDataJson: JSON.stringify(memberFormData),
          scopeKey: member.key,
        });
      }));
    }
    return result;
  };

  useEffect(() => {
    persistFormDataRef.current = persistFormData;
  });

  const handleSave = async () => {
    const nextFieldErrors = getDocumentFormFieldErrors(docCode, formData);
    const invalidFields = Object.keys(nextFieldErrors).filter((field) => nextFieldErrors[field]);
    setFieldErrors(nextFieldErrors);
    if (invalidFields.length > 0) {
      setError(getDocumentFormValidationError(docCode, formData));
      // Bring the first offending field into view — on the inline (non-modal)
      // render this form is long enough that an error alert pinned to the top
      // is easy to miss entirely.
      if (typeof document !== 'undefined') {
        const firstInvalid = document.getElementById(`field-${getFirstInvalidDocumentFormField(docCode, formData)}`);
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalid.querySelector('input, textarea, select')?.focus({ preventScroll: true });
        }
      }
      return;
    }
    // Split output saves every standard at once, so a tab the operator has not
    // opened must not slip through unvalidated — the failing standard is named and
    // opened instead of the save silently writing an incomplete document.
    if (splitStandardFormMap) {
      const invalidTarget = splitStandardTargets
        .map((target) => ({
          target,
          message: getDocumentFormValidationError(docCode, splitStandardFormMap[target.standardKey]),
        }))
        .find((entry) => entry.message);
      if (invalidTarget) {
        setError(`${invalidTarget.target.standardName || 'Standard'}: ${invalidTarget.message}`);
        switchSplitStandard(invalidTarget.target.standardKey);
        return;
      }
    }

    // FTRD-01 Stage 1's own "Decision" dropdown now drives what Save does (formerly a
    // separate, always-visible TR decision panel outside this form). Need Revision opens a
    // confirmation modal for notes instead of saving immediately — nothing is persisted
    // until that modal is submitted, matching the reject/revision pattern used elsewhere in
    // Audit Process. Any other decision (including not yet chosen) falls through to a normal
    // save below.
    if (isFtrd01Stage1Scope && formData.decision === 'Need Revision') {
      setTrRevisionError(null);
      setShowTrRevisionModal(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await persistFormData();
      // saveDocumentFormData normalizes request failures into { success: false }
      // for legacy callers. Do not continue into the TR decision flow or show a
      // success state when the document itself was rejected by the API.
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to save document form.');
      }
      if (isFtrd01Stage1Scope && formData.decision === 'Continue to Stage 2') {
        // Mirrors the previous standalone panel's "OK — Approve" submission: marks the
        // Technical Review task done and logs the decision for the stage stepper. The form
        // itself already saved successfully above, so a logging failure here is surfaced as
        // a warning, not a save failure.
        try {
          await api.post(`/audit/projects/${projectId}/tr-decision`, {
            decision: 'OK',
            notes: '',
            scopeKey: effectiveScopeKey,
          });
        } catch (trErr) {
          console.warn('Failed to log TR OK decision:', trErr?.message || trErr);
        }
      } else if (String(docCode || '').startsWith('FTRD-01') && !isFtrd01Stage1Scope
        && ['RECOMMEND_GRANT', 'RECOMMEND_CONTINUE_WITHOUT_CERTIFICATE'].includes(formData.certificationDecision)) {
        // The final Technical Review decision is the sole authority for the next
        // workflow step.  Grant opens Certificate Issuance; continue-without-
        // certificate closes this cycle and activates the next configured one.
        try {
          await api.post(`/audit/projects/${projectId}/tr-decision`, {
            decision: formData.certificationDecision === 'RECOMMEND_GRANT'
              ? 'GRANT_CERTIFICATE'
              : 'CONTINUE_WITHOUT_CERTIFICATE',
            notes: '',
            scopeKey: effectiveScopeKey,
          });
        } catch (trErr) {
          console.warn('Failed to log Stage 2 TR OK decision:', trErr?.message || trErr);
        }
      }
      setSuccess(true);
      if (onSaved) onSaved(docCode, result);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const submitTrNeedRevision = async () => {
    if (!trRevisionNotes.trim()) return;
    setSubmittingTrRevision(true);
    setTrRevisionError(null);
    try {
      const result = await persistFormData();
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to save document form.');
      }
      await api.post(`/audit/projects/${projectId}/tr-decision`, {
        decision: 'NEED_REVISION',
        notes: trRevisionNotes.trim(),
        scopeKey: effectiveScopeKey,
      });
      setShowTrRevisionModal(false);
      setTrRevisionNotes('');
      setSuccess(true);
      if (onSaved) onSaved(docCode, result);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setTrRevisionError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to submit Need Revision.');
    } finally {
      setSubmittingTrRevision(false);
    }
  };

  const modalTitle = scopeLabel ? `${docCode || 'Document Form'} / ${scopeLabel}` : (docCode || 'Document Form');

  // Autosave used to be visible only while it was in flight or after it failed,
  // so the resting state was silence — indistinguishable from "autosave is not
  // running at all" on a form that swaps its data when the standard tab changes.
  // Remember the last successful pass and keep showing it.
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const wasAutoSavingRef = useRef(false);
  useEffect(() => {
    if (wasAutoSavingRef.current && !autoSaving && !autoSaveError) {
      setLastAutoSavedAt(new Date());
    }
    wasAutoSavingRef.current = autoSaving;
  }, [autoSaving, autoSaveError]);

  const autoSaveBadge = autoSaving
    ? <Badge bg="info" className="d-flex align-items-center gap-1"><Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />Saving...</Badge>
    : autoSaveError
      ? <Badge bg="warning" className="d-flex align-items-center gap-1"><AlertCircle size={12} />Autosave failed</Badge>
      : lastAutoSavedAt
        ? (
          <Badge bg="light" text="dark" className="d-flex align-items-center gap-1 fw-normal border">
            <Check size={12} />
            {`Saved ${lastAutoSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
          </Badge>
        )
        : null;

  const handleExportPDF = useCallback(async () => {
    if (!formData || Object.keys(formData).length === 0 || !docCode || !projectMeta) return;
    const baseProjectData = {
      ...projectMeta,
      projectId,
      clientName: projectMeta.clientName || projectMeta.companyName || projectMeta.locationName || '',
      companyName: projectMeta.companyName || projectMeta.clientName || projectMeta.locationName || '',
      clientId: projectMeta.clientId || projectMeta.companyId || '',
      auditDates: projectMeta.auditDates || projectMeta.estimatedAuditDate || '',
      auditCriteria: projectMeta.auditCriteria || '',
      scope: projectMeta.scope || '',
      scopeLabel,
      scopeContext,
      auditors: availableAuditors,
      auditSchedule,
      stages,
    };
    if (methodologyContext?.outputMode === OUTPUT_MODES.SPLIT) {
      const members = Array.isArray(scopeContext?.memberScopes) ? scopeContext.memberScopes : [];
      for (const member of members) {
        const suffix = String(member?.standardName || member?.standardId || 'standard').replace(/[^A-Za-z0-9_-]/g, '_');
        const memberForm = splitStandardFormMap
          ? resolveSplitFormForMember(member, splitStandardTargets, splitStandardFormMap, formData)
          : formData;
        const memberFormData = buildSplitMemberFormData(memberForm, methodologyContext, member);
        await exportFormToPDF(memberFormData, { code: docCode }, {
          ...baseProjectData,
          scopeLabel: member?.standardName || scopeLabel,
          exportSuffix: suffix,
        });
      }
      return;
    }
    await exportFormToPDF(formData, { code: docCode }, baseProjectData);
  }, [formData, docCode, projectMeta, projectId, scopeLabel, scopeContext, availableAuditors, auditSchedule, stages, methodologyContext, splitStandardFormMap, splitStandardTargets]);

  const hasFormData = Object.keys(formData).length > 0;
  const guardedSetFormData = (...args) => {
    if (!readOnly) setFormData(...args);
  };
  const guardedSetSelectedScope = (...args) => {
    if (!readOnly) setSelectedScope(...args);
  };

  const formBodyContent = (
    <>
      {error && (
        <Alert variant="danger" className="mb-3 d-flex align-items-center gap-2">
          <span className="fw-medium">Error:</span> {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-3">
          Form data saved successfully.
        </Alert>
      )}
      {supportsOutputMode && (
        <fieldset className="audit-output-mode mb-4" disabled={readOnly}>
          <legend>Document output</legend>
          {canChooseOutputMode ? (
            <>
              <p>Choose how this FAPP document is filled, saved and exported for the selected methodology group.</p>
              <div className="audit-output-mode__options">
                <label className={`audit-output-mode__option ${outputMode === OUTPUT_MODES.COMBINED ? 'is-selected' : ''}`}>
                  <input type="radio" name={`output-mode-${docCode}`} value={OUTPUT_MODES.COMBINED} checked={outputMode === OUTPUT_MODES.COMBINED} onChange={() => handleOutputModeChange(OUTPUT_MODES.COMBINED)} />
                  <span className="audit-output-mode__icon"><Layers3 size={20} /></span>
                  <span><strong>Combined output</strong><small>One form, one document, covering every standard in this audit group.</small></span>
                </label>
                <label className={`audit-output-mode__option ${outputMode === OUTPUT_MODES.SPLIT ? 'is-selected' : ''}`}>
                  <input type="radio" name={`output-mode-${docCode}`} value={OUTPUT_MODES.SPLIT} checked={outputMode === OUTPUT_MODES.SPLIT} onChange={() => handleOutputModeChange(OUTPUT_MODES.SPLIT)} />
                  <span className="audit-output-mode__icon"><Columns2 size={20} /></span>
                  <span><strong>Separate per standard</strong><small>The same form filled once per standard, in tabs — one document each.</small></span>
                </label>
              </div>
              <div className="audit-output-mode__summary" role="status">
                {outputMode === OUTPUT_MODES.COMBINED
                  ? `Combined document · ${methodologyContext?.memberStandards?.length || 0} standards`
                  : `${splitStandardTargets.length} separate standard documents · one form per standard`}
              </div>
            </>
          ) : (
            <>
              <p>
                An audit of integrated systems produces one integrated document for every
                standard in the group, so this form is not filled per standard.
              </p>
              <div className="audit-output-mode__summary" role="status">
                {`Combined document · ${methodologyContext?.memberStandards?.length || 0} standards`}
              </div>
            </>
          )}
        </fieldset>
      )}
      {isSplitOutput && (
        <nav className="audit-split-standards mb-4" aria-label="Standard forms">
          <div className="nav nav-tabs audit-split-standards__tabs" role="tablist">
            {splitStandardTargets.map((target) => {
              const isActive = target.standardKey === effectiveSplitStandardKey;
              return (
                <button
                  key={target.standardKey}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`nav-link${isActive ? ' active' : ''}`}
                  onClick={() => switchSplitStandard(target.standardKey)}
                >
                  {target.standardName || `Standard ${target.standardKey}`}
                </button>
              );
            })}
          </div>
          <p className="audit-split-standards__hint">
            Filling <strong>{activeSplitTarget?.standardName || 'this standard'}</strong>. Every
            standard has its own copy of this form — switching tabs keeps what you filled in, and
            saving writes one document per standard.
          </p>
        </nav>
      )}
      <fieldset
        disabled={readOnly}
        aria-disabled={readOnly}
        className={`border-0 p-0 m-0 ${readOnly ? 'audit-form-read-only' : ''}`}
      >
        <ScopeContextBanner
          scopeLabel={scopeLabel}
          standards={availableStandards.length > 0 ? availableStandards : standardSelectOptions.map((option) => option.label)}
          autoAuditType={autoAuditType}
          auditors={availableAuditors}
        />
        <FormBody
          docCode={docCode}
          formData={formData}
          set={set}
          setNested={setNested}
          setFormData={guardedSetFormData}
          auditorOptions={availableAuditors}
          stages={stages || []}
          selectedScope={selectedScope}
          setSelectedScope={guardedSetSelectedScope}
          availableStandards={availableStandards}
          autoAuditType={autoAuditType}
          loadingScope={loadingScope}
          projectId={projectId}
          auditSchedule={auditSchedule}
          requirementOptions={requirementOptions}
          requirementLookup={requirementLookup}
          requirementsLoading={requirementsLoading}
          standardSelectOptions={standardSelectOptions}
          projectMeta={projectMeta}
          scopeLabel={scopeLabel}
          scopeKey={effectiveScopeKey}
          getCycleMeta={getCycleMeta}
          setCycleMeta={setCycleMeta}
          getCoverage={getCoverage}
          toggleCoverage={toggleCoverage}
          getRowNotes={getRowNotes}
          setRowNotes={setRowNotes}
          fapp02Template={fapp02Template}
        />
        {EVIDENCE_ENABLED_DOC_CODES.has(docCode) && (
          <section className="audit-evidence-panel mt-4" aria-labelledby="fapp04-evidence-heading">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
              <div>
                <h6 id="fapp04-evidence-heading" className="mb-1 d-flex align-items-center gap-2"><Paperclip size={17} />
                  {docCode.startsWith('FAPP-06') ? 'Signed CIF Document' : 'Audit Evidence'}
                </h6>
                <p className="small text-muted mb-0">
                  {docCode.startsWith('FAPP-06')
                    ? 'Attach the signed Certificate Information Form (scan/photo/PDF) for this scope.'
                    : 'Attach photos, PDFs, or videos to the selected audit scope.'}
                </p>
              </div>
              {!readOnly && (
                <Button variant="outline-primary" size="sm" onClick={() => setEvidenceModalOpen(true)} disabled={!effectiveScopeKey}>
                  <Plus size={14} className="me-1" />Attach Evidence
                </Button>
              )}
            </div>
            {evidenceLoading ? (
              <div className="small text-muted"><Spinner animation="border" size="sm" className="me-2" />Loading evidence…</div>
            ) : evidenceItems.length === 0 ? (
              <div className="audit-evidence-empty"><FileText size={20} /><span>No evidence attached to this form yet.</span></div>
            ) : (
              <div className="audit-evidence-list">
                {evidenceItems.map((item) => (
                  <div key={item.id} className="audit-evidence-item">
                    <div className="min-w-0">
                      <strong className="d-block text-truncate">{item.fileName}</strong>
                      <span>{item.evidenceType || 'Document'} · {item.uploadedBy || 'Audit user'}</span>
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="outline-secondary" size="sm" onClick={() => downloadEvidence(item.id, item.fileName)}>Download</Button>
                      {!readOnly && <Button variant="outline-danger" size="sm" onClick={async () => { await deleteEvidence(item.id); refreshEvidence(); }}>Remove</Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        <AuditEvidenceModal
          isOpen={evidenceModalOpen}
          onClose={() => setEvidenceModalOpen(false)}
          projectId={projectId}
          processId={projectMeta?.processId || projectMeta?.auditProcessId || null}
          scopeKey={effectiveScopeKey}
          documentCode={docCode}
          onUploadSuccess={refreshEvidence}
        />
      </fieldset>
    </>
  );

  const formFooterContent = (
    <>
      <div>
        <Button
          variant="outline-success"
          onClick={handleExportPDF}
          disabled={!hasFormData || saving}
          className="me-2"
        >
          <Download size={16} className="me-1" />
          Export PDF
        </Button>
      </div>
      <div className="d-flex gap-2 align-items-center">
        {readOnly && (
          <span className="badge bg-warning text-dark me-2">
            Read Only — Stage completed
          </span>
        )}
        <Button variant="outline-secondary" onClick={inline ? (onCancel || onHide) : onHide} disabled={saving}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button variant="primary" onClick={handleSave} disabled={saving} style={{ minWidth: '110px', backgroundColor: '#1a767e', borderColor: '#1a767e' }}>
            {saving ? (
              <><Spinner animation="border" size="sm" className="me-1" />Saving…</>
            ) : (
              'Save'
            )}
          </Button>
        )}
      </div>
    </>
  );

  // Need Revision confirmation — opens when FTRD-01 Stage 1's Decision dropdown is set to
  // "Need Revision" and Save is clicked (see handleSave above). Rendered alongside either
  // return path below so it works whether this form is shown inline or as a modal.
  const trRevisionModalMarkup = isFtrd01Stage1Scope && (
    <Modal
      show={showTrRevisionModal}
      onHide={() => { if (!submittingTrRevision) setShowTrRevisionModal(false); }}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Technical Review — Need Revision</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          This sends the audit back to the Auditor for revision. Stage 2 remains locked until Stage 1 is resubmitted with "Continue to Stage 2".
        </Alert>
        {trRevisionError && <Alert variant="danger">{trRevisionError}</Alert>}
        <Form.Group>
          <Form.Label className="fw-semibold">
            Revision Notes <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={trRevisionNotes}
            onChange={(e) => setTrRevisionNotes(e.target.value)}
            placeholder="Describe what needs to be revised..."
            disabled={submittingTrRevision}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setShowTrRevisionModal(false)} disabled={submittingTrRevision}>
          Cancel
        </Button>
        <Button variant="warning" onClick={submitTrNeedRevision} disabled={submittingTrRevision || !trRevisionNotes.trim()}>
          {submittingTrRevision ? 'Submitting…' : 'Submit Need Revision'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // ── Inline (non-modal) render ──────────────────────────────────────────
  if (inline) {
    return (
      <div className="inline-form-wrapper audit-form-shell">
        {autoSaveBadge && <div className="d-flex justify-content-end px-4 pt-3">{autoSaveBadge}</div>}
        <div className="px-4 py-3">
          {formBodyContent}
        </div>
        {/* Sticky inline: unlike the modal render (whose Modal.Footer is always
            on screen), the inline form is as tall as the page, so Save used to
            sit far below the fold. Degrades to a normal footer if an ancestor
            ever establishes a scroll container. */}
        <div className="d-flex justify-content-between px-4 py-3 border-top audit-form-sticky-actions">
          {formFooterContent}
        </div>
        {trRevisionModalMarkup}
      </div>
    );
  }

  // ── Modal render ──────────────────────────────────────────────────────
  return (
    <>
      {/* The dialog, not the body, owns the height budget (see
          .document-form-modal in pdfExport.css). A fixed `maxHeight: 75vh` on
          Modal.Body used to stack on top of the header and footer, so on a
          phone the dialog ran taller than the viewport: the body scrolled
          internally while the footer's Save/Cancel sat off-screen below it,
          and `backdrop="static"` meant tapping outside was not an escape
          either. Capping .modal-content at the dynamic viewport height and
          letting the body flex-scroll between a fixed header and footer keeps
          the actions reachable at every size. */}
      <Modal show={show} onHide={onHide} size="xl" backdrop="static" centered dialogClassName="document-form-modal">
        <Modal.Header closeButton className="modal-header-enhanced audit-form-shell">
          <div className="d-flex align-items-center gap-3 w-100">
            <Modal.Title className="fw-semibold">{modalTitle}</Modal.Title>
            {autoSaveBadge && <div className="ms-auto">{autoSaveBadge}</div>}
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 py-3 audit-form-shell">
          {formBodyContent}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between bg-light border-top audit-form-shell">
          {formFooterContent}
        </Modal.Footer>
      </Modal>
      {trRevisionModalMarkup}
    </>
  );
};

// ── Form switcher ──────────────────────────────────────────────────────────
const FormBody = ({ docCode, fieldErrors = {}, formData, set, setNested, setFormData, auditorOptions, stages, selectedScope, setSelectedScope, availableStandards, autoAuditType, loadingScope, projectId, auditSchedule, requirementOptions, requirementLookup, requirementsLoading, standardSelectOptions, projectMeta, scopeLabel, scopeKey, getCycleMeta, setCycleMeta, getCoverage, toggleCoverage, getRowNotes, setRowNotes, fapp02Template }) => {
  switch (docCode) {
    case 'FAPP-01 Audit Plan':
      return <AuditPlanForm formData={formData} set={set} setNested={setNested} setFormData={setFormData} auditorOptions={auditorOptions} selectedScope={selectedScope} setSelectedScope={setSelectedScope} availableStandards={availableStandards} autoAuditType={autoAuditType} loadingScope={loadingScope} requirementOptions={requirementOptions} requirementsLoading={requirementsLoading} standardSelectOptions={standardSelectOptions} onRegenerateSchedule={() => setFormData((prev) => normalizeAuditPlanFormData(prev, { projectMeta, stages, auditorOptions, auditSchedule, scopeLabel, autoAuditType, standardSelectOptions }, true))} />;
    case 'Audit Summary Report':
      return <AuditSummaryForm formData={formData} set={set} setFormData={setFormData} auditorOptions={auditorOptions} />;
    case 'FAPP-06 Certificate Information Form':
    case 'FAPP-06 CIF (signed by client)':
      return <CertificateInfoForm docCode={docCode} formData={formData} set={set} setFormData={setFormData} auditorOptions={auditorOptions} projectMeta={projectMeta} auditSchedule={auditSchedule} standardSelectOptions={standardSelectOptions} scopeLabel={scopeLabel} />;
    case 'FAPP-07 Finding Form':
      return <FindingForm formData={formData} set={set} auditorOptions={auditorOptions} requirementOptions={requirementOptions} requirementLookup={requirementLookup} requirementsLoading={requirementsLoading} />;
    case 'FAPP-02 Three Years Audit Program':
      return <ThreeYearProgramForm formData={formData} set={set} setNested={setNested} setFormData={setFormData} selectedScope={selectedScope} setSelectedScope={setSelectedScope} auditorOptions={auditorOptions} availableStandards={availableStandards} loadingScope={loadingScope} getCycleMeta={getCycleMeta} setCycleMeta={setCycleMeta} getCoverage={getCoverage} toggleCoverage={toggleCoverage} getRowNotes={getRowNotes} setRowNotes={setRowNotes} fapp02Template={fapp02Template} />;
    case 'FAPP-03 Attendance List':
      return <AttendanceListForm formData={formData} set={set} setFormData={setFormData} auditorOptions={auditorOptions} />;
    case 'FAPP-04 Audit Notes':
      return <AuditNotesForm formData={formData} set={set} setFormData={setFormData} auditorOptions={auditorOptions} auditSchedule={auditSchedule} requirementOptions={requirementOptions} requirementsLoading={requirementsLoading} />;
    case 'FAPP-05 Audit Report':
      return <AuditReportForm formData={formData} set={set} setFormData={setFormData} auditorOptions={auditorOptions} selectedScope={selectedScope} setSelectedScope={setSelectedScope} availableStandards={availableStandards} loadingScope={loadingScope} projectId={projectId} standardSelectOptions={standardSelectOptions} />;
    case 'FAPP-08 Change Notice Form':
      return <ChangeNoticeForm formData={formData} set={set} />;
    case 'FAPP-07 Finding Form (accepted)':
      return <CorrectiveActionFindingForm formData={formData} set={set} auditorOptions={auditorOptions} requirementOptions={requirementOptions} requirementsLoading={requirementsLoading} />;
    case 'FTRD-01 Technical Review and Certification Decision':
      return <TechnicalReviewForm formData={formData} set={set} setFormData={setFormData} auditorOptions={auditorOptions} selectedScope={selectedScope} setSelectedScope={setSelectedScope} availableStandards={availableStandards} loadingScope={loadingScope} standardSelectOptions={standardSelectOptions} scopeKey={scopeKey} />;
    case 'FTRD-02 Certificate Review and Production':
      return <CertificateReviewProductionForm formData={formData} fieldErrors={fieldErrors} set={set} setFormData={setFormData} auditorOptions={auditorOptions} projectMeta={projectMeta} auditSchedule={auditSchedule} scopeLabel={scopeLabel} />;
    default:
      return <p className="text-muted small">No form available for this document.</p>;
  }
};

// ── Shared helpers ─────────────────────────────────────────────────────────
// `error` is a save-time validation message for this field (see
// documentFormValidationUtils). `fieldKey` becomes the group's DOM id so
// handleSave can scroll the first offending field into view.
// A field group can wrap a single plain control (the common case) or a whole
// composite -- a grid of sites, a table, react-select, a Quill editor. Only the
// first kind is labellable, so the real <label htmlFor> / aria wiring is applied
// to that case and everything else is left exactly as it was rendered.
const LABELLABLE_DOM_TAGS = ['input', 'textarea', 'select'];

// FormFieldSelect is included because it forwards the injected id to react-select's
// own text input via inputId -- without it, every converted dropdown would silently
// lose the <label htmlFor> association FG otherwise gives it.
const isLabellableControl = (node) => React.isValidElement(node)
  && (node.type === Form.Control
    || node.type === FormFieldSelect
    || (typeof node.type === 'string' && LABELLABLE_DOM_TAGS.includes(node.type)));

const FG = ({ label, children, required, hint, autoPopulated, error, fieldKey }) => {
  const generatedId = useId();
  const controlId = `control-${fieldKey || generatedId}`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const labellable = isLabellableControl(children);
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined;
  const control = labellable
    ? React.cloneElement(children, {
      id: children.props.id || controlId,
      'aria-describedby': children.props['aria-describedby'] || describedBy,
      'aria-invalid': error ? true : children.props['aria-invalid'],
    })
    : children;

  return (
    <Form.Group
      className={`mb-3 field-group${error ? ' field-group--invalid' : ''}`}
      id={fieldKey ? `field-${fieldKey}` : undefined}
    >
      <div className="form-label-enhanced">
        {/* A <label> with no control to point at is worse than a span, so the
            composite case keeps rendering plain text. */}
        {labellable ? (
          <label htmlFor={children.props.id || controlId} className="mb-0">
            {label}
            {required && <span className="required-indicator">*</span>}
          </label>
        ) : (
          <span>
            {label}
            {required && <span className="required-indicator">*</span>}
          </span>
        )}
        {autoPopulated && <span className="auto-populated-badge">Auto-filled</span>}
      </div>
      {hint && <span className="field-hint" id={hintId}>{hint}</span>}
      <div>
        {control}
      </div>
      {error && <span className="invalid-feedback" id={errorId}>{error}</span>}
    </Form.Group>
  );
};

// Spacing lives entirely in .form-section-title so the section rule's breathing
// room is not split between a utility class and the stylesheet (whichever of
// .mt-4 / .form-section-title came last in the bundle would have won).
const SectionHeader = ({ children }) => (
  <div className="form-section-title">
    {children}
  </div>
);

const auditTypeOptions = ['Initial Certification', 'Surveillance 1', 'Surveillance 2', 'Recertification', 'Special Audit', 'Transfer'];

// ── FAPP-05 Audit Report — stage & standard detection ───────────────────────
// Per "Audit Platform Enhancement Requirement - Document FAPP" (2026-07-09):
// PART 2 recommendation wording, the NC review boilerplate, and PART 6 "Audit
// Summary" subsections all differ by audit stage and/or certification standard.
// f.auditType is already auto-populated from the selected scope (see the
// setAutoAuditType effect above) as e.g. "Stage 1 (Document Review)",
// "Stage 2 (Main Audit)", "Surveillance 1st", "Recertification".
const deriveAuditReportStage = (auditTypeText = '') => {
  const t = String(auditTypeText).toLowerCase();
  if (t.includes('recert')) return 'recertification';
  if (t.includes('surveillance')) return 'surveillance';
  if (/stage[\s-]*1\b/.test(t) || /\bstage\s*i\b(?!i)/.test(t) || t.includes('document review')) return 'stage1';
  return 'stage2';
};

const AUDIT_REPORT_STANDARD_PATTERNS = [
  ['PIMS', /27701/],
  ['ISMS', /27001/],
  ['ITSM', /20000-?1/],
  ['QMS', /\b9001\b/],
  ['OHSMS', /45001/],
  ['ABMS', /37001/],
  ['CSS', /27017/],
  ['PII', /27018/],
  ['ST', /29119/],
  ['BCMS', /22301/],
  ['BIM', /19650/],
  ['PM', /21500/],
];
const deriveAuditReportStandardKey = (applicableStandards) => {
  const text = (Array.isArray(applicableStandards) ? applicableStandards.join(' ') : String(applicableStandards || '')).toLowerCase();
  const found = AUDIT_REPORT_STANDARD_PATTERNS.find(([, re]) => re.test(text));
  return found ? found[0] : 'ISMS'; // ISMS is the baseline/reference template
};

// PART 2 "Result of Audit (Lead Auditor Recommendation)" — verbatim wording per
// stage, transcribed from the released FAPP-05 templates (ISMS baseline; the
// wording is standard across all 12 certification schemes).
const AUDIT_REPORT_RECOMMENDATION_OPTIONS = {
  stage1: [
    ['s1_proceed', 'No Action Required — It is recommended to proceed directly with the stage II activity. The level of implementation of the management system is sufficient.'],
    ['s1_proceed_minor_aoc', 'No Action Required — It is recommended to proceed with the stage II activity (only Minor-Area of Concern were raised). If Major-Area of Concern (if any) is not addressed by client prior to stage II, the recommendation for certification is in jeopardy during the stage 2 activity.'],
    ['s1_not_recommended', 'Immediate Action Required — It is not recommended to proceed with the stage II activity. The level of implementation of the management system is insufficient.'],
  ],
  stage2: [
    ['s2_recommended', 'The management system is in conformity with the audit criteria and can be considered effective in assuring that objectives will be met. Therefore, certification is recommended.'],
    ['s2_pending_car', 'The nonconformity(ies) identified do not jeopardize the certification of the management system. Certification is therefore recommended pending acceptance of the corrective action plan(s) for identified nonconformity(ies).'],
    ['s2_followup', 'Due to major nonconformity(ies) and/or the total number of minor non-conformities reported, the conformity of the management system with the audit criteria is potentially in jeopardy. As a result, a subsequent On-Site follow-up audit is recommended.'],
    ['s2_reaudit', 'Due to major nonconformity(ies) and/or the total number of minor non-conformities reported, the conformity of the management system with the audit criteria is in jeopardy. As a result, a subsequent and complete re-audit to the full system is recommended.'],
    ['s2_not_recommended', 'Based on nonconformity(ies), initial certification is not recommended. CBQA Global will contact the organization to discuss the subsequent step(s) of the certification process.'],
  ],
  surveillance: [
    ['sv_continued', 'The management system is in conformity with the audit criteria and can be considered effective in assuring that objectives will be met. Therefore, Continuance of Certification is recommended.'],
    ['sv_pending_car', 'The nonconformity(ies) identified do not jeopardize the certification of the management system. Continuance of Certification is therefore recommended, pending acceptance of the corrective action plan(s) for identified nonconformity(ies).'],
    ['sv_followup', 'Due to major nonconformity(ies) and/or the total number of minor non-conformities reported, the conformity of the management system with the audit criteria is potentially in jeopardy. As a result, a subsequent On-Site follow-up audit is recommended.'],
    ['sv_reaudit', 'Due to major nonconformity(ies) and/or the total number of minor non-conformities reported, the conformity of the management system with the audit criteria is in jeopardy. As a result, a subsequent and complete Re-audit to the full system is recommended.'],
    ['sv_suspend', 'Due to major nonconformity(ies) and/or the total number of minor non-conformities reported, the conformity of the management system with the audit criteria is in jeopardy. As a result, Suspension of the certification is recommended.'],
    ['sv_withdraw', 'Due to major nonconformity(ies) and/or the total number of minor non-conformities reported, the conformity of the management system with the audit criteria is in jeopardy. As a result, Withdrawal of the certification is recommended.'],
  ],
  recertification: [
    ['rc_continued', 'The management system is in conformity with the audit criteria and can be considered effective in assuring that objectives will be met. Therefore, Continuance of Certification is recommended.'],
    ['rc_pending_car', 'The nonconformity(ies) identified do not jeopardize the certification of the management system. Continuance of Certification is therefore recommended, pending acceptance of the corrective action plan(s) for identified nonconformity(ies).'],
    ['rc_followup', 'Due to major nonconformity(ies) and/or the total number of minor non-conformities reported, the conformity of the management system with the audit criteria is potentially in jeopardy. As a result, a subsequent On-Site follow-up audit is recommended.'],
    ['rc_reaudit', 'Due to major nonconformity(ies) and/or the total number of minor non-conformities reported, the conformity of the management system with the audit criteria is in jeopardy. As a result, a subsequent and complete re-audit to the full system is recommended.'],
    ['rc_not_recommended', 'Based on nonconformity(ies), re-certification can not be continued, CBQA Global will contact the organization to discuss the subsequent step(s) of the certification process.'],
  ],
};
const AUDIT_REPORT_RECOMMENDATION_TITLE = {
  stage1: null, // tier label is embedded in each option's text above
  stage2: 'Recommendation for Initial Certification',
  surveillance: 'Recommendation for Continuance of Certification',
  recertification: 'Recommendation for Continued Certification',
};

// PART 5 findings-table shape — Stage 1 uses AOC (Area of Concern) terms with a
// single "Current Activity" row; Stage 2/Surveillance/Recertification compare
// "Previous Activity" (carried over) against "Current Activity" using NC terms.
const AUDIT_REPORT_FINDINGS_COLUMNS = {
  stage1: [['aocMajorIssued', 'AOC Major Issued'], ['aocMinorIssued', 'AOC Minor Issued'], ['openNCs', "Open NC's"]],
  default: [['majorIssued', 'Major Issued'], ['majorClosed', 'Major Closed'], ['minorIssued', 'Minor Issued'], ['minorClosed', 'Minor Closed'], ['openNCs', "# Open NC's"]],
};

// NOTES to Non-Conformity — controlled-document boilerplate, verbatim from the
// released templates, identical across all 12 standards. Not shown for Stage 1
// (there is no prior cycle's nonconformities to review yet).
const AUDIT_REPORT_NC_NOTES_A = 'Corrections and Corrective actions (if possible) to address identified major nonconformities shall be carried out immediately. Correction, Root Cause Analysis and Corrective action plan together with satisfactory evidences of implementation shall be submitted within 90 days after the last day of the audit unless CBQA Global and client agree on a longer period of time. Review of nonconformities is done through desktop review. However, depending of severity of the findings, our auditor may perform a follow up visit to confirm the actions taken, evaluate their effectiveness, and determine whether certification can be recommended or continued. For a minor nonconformity, correction, root cause analysis and corrective action plan shall be approved by the team leader and verification of implementation and effectiveness of corrective action(s) taken will be performed at the next visit. It is recommended that the Client provide responses early to allow time for additional reviews if needed. For recertification time limits to address nonconformities will be defined by the team leader in order to have them implemented prior to expiration of certification. Any responses to the nonconformities which were raised may be either in hard copy or electronically using the NCR herein (preferred) and forwarded to the CBQA Global office.';
const AUDIT_REPORT_NC_NOTES_B = 'Client response to NCR should be reviewed by the lead auditor in three parts: correction, root cause analysis and corrective actions. In reviewing the three parts, the auditor looks for a plan and then evidence that plan is being implemented.\n\nCorrection\n- The extent of the nonconformity has been determined (NCR has been corrected & the client has examined the system to see if there are other examples that need to be corrected). Ensure that correction answers the question "Is this isolated case or not?" in other words "Is there a risk that this can reoccur at the other site / department?"\n- If correction cannot be immediate; a plan to correct the NCR may be appropriate (responsible & date).\n- Evidence that the correction was implemented or evidence that the plan is being implemented.\n\nRoot Cause Analysis\n- The Root Cause is not simply repeating the finding, neither is the direct cause of the issue.\n- Well thought out analysis to determine the true root cause: e.g. someone did not follow a process would be direct cause; determining why someone did not follow a process would lead to the true root cause.\n- The root cause statement must focus on a single issue without any obvious "why" questions remaining. If a "why" question can reasonably be asked about the root cause analysis, this indicates that the analysis did not go far enough.\n- Ensure that the root cause answers the question, "What in the system failed such that the problem occurred?"\n- Blaming the employee will not be accepted as the only root cause\n- Address problems with the process as well as what detection system failed\n\nCorrective Action\n- The corrective action or corrective action plan addresses the root cause(s) determined in the root cause analysis. If you have not defined true root cause you cannot prevent the problem from its reoccurrence\n- In order to accept the plan, it shall include: actions to address the root cause(s), identification of responsible parties for the actions, and a schedule (dates) for implementation — always include a "change" to your system. Training and/or publishing a newsletter are generally not changes to your system\n- In order to accept the evidence of implementation: enough evidence is provided to show the plan is being implemented as outlined in the response (and on schedule).';

// PART 6 "Audit Summary" — common skeleton (all standards) followed by
// per-standard subsections, in the order they appear in the released templates.
const AUDIT_REPORT_SUMMARY_COMMON = [
  ['context', 'Conclusions regarding Context of the Organization'],
  ['scope', 'Verification of Scope'],
  ['policy', 'Policy & Commitment'],
];
const AUDIT_REPORT_SUMMARY_TAIL = [
  ['listOfNonConformities', 'List/Summary of Non-Conformities'],
  ['significantChanges', 'Significant Changes impacting on the Audit Program (if any)'],
  ['significantIssues', 'Significant Issues and its Impact to the next audit (if any)'],
  ['referencesToAppendices', 'References to appendices'],
  ['certMarkUsage', 'Using of CBQA Certification Mark & Logo'],
];
const AUDIT_REPORT_SUMMARY_BY_STANDARD = {
  ISMS: [['risk', 'Conclusions regarding Risk Assessment / Risk Treatment processes'], ['soa', 'Statement of Applicability (SoA)'], ['objectives', 'Objectives'], ['communication', 'Communication and Information requirements (including Awareness)'], ['performance', 'Performance Evaluation (through Internal Audit & Management Review processes)']],
  PIMS: [['risk', 'Conclusions regarding Risk Assessment / Risk Treatment processes'], ['soa', 'Statement of Applicability (SoA)'], ['objectives', 'PIMS Objectives'], ['communication', 'Communication and Information requirements (including Awareness)'], ['performance', 'Performance Evaluation (through Internal Audit & Management Review processes)'], ['piiProcessing', 'PII Processing Activities (By PII Controller and/or PII Processor)'], ['privacyIncident', 'Privacy Incident Management'], ['ropa', 'Record of Processing Activities'], ['pia', 'Privacy Impact Assessment']],
  CSS: [['risk', 'Conclusions regarding Risk Assessment / Risk Treatment processes'], ['soa', 'Statement of Applicability (SoA)'], ['objectives', 'Objectives'], ['communication', 'Communication and Information requirements (including Awareness)'], ['performance', 'Performance Evaluation (through Internal Audit & Management Review processes)']],
  PII: [['risk', 'Conclusions regarding Risk Assessment / Risk Treatment processes'], ['soa', 'Statement of Applicability (SoA)'], ['objectives', 'Objectives'], ['communication', 'Communication and Information requirements (including Awareness)'], ['performance', 'Performance Evaluation (through Internal Audit & Management Review processes)']],
  ITSM: [['risk', 'Conclusions regarding Risk Assessment / Risk Treatment processes'], ['objectives', 'Objectives'], ['communication', 'Communication and Information requirements (including Awareness)'], ['performance', 'Performance Evaluation (through Internal Audit & Management Review processes)'], ['itServicesProvision', 'IT Services Provision (related to ITSM scope of certification)'], ['serviceContinuity', 'Service Continuity Management (inc. Incident Management)']],
  QMS: [['risk', 'Conclusions regarding Risk Assessment / Risk Treatment processes'], ['objectives', 'Objectives'], ['communication', 'Communication and Information requirements (including Awareness)'], ['performance', 'Performance Evaluation (through Internal Audit & Management Review processes)'], ['customerFocus', 'Customer Focus, Feedback and Satisfaction'], ['productionServiceProvision', 'Production and Service Provision']],
  OHSMS: [['risk', 'Conclusions regarding Risk Assessment / Risk Treatment processes'], ['objectives', 'Objectives'], ['communication', 'Communication, Participation and Consultation Requirements (including Awareness, Resources, Roles, Responsibilities & Authorities and Competence)'], ['operationalPlanning', 'Operational Planning and Control (Including MoC, Procurement, ERP)'], ['performance', 'Performance Evaluation (Including Monitoring & Measurement, Evaluation of Compliance, Internal Audit & Management Review Processes and Incident)']],
  ABMS: [['risk', 'Conclusions regarding Bribery Risk Assessment / Risk Treatment processes'], ['objectives', 'Anti-Bribery Objectives'], ['communication', 'Resources (including Employment Process), Competency, Communication'], ['performance', 'Performance Evaluation (through Internal Audit & Management Review processes)']],
  BCMS: [['risk', 'Conclusions regarding Risk Assessment / Risk Treatment processes'], ['objectives', 'Objectives (BCMS)'], ['communication', 'Communication and Information requirements (including Awareness)'], ['performance', 'Performance Evaluation (through Internal Audit & Management Review processes)'], ['productionServiceProvision', 'Production and Service Provision (including BCM / BCP Implementation)']],
  BIM: [['risk', 'Conclusions regarding Risk Assessment / Risk Treatment processes'], ['objectives', 'Objectives'], ['communication', 'Communication and Information requirements (including Awareness)'], ['performance', 'Performance Evaluation (through Internal Audit & Management Review processes)']],
  // ST (29119) and PM (21500) replace the ISMS-style context/policy/risk flow entirely with
  // domain-specific sections — handled as a full override list, not common+extra.
  ST: { override: [
    ['testPlansStrategies', 'Test plans and strategies'],
    ['testFrameworks', 'Test Frameworks'],
    ['testDesignExecution', 'Test design and execution'],
    ['projectManagementTesting', 'Project management and testing'],
    ['communicationReporting', 'Communication and reporting'],
    ['defectsIncidentManagement', 'Defects and incident management'],
    ['riskManagementProcess', 'Risk and Risks Management Process'],
    ['identifiedNonConformity', 'Identified Non-Conformity'],
  ] },
  PM: { override: [
    ['projectManagementConcepts', 'Conclusions regarding Project Management Concepts'],
    ['scope', 'Verification of Scope'],
    ['integratedPMPractice', 'Integrated Project Management Practice'],
    ['planningBenefitScopeResourceScheduleCost', 'Planning, Benefit, Scope, Resources, Schedule and Cost Management'],
    ['riskIssueManagement', 'Risk and Issue Management'],
    ['qualityManagement', 'Quality Management'],
    ['communicationInfoDocManagement', 'Communication, Information, and Documentation Management'],
    ['changeControl', 'Change Control'],
    ['procurement', 'Procurement'],
  ] },
};
const getAuditReportSummarySections = (standardKey) => {
  const config = AUDIT_REPORT_SUMMARY_BY_STANDARD[standardKey] || AUDIT_REPORT_SUMMARY_BY_STANDARD.ISMS;
  if (Array.isArray(config?.override)) {
    return [...config.override, ...AUDIT_REPORT_SUMMARY_TAIL];
  }
  return [...AUDIT_REPORT_SUMMARY_COMMON, ...(Array.isArray(config) ? config : []), ...AUDIT_REPORT_SUMMARY_TAIL];
};

const AUDIT_REPORT_TEAM_ROLES = [
  ['leadAuditor', 'Lead Auditor'],
  ['auditor1', 'Auditor (if needed)'],
  ['auditor2', 'Auditor (if needed)'],
  ['observer', 'Observer (if needed)'],
];
const AUDIT_REPORT_ACTIVITY_OPTIONS = [
  ['stage1', 'Initial Audit - Stage I'],
  ['stage2', 'Stage II'],
  ['surveillance', 'Surveillance audit No: MS-'],
  ['recertification', 'Re-certification Audit'],
  ['changeOfScope', 'Change of scope (specify):'],
  ['specialSurveillance', 'Special surveillance purpose (specify):'],
  ['other', 'Other:'],
];
const AUDIT_REPORT_OBJECTIVES_INTRO = "Audit objectives aims to verify the compliance of the organization's management system, assess the organization's capability to adhere to relevant statutory, regulatory, and contractual obligations, evaluate the effectiveness of the management system in ensuring that the organization has implemented necessary controls based on risk assessment and achieved its intended outcomes, and identify opportunities for enhancing the management system within the organization. The objectives of the audit are then outlined in the audit cycle as follows:";
const AUDIT_REPORT_OBJECTIVES_BULLETS = [
  "Stage-1 Audit – Audit management system documentation; evaluation client's location and site-specific conditions and the preparedness for the stage 2 audit; reviewing the client's status and understanding regarding requirements of the standard; collecting necessary information regarding the scope of the management system; reviewing the allocation of resources for stage 2 audit and agree with the client on the details of the stage 2 audit; planning the stage 2 audit; evaluation if the internal audits and management review are being planned and performed.",
  'Initial Audits / Stage-2 – Evaluation of the conformance and implementation of the management system with applicable standard(s) as well evaluation of the ability of the management system to ensure the client organization meets applicable statutory, regulatory and contractual requirements in order to determine if the facility can be recommended for Certification.',
  "Recertification Audits – Evaluation the continued fulfilment of all the requirements of the relevant management system standard or other normative document for renewal of the certification. Evaluation of the effectiveness of the management system in its entirety in the light of internal and external changes and its continued relevance and applicability to the scope of certification, evaluation of demonstrated commitment to maintain the effectiveness and improvement of the management system in order to enhance overall performance as well as the effectiveness of the management system with regard to achieving the certified client's objectives and the intended results of the respective management system(s).",
  'Surveillance – Evaluation of the conformance and effectiveness of the management system with applicable standard(s) for the purpose of continuing of the certification as well evaluation of the effectiveness of the management system to ensure the client organization is continually meeting its specified objectives.',
  'Special Audit – Evaluation of effectiveness of correction and corrective action implemented in order to close nonconformity issued during previous audit and/or to investigate complaints received and/or in response to changes and/or as follow up on suspended clients.',
  'Special Audit (Scope Extension) – Evaluation of the conformance and implementation of the management system with applicable standard(s) against with extension application',
];
const AUDIT_REPORT_METHODOLOGY_BULLETS = ["Documented Information's verification", 'Observation', 'Interview', 'Sampling (sample)'];

// ── FAPP-01 Audit Plan ─────────────────────────────────────────────────────
const AuditPlanForm = ({ formData: f, set, setFormData, auditorOptions, requirementOptions = [], requirementsLoading = false, standardSelectOptions = [], onRegenerateSchedule }) => {
  const [showPreview, setShowPreview] = useState(false);
  const addRundown = () =>
    setFormData((p) => ({
      ...p,
      rundowns: [...(p.rundowns || []), {
        type: 'Event', date: p.auditStartDate || '', timeFrom: '', timeTo: '', locationSite: p.auditSite || 'On site',
        auditorIds: [], clientProcesses: '', processOwner: '', clausulAnnex: [],
      }],
    }));
  const removeRundown = (i) =>
    setFormData((p) => ({ ...p, rundowns: p.rundowns.filter((_, idx) => idx !== i) }));
  const setRundown = (i, field, val) =>
    setFormData((p) => {
      const arr = [...(p.rundowns || [])];
      arr[i] = { ...arr[i], [field]: val };
      return { ...p, rundowns: arr };
    });
  const toggleAuditor = (i, auditorId) =>
    setFormData((p) => {
      const arr = [...(p.rundowns || [])];
      const ids = arr[i].auditorIds || [];
      arr[i] = { ...arr[i], auditorIds: ids.includes(auditorId) ? ids.filter((x) => x !== auditorId) : [...ids, auditorId] };
      return { ...p, rundowns: arr };
    });

  return (
    <>
      <div className="form-section">
        <div className="form-section-title">Document Issue Information</div>
        <Row>
          <Col md={4}>
            <FG label="Issue Date" required>
              <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.issueDate || ''}
                onChange={(e) => set('issueDate', e.currentTarget.value)}
                onInput={(e) => set('issueDate', e.currentTarget.value)} />
            </FG>
          </Col>
          <Col md={4}>
            <FG label="Revision Date">
              <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.revDate || ''} onChange={(e) => set('revDate', e.target.value)} />
            </FG>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Form.Check type="checkbox" id="executed" label="Mark as Executed"
              checked={!!f.executed} onChange={(e) => set('executed', e.target.checked)} className="small" />
          </Col>
        </Row>
      </div>

      <div className="form-section">
        <div className="form-section-title">Audit Plan Header</div>
        <Row>
          <Col md={6}>
            <FG label="Company">
              <Form.Control size="sm" className="form-control-enhanced" value={f.clientCompany || ''} onChange={(e) => set('clientCompany', e.target.value)} placeholder="Client company name" />
            </FG>
          </Col>
          <Col md={6}>
            <FG label="PIC / Position">
              <Form.Control size="sm" className="form-control-enhanced" value={f.picPosition || ''} onChange={(e) => set('picPosition', e.target.value)} placeholder="PIC / Position" />
            </FG>
          </Col>
        </Row>
        <FG label="Audit Site(s)">
          <Form.Control size="sm" as="textarea" rows={2} className="form-control-enhanced" value={f.auditSite || ''} onChange={(e) => set('auditSite', e.target.value)} placeholder="Audit site address / location" />
        </FG>
        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold mb-1 text-secondary">Address Audit Site</Form.Label>
          <div className="small text-muted mb-1">Is the audit site location the same as the head office?</div>
          <div className="d-flex gap-3">
            <Form.Check type="radio" id="addr-yes" label="Yes" name="addrSite"
              checked={f.addressSameAsHeadOffice === 'yes' || f.addressSameAsHeadOffice === true}
              onChange={() => set('addressSameAsHeadOffice', 'yes')} />
            <Form.Check type="radio" id="addr-no" label="No" name="addrSite"
              checked={f.addressSameAsHeadOffice === 'no' || f.addressSameAsHeadOffice === false}
              onChange={() => set('addressSameAsHeadOffice', 'no')} />
          </div>
        </Form.Group>
      </div>

      <div className="form-section">
        <div className="form-section-title">Audit Details</div>
        <Row>
          <Col md={6}>
            <FG label="Prepared By" autoPopulated={!!f.preparedBy}>
              <FormFieldSelect value={f.preparedBy || ''} onChange={(e) => set('preparedBy', e.target.value)}>
                <option value="">Select prepared by</option>
                {auditorOptions.filter((a) => a.isScheduled).map((a) => <option key={a.userId || a.id} value={a.name || a.fullName}>{a.name || a.fullName}</option>)}
              </FormFieldSelect>
            </FG>
          </Col>
          <Col md={6}>
            <FG label="Audit Language" required>
              <FormFieldSelect value={f.auditLanguage || 'Bahasa Indonesia'} onChange={(e) => set('auditLanguage', e.target.value)}>
                <option>Bahasa Indonesia</option>
                <option>English</option>
              </FormFieldSelect>
            </FG>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FG label="Audit Type">
              <FormFieldSelect value={f.auditType || ''} onChange={(e) => set('auditType', e.target.value)}>
                <option value="">Select audit type</option>
                {auditTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </FormFieldSelect>
            </FG>
          </Col>
          <Col md={6}>
            <FG label="NACE/Product/Service/EAC Code(s)">
              <Form.Control size="sm" className="form-control-enhanced" value={f.naceCode || ''} onChange={(e) => set('naceCode', e.target.value)} placeholder="No-code / EAC code" />
            </FG>
          </Col>
        </Row>
        <FG label="Applicable Standard(s). Ref.">
          <MultiValueSelect
            value={f.applicableStandards}
            options={standardSelectOptions}
            onChange={(value) => set('applicableStandards', value)}
            placeholder="Select applicable standards"
            noOptionsMessage={() => 'No standards available for this scope'}
          />
        </FG>
        <FG label="Audit Criteria Details">
          <Form.Control size="sm" as="textarea" rows={2} className="form-control-enhanced" value={f.auditCriteriaDetails || ''} onChange={(e) => set('auditCriteriaDetails', e.target.value)} placeholder="Processes and documentation of the management system developed by the company" />
        </FG>
        <FG label="Scope of Certification">
          <Form.Control size="sm" as="textarea" rows={2} className="form-control-enhanced" value={f.scopeOfCertification || ''} onChange={(e) => set('scopeOfCertification', e.target.value)} placeholder="Certification scope" />
        </FG>
        <FG label="Audit Objectives" hint="Free text summary for this engagement; the controlled definitions below are shown for reference and always included in the export.">
          <Form.Control size="sm" as="textarea" rows={3} className="form-control-enhanced" value={f.auditObjectives || ''} onChange={(e) => set('auditObjectives', e.target.value)} placeholder="Audit objectives" />
        </FG>
        <details className="mb-3">
          <summary className="small text-muted" style={{ cursor: 'pointer' }}>View controlled Audit Objectives definitions (FAPP-01 V2)</summary>
          <p className="small text-muted mt-2">{AUDIT_REPORT_OBJECTIVES_INTRO}</p>
          <ul className="small text-muted">
            {AUDIT_REPORT_OBJECTIVES_BULLETS.map((text, i) => <li key={i}>{text}</li>)}
          </ul>
        </details>
        <FG label="Audit Team / Role">
          <Form.Control size="sm" as="textarea" rows={2} className="form-control-enhanced" value={f.auditTeamRole || ''} onChange={(e) => set('auditTeamRole', e.target.value)} placeholder="Auditor team and role" />
        </FG>
      </div>

      <Row>
        <Col md={3}>
          <FG label="On-site Audit Days">
            <Form.Control size="sm" className="form-control-enhanced" value={f.onSiteAuditDays || ''} onChange={(e) => set('onSiteAuditDays', e.target.value)} placeholder="e.g. 1.5" />
          </FG>
        </Col>
        <Col md={3}>
          <FG label="Off-site Audit Days">
            <Form.Control size="sm" className="form-control-enhanced" value={f.offSiteAuditDays || ''} onChange={(e) => set('offSiteAuditDays', e.target.value)} placeholder="e.g. 0.5" />
          </FG>
        </Col>
        <Col md={3}>
          <FG label="No of Shift" hint="Total number of shifts operating at the facility">
            <Form.Control size="sm" className="form-control-enhanced" type="number" value={f.noOfShift || ''} onChange={(e) => set('noOfShift', e.target.value)} placeholder="e.g. 2 or 3" />
          </FG>
        </Col>
        <Col md={3}>
          <FG label="Shift to be Audited" hint="Which shift(s) will be included in the audit">
            <Form.Control size="sm" className="form-control-enhanced" value={f.shiftToBeAudited || ''} onChange={(e) => set('shiftToBeAudited', e.target.value)} placeholder="e.g. All shifts" />
          </FG>
        </Col>
      </Row>

      <div className="form-section mt-4">
        <div className="form-section-title">Audit Schedule</div>
        <Row>
          <Col md={6}>
            <FG label="Audit Start Date" required>
              <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.auditStartDate || ''} onChange={(e) => set('auditStartDate', e.target.value)} />
            </FG>
          </Col>
          <Col md={6}>
            <FG label="Audit End Date" required>
              <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.auditEndDate || ''} onChange={(e) => set('auditEndDate', e.target.value)} />
            </FG>
          </Col>
        </Row>
      </div>

      <div className="form-section">
        <div className="form-section-title">Closing Notes for Export</div>
        <FG label="Preparation Requests">
          <Form.Control size="sm" as="textarea" rows={4} className="form-control-enhanced" value={f.preAuditRequests || ''} onChange={(e) => set('preAuditRequests', e.target.value)} placeholder="One line per bullet point" />
        </FG>
        <FG label="Additional Information">
          <Form.Control size="sm" as="textarea" rows={6} className="form-control-enhanced" value={f.additionalInformation || ''} onChange={(e) => set('additionalInformation', e.target.value)} placeholder="One line per numbered note" />
        </FG>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2 mt-4">
        <SectionHeader style={{ margin: 0 }}>Audit Event Overview</SectionHeader>
        <div className="d-flex gap-2">
          <div className="btn-group" role="group">
            <button type="button" className={`btn btn-sm ${!showPreview ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setShowPreview(false)}>Edit</button>
            <button type="button" className={`btn btn-sm ${showPreview ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setShowPreview(true)}>Table Preview</button>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={onRegenerateSchedule}>
            Regenerate
          </Button>
        </div>
      </div>

      {showPreview ? (
        <div className="table-responsive mb-3">
          <table className="table table-sm table-bordered" style={{ fontSize: '0.8rem' }}>
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: 80 }}>Date</th>
                <th style={{ minWidth: 70 }}>Time</th>
                <th style={{ minWidth: 90 }}>Location</th>
                <th style={{ minWidth: 110 }}>Auditor(s)</th>
                <th>Client Processes</th>
                <th style={{ minWidth: 90 }}>Process Owner</th>
                <th style={{ minWidth: 90 }}>Clause/Annex</th>
              </tr>
            </thead>
            <tbody>
              {(f.rundowns || []).length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted py-3">No rundown entries yet. Switch to Edit to add rows.</td></tr>
              ) : (f.rundowns || []).map((rd, i) => {
                const auditorNames = (rd.auditorIds || [])
                  .map((id) => {
                    const a = auditorOptions.find((x) => String(x.userId || x.id) === String(id));
                    return a ? (a.name || a.fullName) : id;
                  })
                  .filter(Boolean)
                  .join(', ');
                const clauses = Array.isArray(rd.clausulAnnex)
                  ? rd.clausulAnnex.join(', ')
                  : (rd.clausulAnnex || '');
                return (
                  <tr key={i} style={rd.type === 'Break' ? { backgroundColor: '#f8f9fa' } : rd.type === 'Day' ? { backgroundColor: '#e8f5e9', fontWeight: 600 } : {}}>
                    <td>{rd.date || '-'}</td>
                    <td>{rd.timeFrom && rd.timeTo ? `${rd.timeFrom}–${rd.timeTo}` : rd.timeFrom || rd.timeTo || '-'}</td>
                    <td>{rd.locationSite || 'On site'}</td>
                    <td>{auditorNames || '-'}</td>
                    <td style={{ whiteSpace: 'pre-wrap' }}>{rd.clientProcesses || '-'}</td>
                    <td>{rd.processOwner || '-'}</td>
                    <td>{clauses || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          {(f.rundowns || []).map((rd, i) => (
            <div key={i} className="dynamic-row">
              <div className="dynamic-row-header">
                <span className="dynamic-row-index">Rundown {i + 1}</span>
                <div className="dynamic-row-actions">
                  <Button size="sm" variant="link" className="btn-remove-row p-0" onClick={() => removeRundown(i)}
                    disabled={(f.rundowns || []).length <= 1}><Trash2 size={14} /></Button>
                </div>
              </div>
              <div>
                <Row className="g-2 mb-3">
                  <Col xs={12} md={3}>
                    <div className="small text-muted text-uppercase mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Audit Date</div>
                    <Form.Control size="sm" type="date" value={rd.date || ''} onChange={(e) => setRundown(i, 'date', e.target.value)} />
                  </Col>
                  <Col xs={12} md={4}>
                    <div className="small text-muted text-uppercase mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Rundown Type</div>
                    <div className="btn-group w-100" role="group">
                      {['Event', 'Break', 'Day'].map((t) => (
                        <button key={t} type="button"
                          className={`btn btn-sm ${rd.type === t ? '' : 'btn-outline-secondary'}`}
                          style={rd.type === t ? { backgroundColor: '#1a767e', color: '#fff', borderColor: '#1a767e' } : {}}
                          onClick={() => setRundown(i, 'type', t)}>{t}</button>
                      ))}
                    </div>
                  </Col>
                  <Col xs={12} md={3}>
                    <div className="small text-muted text-uppercase mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Time</div>
                    <div className="d-flex align-items-center gap-1">
                      <Form.Control size="sm" type="time" value={rd.timeFrom || ''} onChange={(e) => setRundown(i, 'timeFrom', e.target.value)} style={{ width: 90 }} />
                      <span className="text-muted small">to</span>
                      <Form.Control size="sm" type="time" value={rd.timeTo || ''} onChange={(e) => setRundown(i, 'timeTo', e.target.value)} style={{ width: 90 }} />
                    </div>
                  </Col>
                  <Col xs={12} md={2}>
                    <div className="small text-muted text-uppercase mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Location/Site</div>
                    <Form.Control size="sm" value={rd.locationSite || 'On site'} onChange={(e) => setRundown(i, 'locationSite', e.target.value)} />
                  </Col>
                </Row>

                <div className="small text-muted text-uppercase mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Auditor</div>
                <div className="d-flex flex-wrap gap-3 mb-3 p-2 border rounded bg-white">
                  {(() => {
                    const scheduledAuditors = auditorOptions.filter(a => a.isScheduled);
                    return scheduledAuditors.length === 0
                      ? <span className="small text-muted">No scheduled auditors</span>
                      : scheduledAuditors.map((a) => {
                          const id = String(a.userId || a.id);
                          return (
                            <Form.Check key={id} type="checkbox" id={`aud-${i}-${id}`}
                              label={a.name || a.fullName}
                              checked={(rd.auditorIds || []).includes(id)}
                              onChange={() => toggleAuditor(i, id)} className="small" />
                          );
                        });
                  })()}
                </div>

                <FG label="Client Processes">
                  <Form.Control size="sm" as="textarea" rows={2} value={rd.clientProcesses || ''}
                    onChange={(e) => setRundown(i, 'clientProcesses', e.target.value)} placeholder="Enter Client Processes" />
                </FG>
                <FG label="Process Owner">
                  <Form.Control size="sm" value={rd.processOwner || ''}
                    onChange={(e) => setRundown(i, 'processOwner', e.target.value)} placeholder="ISMS Leader, Doc Controller, Risk, & IA" />
                </FG>
                <FG label="Clausul / Annex">
                  <MultiValueSelect
                    value={rd.clausulAnnex}
                    options={requirementOptions}
                    onChange={(value) => setRundown(i, 'clausulAnnex', value)}
                    isLoading={requirementsLoading}
                    placeholder="Select Clausul / Annex"
                    noOptionsMessage={() => 'No clauses or annexes available from backend'}
                  />
                </FG>
              </div>
            </div>
          ))}

          <Button variant="outline-primary" size="sm" className="w-100 mb-2" onClick={addRundown}>
            <Plus size={14} className="me-1" />Add Row/Rundown
          </Button>
        </>
      )}
    </>
  );
};

// ── Audit Summary Report ───────────────────────────────────────────────────
const AuditSummaryForm = ({ formData: f, set, setFormData, auditorOptions }) => {
  const addMember = () => setFormData((p) => ({ ...p, auditTeam: [...(p.auditTeam || []), { name: '' }] }));
  const removeMember = (i) => setFormData((p) => ({ ...p, auditTeam: p.auditTeam.filter((_, idx) => idx !== i) }));
  const setMember = (i, val) => setFormData((p) => { const arr = [...(p.auditTeam || [])]; arr[i] = { name: val }; return { ...p, auditTeam: arr }; });

  // Pre-populate audit team from scheduled auditors
  React.useEffect(() => {
    const scheduled = auditorOptions.filter(a => a.isScheduled);
    if (scheduled.length > 0 && (f.auditTeam || []).length === 0) {
      setFormData(p => ({ ...p, auditTeam: scheduled.map(a => ({ name: a.name || a.fullName || a.label || '' })) }));
    }
  }, [auditorOptions.length]); // eslint-disable-line

  return (
    <>
      <Row>
        <Col md={6}>
          <FG label="Audit Date" required>
            <Form.Control size="sm" type="date" value={f.auditDate || ''} onChange={(e) => set('auditDate', e.target.value)} />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Audit Type">
            <FormFieldSelect value={f.auditType || ''} onChange={(e) => set('auditType', e.target.value)}>
              <option value="">-- Select --</option>
              {auditTypeOptions.map((o) => <option key={o}>{o}</option>)}
            </FormFieldSelect>
          </FG>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FG label="Lead Auditor">
            <FormFieldSelect value={f.auditLeadId || ''} onChange={(e) => set('auditLeadId', e.target.value)}>
              <option value="">-- Select --</option>
              {auditorOptions.map((a) => <option key={a.userId || a.id} value={a.userId || a.id}>{a.name || a.fullName}</option>)}
            </FormFieldSelect>
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Applicable Standards" hint="Standards audited in this engagement">
            <Form.Control size="sm" value={Array.isArray(f.applicableStandards) ? f.applicableStandards.join(', ') : (f.applicableStandards || '')} onChange={(e) => set('applicableStandards', e.target.value)} placeholder="e.g. ISO 9001:2015" />
          </FG>
        </Col>
      </Row>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <SectionHeader>Audit Team Members</SectionHeader>
          <Button size="sm" variant="outline-primary" onClick={addMember}><Plus size={14} className="me-1" />Add</Button>
        </div>
        {(f.auditTeam || []).map((m, i) => (
          <div key={i} className="card mb-2">
            <div className="card-body p-2">
              <Row className="g-2 align-items-center">
                <Col>
                  <Form.Control size="sm" value={m.name || ''} onChange={(e) => setMember(i, e.target.value)} placeholder="Auditor name" />
                </Col>
                <Col xs="auto">
                  <Button size="sm" variant="outline-danger" onClick={() => removeMember(i)} disabled={(f.auditTeam || []).length <= 1}><Trash2 size={14} /></Button>
                </Col>
              </Row>
            </div>
          </div>
        ))}
      </div>

      <Row className="mb-3">
        {[['majorNC', 'Major NC'], ['minorNC', 'Minor NC'], ['ofi', 'OFI'], ['observations', 'Observations'], ['conformities', 'Conformities']].map(([key, label]) => (
          <Col key={key} xs={6} md={2}>
            <FG label={label}>
              <Form.Control size="sm" type="number" min={0} value={f[key] ?? 0} onChange={(e) => set(key, Number(e.target.value))} />
            </FG>
          </Col>
        ))}
      </Row>

      <FG label="Summary Narrative">
        <Form.Control size="sm" as="textarea" rows={3} value={f.summaryNarrative || ''} onChange={(e) => set('summaryNarrative', e.target.value)} placeholder="Overall summary of audit findings and observations..." />
      </FG>

      <FG label="Audit Conclusion" required>
        <FormFieldSelect value={f.conclusion || ''} onChange={(e) => set('conclusion', e.target.value)}>
          <option>Recommend Certification</option>
          <option>Not Recommend Certification</option>
          <option>Defer Decision</option>
          <option>Recommend with Conditions</option>
        </FormFieldSelect>
      </FG>

      <FG label="Recommendations">
        <Form.Control size="sm" as="textarea" rows={2} value={f.recommendations || ''} onChange={(e) => set('recommendations', e.target.value)} />
      </FG>
    </>
  );
};

// ── FAPP-06 Certificate Information Form ──────────────────────────────────
const CertificateInfoForm = ({ formData: f, set, setFormData, projectMeta, auditSchedule, standardSelectOptions = [], scopeLabel = '', auditorOptions = [] }) => {
  const auditorNameOptions = useMemo(() => {
    const seen = new Set();
    return auditorOptions
      .map((a) => a.name || a.fullName || '')
      .filter((name) => {
        const trimmed = name.trim();
        if (!trimmed || seen.has(trimmed)) return false;
        seen.add(trimmed);
        return true;
      })
      .map((name) => ({ value: name, label: name }));
  }, [auditorOptions]);
  const derivedLocations = useMemo(() => resolveAuditSites(projectMeta, auditSchedule, scopeLabel), [projectMeta, auditSchedule, scopeLabel]);
  const requiresSoa = f.requiresSoa || isIso27Family([
    f.applicableStandards,
    scopeLabel,
    ...standardSelectOptions.map((option) => `${option.label || ''} ${option.value || ''}`),
  ]);

  useEffect(() => {
    if (!setFormData || derivedLocations.length === 0) return;
    const current = Array.isArray(f.additionalLocations) ? f.additionalLocations : [];
    const hasLocations = current.some((loc) => String(loc?.siteFunction || loc?.scope || loc?.address || '').trim());
    if (!hasLocations) setFormData((prev) => ({ ...prev, additionalLocations: derivedLocations }));
  }, [derivedLocations, f.additionalLocations, setFormData]);
  const setLoc = (i, field, val) =>
    setFormData((p) => {
      const arr = [...(p.additionalLocations || [{}, {}, {}])];
      arr[i] = { ...arr[i], [field]: val };
      return { ...p, additionalLocations: arr };
    });
  const locs = f.additionalLocations || [{}, {}, {}];

  return (
    <>
      <FG label="Client / Company Name" required hint="Client's full registered company name" autoPopulated={!!f.clientName}>
        <Form.Control size="sm" className="form-control-enhanced" value={f.clientName || ''} onChange={(e) => set('clientName', e.target.value)} placeholder="PT. Example Indonesia" />
      </FG>
      <Row>
        <Col md={6}>
          <FG label="Contact Person" hint="Full name of client contact person">
            <Form.Control size="sm" className="form-control-enhanced" value={f.contact || ''} onChange={(e) => set('contact', e.target.value)} placeholder="Mr. Boby Ricardo" />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Position / Title" hint="Job title of the contact person">
            <Form.Control size="sm" className="form-control-enhanced" value={f.position || ''} onChange={(e) => set('position', e.target.value)} placeholder="ISMS Manager" />
          </FG>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FG label="Phone / Mobile" hint="Direct contact number">
            <Form.Control size="sm" className="form-control-enhanced" type="tel" value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="+62 812-3456-7890" />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Email Address" hint="Valid email address for contact">
            <Form.Control size="sm" className="form-control-enhanced" type="email" value={f.email || ''} onChange={(e) => set('email', e.target.value)} placeholder="bobbyricardo@gmail.com" />
          </FG>
        </Col>
      </Row>
      <FG label="Direction">
        <Form.Control size="sm" as="textarea" rows={2} value={f.direction || ''} onChange={(e) => set('direction', e.target.value)} />
      </FG>
      <FG label="Address">
        <Form.Control size="sm" as="textarea" rows={2} value={f.address || ''} onChange={(e) => set('address', e.target.value)} placeholder="Gd. Merdeka, Jl. Pahlawan No.08 Jakarta Selatan" />
      </FG>

      <div className="mb-3">
        <p className="small text-muted mb-3">
          <strong>Additional Locations/Supporting sites or functions to be on the certificate</strong><br/>
          Data is seeded from Audit Methodology multi-site when available. Please specify the scope of certification specific to each site.
        </p>
        <div className="table-responsive">
          <table className="table table-enhanced table-sm">
            <thead>
              <tr>
                <th>Site / Function</th>
                <th>Scope of Certification</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: Math.max(3, locs.length || 0) }).map((_, i) => (
                <tr key={i}>
                  <td>
                    <Form.Control size="sm" className="form-control-enhanced" value={(locs[i] || {}).siteFunction || ''} onChange={(e) => setLoc(i, 'siteFunction', e.target.value)} placeholder="e.g. Warehouse, Office" />
                    {(locs[i] || {}).address ? <div className="small text-muted mt-1">{(locs[i] || {}).address}</div> : null}
                  </td>
                  <td>
                    <Form.Control size="sm" className="form-control-enhanced" value={(locs[i] || {}).scope || ''} onChange={(e) => setLoc(i, 'scope', e.target.value)} placeholder="e.g. Storage, Administration" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {requiresSoa && (
        <FG label="Statement of Applicability (SOA)" hint="Required for ISO 27 family standards and included in export.">
          <Form.Control
            size="sm"
            className="form-control-enhanced"
            value={f.statementOfApplicability || f.soa || ''}
            onChange={(e) => {
              set('statementOfApplicability', e.target.value);
              set('soa', e.target.value);
            }}
            placeholder="e.g. SOA Document, Version 1.2 Released date 12 March 2026"
          />
        </FG>
      )}

      <SectionHeader>Exceptions/Exclusion (Please List Below)</SectionHeader>
      <FG label="Requirement">
        <Form.Control size="sm" as="textarea" rows={2} value={f.exceptionsRequirement || ''} onChange={(e) => set('exceptionsRequirement', e.target.value)} />
      </FG>
      <FG label="Location">
        <Form.Control size="sm" value={f.exceptionsLocation || ''} onChange={(e) => set('exceptionsLocation', e.target.value)} />
      </FG>
      <FG label="Activities">
        <Form.Control size="sm" as="textarea" rows={2} value={f.exceptionsActivities || ''} onChange={(e) => set('exceptionsActivities', e.target.value)} />
      </FG>
      <FG label="Additional Comment">
        <Form.Control size="sm" value={f.exceptionsAdditionalComment || ''} onChange={(e) => set('exceptionsAdditionalComment', e.target.value)} />
      </FG>

      <SectionHeader>Client and Lead Auditor Acknowledgement</SectionHeader>
      <p className="small text-muted mb-3">Stage 2 acknowledgement fields. FAPP-06 is not required during Initial Certification Stage 1.</p>
      <Row>
        <Col md={6}>
          <FG label="Lead Auditor Name" hint="Searchable — type to filter the audit team">
            <SingleSearchSelect
              value={f.acknowledgeAuditor || ''}
              options={auditorNameOptions}
              onChange={(value) => set('acknowledgeAuditor', value)}
              placeholder="Search auditor name..."
              noOptionsMessage={() => 'No auditors found for this scope'}
            />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Date(s) of Audit">
            <Form.Control size="sm" type="date" value={f.acknowledgeDateOfAudit || ''} onChange={(e) => set('acknowledgeDateOfAudit', e.target.value)} />
          </FG>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FG label="Lead Auditor Signature" hint="Type the signatory name or signature reference used in the controlled document.">
            <Form.Control size="sm" value={f.leadAuditorSignature || ''} onChange={(e) => set('leadAuditorSignature', e.target.value)} placeholder="Lead Auditor signature / reference" />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Client Representative Name">
            <Form.Control size="sm" value={f.clientRepresentativeName || ''} onChange={(e) => set('clientRepresentativeName', e.target.value)} placeholder="Authorized client representative" />
          </FG>
        </Col>
      </Row>
      <FG label="Client Representative Signature" hint="Captured for the signed CIF output.">
        <Form.Control size="sm" value={f.clientRepresentativeSignature || ''} onChange={(e) => set('clientRepresentativeSignature', e.target.value)} placeholder="Client signature / reference" />
      </FG>
    </>
  );
};

// ── FAPP-07 Finding Form ───────────────────────────────────────────────────
const FindingForm = ({ formData: f, set, auditorOptions = [], requirementOptions = [], requirementLookup = {}, requirementsLoading = false }) => {
  const categoryLabel = FAPP07_FINDING_CATEGORY_OPTIONS.find(([value]) => value === f.findingCategory)?.[1] || f.findingCategory || '';
  const normalizedFindingType = f.findingType || (
    String(f.findingCategory || '').includes('MAJOR') ? 'MAJOR'
      : String(f.findingCategory || '').includes('MINOR') ? 'MINOR'
        : String(f.findingCategory || '').includes('OPPORTUNITY') ? 'OFI'
          : 'MAJOR'
  );

  return (
  <>
    <div className="template-aligned-card mb-3">
      <div>
        <div className="template-aligned-card__eyebrow">FAPP-07 Official Template</div>
        <div className="template-aligned-card__title">Finding Form — Client Audit Data</div>
        <p className="template-aligned-card__copy mb-0">Use the official category and action-required pairing from the released spreadsheet so the saved JSON is ready for export and review.</p>
      </div>
      <Badge bg="light" text="dark" className="border">Rev.02</Badge>
    </div>

    <div className="form-section">
      <div className="form-section-title">Part 1: Client Audit Data</div>
      <Row>
        <Col md={6}>
          <FG label="Client Name" autoPopulated={!!f.clientName}>
            <Form.Control size="sm" className="form-control-enhanced" value={f.clientName || ''} onChange={(e) => set('clientName', e.target.value)} placeholder="Client name" />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Client ID#">
            <Form.Control size="sm" className="form-control-enhanced" value={f.clientId || ''} onChange={(e) => set('clientId', e.target.value)} placeholder="Client ID" />
          </FG>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FG label="Audit Criteria">
            <Form.Control size="sm" className="form-control-enhanced" value={f.auditCriteria || ''} onChange={(e) => set('auditCriteria', e.target.value)} placeholder="e.g. ISO 9001:2015" />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Date(s) of Audit">
            <Form.Control size="sm" className="form-control-enhanced" value={f.auditDates || ''} onChange={(e) => set('auditDates', e.target.value)} placeholder="e.g. 2026-07-09 - 2026-07-10" />
          </FG>
        </Col>
      </Row>
      <FG label="Audit Activity">
        <Form.Control size="sm" className="form-control-enhanced" value={f.auditActivity || ''} onChange={(e) => set('auditActivity', e.target.value)} placeholder="Stage 1 / Stage 2 / Surveillance / Recertification" />
      </FG>
    </div>

    <div className="form-section">
      <div className="form-section-title">Part 2: Finding Detail</div>
      <Row>
        <Col md={3}>
          <FG label="Finding ID / Number">
            <Form.Control size="sm" value={f.findingNumber || f.findingReference || ''} onChange={(e) => set('findingNumber', e.target.value)} />
          </FG>
        </Col>
        <Col md={3}>
          <FG label="Finding Category" required hint="Official category from the released FAPP-07 workbook">
            <FormFieldSelect
             
             
              value={f.findingCategory || ''}
              onChange={(e) => {
                const selected = FAPP07_FINDING_CATEGORY_OPTIONS.find(([value]) => value === e.target.value);
                set('findingCategory', e.target.value);
                if (selected) {
                  set('findingType', selected[1]);
                  set('actionRequired', selected[2]);
                }
              }}
            >
              <option value="">Select finding category</option>
              {FAPP07_FINDING_CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </FormFieldSelect>
          </FG>
        </Col>
        <Col md={3}>
          <FG label="Finding Type" hint="Legacy severity alias kept for compatibility">
            <FormFieldSelect value={normalizedFindingType} onChange={(e) => set('findingType', e.target.value)}>
              <option value="MAJOR">Major NC</option>
              <option value="MINOR">Minor NC</option>
              <option value="OFI">Opportunity for Improvement</option>
              <option value="SENSITIVE">Sensitive Finding</option>
              {categoryLabel && !['MAJOR', 'MINOR', 'OFI', 'SENSITIVE'].includes(categoryLabel) ? <option value={categoryLabel}>{categoryLabel}</option> : null}
            </FormFieldSelect>
          </FG>
        </Col>
        <Col md={3}>
          <FG label="Status" hint="Track the current lifecycle of this finding">
            <FormFieldSelect value={f.status || 'OPEN'} onChange={(e) => set('status', e.target.value)}>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="RESOLVED">Resolved</option>
              <option value="CLOSED">Closed</option>
            </FormFieldSelect>
          </FG>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FG label="Finding Date">
            <Form.Control size="sm" type="date" value={f.findingDate || ''} onChange={(e) => set('findingDate', e.target.value)} />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Action Required" required hint="Auto-filled from Finding Category; editable when the audit decision requires context">
            <Form.Control size="sm" className="form-control-enhanced" value={f.actionRequired || ''} onChange={(e) => set('actionRequired', e.target.value)} placeholder="Submit corrective action / CAP by: (date)" />
          </FG>
        </Col>
      </Row>
      <FG label="Finding Details" required hint="Describe the nonconformity or observation before recording its evidence">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={3} value={f.findingDetails || f.findingDescription || ''} onChange={(e) => set('findingDetails', e.target.value)} placeholder="Describe the finding..." />
      </FG>
      <FG label="Objective Evidence" required hint="Record the verifiable evidence supporting the finding">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={3} value={f.objectiveEvidence || f.evidenceFinding || ''} onChange={(e) => set('objectiveEvidence', e.target.value)} placeholder="Describe documents, records, interviews, or observations..." />
      </FG>
      <FG label="Auditor">
        <FormFieldSelect value={f.auditorName || ''} onChange={(e) => set('auditorName', e.target.value)}>
          <option value="">-- Select Auditor --</option>
          {auditorOptions.map((auditor) => {
            const name = auditor.name || auditor.fullName || auditor.username || '';
            return <option key={auditor.userId || auditor.id || name} value={name}>{name}</option>;
          })}
        </FormFieldSelect>
      </FG>
      <FG label="Clause Reference" required hint="Pick one or more clauses / annexes from the active audit standard">
        <MultiValueSelect
          value={f.clauseReference || (Array.isArray(f.clauseReferences) ? f.clauseReferences.map((item) => item.referenceKey || item.code) : [])}
          options={requirementOptions}
          onChange={(selectedValues, selectedOptions) => {
            set('clauseReference', selectedValues);
            set('clauseReferences', selectedOptions.map((item) => ({
              referenceKey: item.value,
              code: item.code || item.value,
              label: item.label,
              description: item.description || '',
              type: item.type || 'clause',
              standardId: item.standardId || '',
            })));
            if (!f.requirement) {
              const description = selectedOptions
                .map((item) => item?.description || requirementLookup[item?.value]?.description)
                .filter(Boolean)
                .join('\n\n');
              if (description) {
                set('requirement', description);
              }
            }
          }}
          isLoading={requirementsLoading}
          placeholder="Select Clausul / Annex"
          noOptionsMessage={() => 'No clauses or annexes available from backend'}
        />
      </FG>
      <FG label="Requirement" hint="Requirement description pulled from the selected clause / annex">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={2} value={f.requirement || ''} onChange={(e) => set('requirement', e.target.value)} />
      </FG>
    </div>

    <div className="form-section">
      <div className="form-section-title">Part 3: Corrective Action Plan</div>
      <FG label="Correction (Immediate Action)" hint="Replaces the legacy Auditee Response field in the V2 form">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={2} value={f.correction || f.auditeeResponse || ''} onChange={(e) => set('correction', e.target.value)} />
      </FG>
      <FG label="Root Cause Analysis" hint="Record the underlying cause identified by the auditee">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={2} value={f.rootCause || ''} onChange={(e) => set('rootCause', e.target.value)} />
      </FG>
      <FG label="Proposed Corrective Action" hint="Summarize the agreed corrective action plan">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={2} value={f.correctiveAction || ''} onChange={(e) => set('correctiveAction', e.target.value)} />
      </FG>
      <FG label="Plan for Verification of Effectiveness">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={2} value={f.planVerification || ''} onChange={(e) => set('planVerification', e.target.value)} />
      </FG>
      <Row>
        <Col md={6}>
          <FG label="Target Completion Date">
            <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.targetDate || ''} onChange={(e) => set('targetDate', e.target.value)} />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Responsible Person">
            <Form.Control size="sm" className="form-control-enhanced" value={f.responsiblePerson || ''} onChange={(e) => set('responsiblePerson', e.target.value)} placeholder="Name / Dept" />
          </FG>
        </Col>
      </Row>
    </div>

    <div className="form-section">
      <div className="form-section-title">Part 4–5: Acceptance and Verification</div>
      <FG label="Verification Acceptance / Comment">
        <Form.Control size="sm" as="textarea" rows={2} value={f.acceptanceComment || ''} onChange={(e) => set('acceptanceComment', e.target.value)} />
      </FG>
      <Row>
        <Col md={6}><FG label="Accepted By"><Form.Control size="sm" value={f.acceptedBy || ''} onChange={(e) => set('acceptedBy', e.target.value)} /></FG></Col>
        <Col md={6}><FG label="Accepted Date"><Form.Control size="sm" type="date" value={f.acceptedDate || ''} onChange={(e) => set('acceptedDate', e.target.value)} /></FG></Col>
      </Row>
      <FG label="Corrective Action Implementation">
        <Form.Control size="sm" as="textarea" rows={2} value={f.correctiveActionImplemented || f.implementation || ''} onChange={(e) => set('correctiveActionImplemented', e.target.value)} />
      </FG>
      <FG label="Effectiveness">
        <Form.Control size="sm" as="textarea" rows={2} value={f.effectiveness || ''} onChange={(e) => set('effectiveness', e.target.value)} />
      </FG>
      <FG label="Nonconformity Closed">
        <Form.Control size="sm" as="textarea" rows={2} value={f.nonconformityClosed || ''} onChange={(e) => set('nonconformityClosed', e.target.value)} />
      </FG>
      <Row>
        <Col md={6}><FG label="Verified By"><Form.Control size="sm" value={f.verifiedBy || ''} onChange={(e) => set('verifiedBy', e.target.value)} /></FG></Col>
        <Col md={6}><FG label="Verified Date"><Form.Control size="sm" type="date" value={f.verifiedDate || ''} onChange={(e) => set('verifiedDate', e.target.value)} /></FG></Col>
      </Row>
    </div>
  </>
  );
};

const ThreeYearProgramForm = ({ formData: f, set, setNested, setFormData, auditorOptions, getCycleMeta, setCycleMeta, getCoverage, toggleCoverage, getRowNotes, setRowNotes, fapp02Template }) => {
  // Per UAT MoM (2026-07-09, item #9): IA/SV3/SV4/SV5 removed; RC is the
  // only Recertification column. Legacy keys are normalized into rc on load.
  const programColumns = [
    ['st1', 'ST 1'], ['st2', 'ST 2'], ['sv1', 'SV 1'], ['sv2', 'SV 2'], ['rc', 'Recertification'],
  ];
  // PII Controller/Processor only apply to ISO/IEC 27701 (PIMS) audits. Reuses
  // FAPP-05's own standard detection (deriveAuditReportStandardKey) rather than
  // a second /27701/ regex, so both forms stay in lockstep with one definition.
  // applicableStandards is auto-populated from the selected scope's standard(s).
  const isIso27701 = deriveAuditReportStandardKey(f.applicableStandards) === 'PIMS';

  return (
    <>
      <div className="form-section">
        <div className="form-section-title">Program Header</div>
        <Row>
          <Col md={6}>
            <FG label="Program Reference">
              <Form.Control size="sm" value={f.programReference || ''} onChange={(e) => set('programReference', e.target.value)} placeholder="e.g. 3YP-2026-001" />
            </FG>
          </Col>
          <Col md={6}>
            <FG label="Client / Company" autoPopulated={!!f.clientCompany}>
              <Form.Control size="sm" value={f.clientCompany || ''} onChange={(e) => set('clientCompany', e.target.value)} placeholder="Client company name" />
            </FG>
          </Col>
        </Row>
        <FG label="Applicable Standards" hint="Standard(s) covered across the 3-year program">
          <Form.Control size="sm" value={f.applicableStandards || ''} onChange={(e) => set('applicableStandards', e.target.value)} placeholder="e.g. ISO 9001:2015" />
        </FG>
        <Row>
          <Col md={isIso27701 ? 4 : 6}>
            <FG label="Surveillance Interval">
              <FormFieldSelect value={f.surveillanceInterval || '12'} onChange={(e) => set('surveillanceInterval', e.target.value)}>
                <option value="6">6 months</option><option value="9">9 months</option><option value="12">12 months</option>
              </FormFieldSelect>
            </FG>
          </Col>
          {/* PII Controller/Processor only apply to ISO/IEC 27701 (PIMS) audits. */}
          {isIso27701 && (
            <>
              <Col md={4} className="pt-md-4"><Form.Check label="PII Controller" checked={!!f.piiController} onChange={(e) => set('piiController', e.target.checked)} /></Col>
              <Col md={4} className="pt-md-4"><Form.Check label="PII Processor" checked={!!f.piiProcessor} onChange={(e) => set('piiProcessor', e.target.checked)} /></Col>
            </>
          )}
        </Row>
      </div>

      <SectionHeader>Mandatory Requirements / Coverage Matrix</SectionHeader>
      <div className="table-responsive mb-3">
        <table className="table table-sm table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th>Requirement</th>
              {programColumns.map(([key, label]) => <th key={key} className="text-center">{label}</th>)}
              <th style={{ width: 200 }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {(f.programRows || []).map((row, index) => (
              <tr key={index}>
                <td><Form.Control size="sm" value={row.clauseAnnex || ''} onChange={(e) => setNested('programRows', index, 'clauseAnnex', e.target.value)} /></td>
                {programColumns.map(([cycle]) => (
                  <td key={cycle} className="text-center">
                    <Form.Check type="checkbox" checked={getCoverage(index, cycle)} onChange={() => toggleCoverage(index, cycle)} />
                  </td>
                ))}
                <td><Form.Control size="sm" value={getRowNotes(index)} onChange={(e) => setRowNotes(index, e.target.value)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionHeader>Cycle Details</SectionHeader>
      <div className="table-responsive mb-3">
        <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
                <tr><th>Cycle</th><th>Planned Year</th><th>Executed Date</th><th>Mandays</th><th>Shifts</th><th>Location</th></tr>
            </thead>
            <tbody>
                {fapp02Template?.cycleColumns?.map((cycle) => (
                    <tr key={cycle.key}>
                        <td>{cycle.label}</td>
                        <td><Form.Control size="sm" value={getCycleMeta(cycle.key, 'plannedYear')} onChange={(e) => setCycleMeta(cycle.key, 'plannedYear', e.target.value)} /></td>
                        <td><Form.Control size="sm" type="date" value={getCycleMeta(cycle.key, 'executedDate')} onChange={(e) => setCycleMeta(cycle.key, 'executedDate', e.target.value)} /></td>
                        <td><Form.Control size="sm" type="number" value={getCycleMeta(cycle.key, 'mandays')} onChange={(e) => setCycleMeta(cycle.key, 'mandays', e.target.value)} /></td>
                        <td><Form.Control size="sm" value={getCycleMeta(cycle.key, 'shifts')} onChange={(e) => setCycleMeta(cycle.key, 'shifts', e.target.value)} /></td>
                        <td><Form.Control size="sm" value={getCycleMeta(cycle.key, 'location')} onChange={(e) => setCycleMeta(cycle.key, 'location', e.target.value)} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
      </div>
      
      <Row>
        <Col md={6}>
          <FG label="Prepared By">
            <Form.Control size="sm" value={f.preparedBy || ''} onChange={(e) => set('preparedBy', e.target.value)} />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Created Date">
            <Form.Control size="sm" type="date" value={f.createdDate || ''} onChange={(e) => set('createdDate', e.target.value)} />
          </FG>
        </Col>
      </Row>
      <FG label="Notes">
        <Form.Control size="sm" as="textarea" rows={2} value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} />
      </FG>
    </>
  );
};

// ── FAPP-03 Attendance List ────────────────────────────────────────────────
const AttendanceListForm = ({ formData: f, set, setFormData, auditorOptions = [] }) => {
  // Pre-populate scheduled auditors as attendees on first open
  React.useEffect(() => {
    const scheduled = auditorOptions.filter(a => a.isScheduled);
    if (scheduled.length > 0 && (f.attendees || []).length === 1 && !(f.attendees[0]?.name)) {
      setFormData(p => ({
        ...p,
        attendees: scheduled.map(a => ({
          name: a.name || a.fullName || '',
          position: a.role || 'Auditor',
          email: a.email || '',
          phoneNumber: a.phoneNumber || a.phone || '',
          openingSignature: '',
          closingSignature: '',
        })),
      }));
    }
  }, [auditorOptions.length]); // eslint-disable-line
  const addRow = () => setFormData((p) => {
    const attendees = p.attendees || [];
    if (attendees.length >= 15) return p;
    return {
      ...p,
      attendees: [...attendees, {
        name: '', position: '', email: '', phoneNumber: '',
        openingSignature: '', closingSignature: '',
      }],
    };
  });
  const removeRow = (i) => setFormData((p) => ({ ...p, attendees: p.attendees.filter((_, idx) => idx !== i) }));
  const setRow = (i, field, val) => setFormData((p) => { const arr = [...(p.attendees || [])]; arr[i] = { ...arr[i], [field]: val }; return { ...p, attendees: arr }; });

  return (
    <>
      <Row>
        <Col md={6}>
          <FG label="Date" required>
            <Form.Control size="sm" type="date" value={f.date || ''} onChange={(e) => set('date', e.target.value)} />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Title / Meeting Activity" required hint="Official template field. Examples: Opening Meeting, Closing Meeting, transition training, or audit briefing.">
            <Form.Control
              size="sm"
              className="form-control-enhanced"
              value={f.title || f.meetingType || ''}
              onChange={(e) => {
                set('title', e.target.value);
                set('meetingType', e.target.value);
              }}
              placeholder="e.g. Opening Meeting"
              list="fapp03-title-suggestions"
            />
            <datalist id="fapp03-title-suggestions">
              <option value="Opening Meeting" />
              <option value="Closing Meeting" />
              <option value="Daily Meeting" />
              <option value="Audit Briefing" />
            </datalist>
          </FG>
        </Col>
      </Row>
      <FG label="Audit Type">
        <FormFieldSelect value={f.auditType || ''} onChange={(e) => set('auditType', e.target.value)}>
          {auditTypeOptions.map((o) => <option key={o}>{o}</option>)}
        </FormFieldSelect>
      </FG>
      <FG label="Location">
        <Form.Control size="sm" value={f.location || ''} onChange={(e) => set('location', e.target.value)} />
      </FG>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <SectionHeader>Attendees</SectionHeader>
          <div className="d-flex align-items-center gap-2">
            <span className="badge text-bg-light">{(f.attendees || []).length}/15</span>
            <Button size="sm" variant="outline-primary" onClick={addRow} disabled={(f.attendees || []).length >= 15}>
              <Plus size={14} className="me-1" />Add Row
            </Button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-bordered table-hover align-middle small">
            <thead className="table-dark">
              <tr>
                <th>Name</th>
                <th>Position</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th>Opening Signature</th>
                <th>Closing Signature</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(f.attendees || []).map((row, i) => (
                <tr key={i}>
                  {['name', 'position', 'email', 'phoneNumber', 'openingSignature', 'closingSignature'].map((field) => (
                    <td key={field}>
                      <Form.Control
                        size="sm"
                        type={field === 'email' ? 'email' : 'text'}
                        value={row[field] || ''}
                        onChange={(e) => setRow(i, field, e.target.value)}
                        className="border-0 p-1"
                        placeholder={field.includes('Signature') ? 'Name / sign' : ''}
                      />
                    </td>
                  ))}
                  <td className="text-center">
                    <Button size="sm" variant="link" className="text-danger p-0" onClick={() => removeRow(i)} disabled={(f.attendees || []).length <= 1}><Trash2 size={12} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ── FAPP-04 Audit Notes ────────────────────────────────────────────────────
const AuditNotesForm = ({ formData: f, set, setFormData, auditorOptions = [], auditSchedule, requirementOptions = [], requirementsLoading = false }) => {
  // Auto-fill audit date from schedule and lead auditor name on first open
  React.useEffect(() => {
    const lead = auditorOptions.find(a => a.isLead);
    setFormData(p => ({
      ...p,
      auditDate: p.auditDate || (auditSchedule?.startDate ? new Date(auditSchedule.startDate).toISOString().split('T')[0] : ''),
      auditorName: p.auditorName || (lead ? (lead.name || lead.fullName || '') : ''),
    }));
  }, [auditSchedule?.startDate, auditorOptions.length]); // eslint-disable-line

  React.useEffect(() => {
    if (String(f.notesHtml || '').trim()) return;
    const legacyRows = Array.isArray(f.notes) ? f.notes.filter((note) => (
      note?.observation || note?.evidence || splitStoredMultiValue(note?.clause).length > 0
    )) : [];
    if (legacyRows.length === 0) return;
    const legacyHtml = legacyRows.map((note) => {
      const clause = splitStoredMultiValue(note?.clause).join(', ');
      return `<p><strong>${clause || 'Audit note'}${note?.conformity ? ` · ${note.conformity}` : ''}</strong></p><p>${note?.observation || ''}</p>${note?.evidence ? `<p><em>Evidence:</em> ${note.evidence}</p>` : ''}`;
    }).join('<hr>');
    setFormData((previous) => ({ ...previous, notesHtml: legacyHtml, notesText: stripRichText(legacyHtml) }));
  }, [f.notes, f.notesHtml, setFormData]);

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'link'],
      ['clean'],
    ],
  }), []);

  return (
    <>
      <Row>
        <Col md={4}>
          <FG label="Audit Date">
            <Form.Control size="sm" type="date" value={f.auditDate || ''} onChange={(e) => set('auditDate', e.target.value)} />
          </FG>
        </Col>
        <Col md={4}>
          <FG label="Auditor Name">
            {auditorOptions.length > 0 ? (
              <FormFieldSelect value={f.auditorName || ''} onChange={(e) => set('auditorName', e.target.value)}>
                <option value="">-- Select Auditor --</option>
                {auditorOptions.map((a) => <option key={a.userId || a.id} value={a.name || a.fullName}>{a.name || a.fullName}</option>)}
              </FormFieldSelect>
            ) : (
              <Form.Control size="sm" value={f.auditorName || ''} onChange={(e) => set('auditorName', e.target.value)} />
            )}
          </FG>
        </Col>
        <Col md={4}>
          <FG label="Process / Area Audited">
            <Form.Control size="sm" value={f.processArea || ''} onChange={(e) => set('processArea', e.target.value)} />
          </FG>
        </Col>
      </Row>

      <SectionHeader>Audit Notes</SectionHeader>
      <p className="small text-muted mb-2">Use headings, lists, emphasis, and links to capture the audit trail in the same flow as the audit conversation.</p>
      <div className="audit-notes-editor">
        <ReactQuill
          theme="snow"
          value={f.notesHtml || ''}
          modules={quillModules}
          onChange={(html) => {
            set('notesHtml', html);
            set('notesText', stripRichText(html));
          }}
          placeholder="Record audit notes, observations, interviews, and evidence references…"
        />
      </div>
    </>
  );
};

// ── FAPP-05 Audit Report ───────────────────────────────────────────────────
const AuditReportForm = ({ formData: f, set, setFormData, auditorOptions, selectedScope, setSelectedScope, availableStandards, loadingScope, projectId, standardSelectOptions = [] }) => {
  // The audit type endpoint may return the generic "Initial Certification" label.
  // For FAPP-05, the selected scoped key is authoritative because it distinguishes
  // Initial Certification Stage 1 from Stage 2 even when both share that label.
  const scopedStage = String(selectedScope || '').endsWith(':s1')
    ? 'stage1'
    : String(selectedScope || '').endsWith(':s2') ? 'stage2' : null;
  const stage = scopedStage || deriveAuditReportStage(f.auditType);
  const standardKey = deriveAuditReportStandardKey(f.applicableStandards?.length ? f.applicableStandards : availableStandards);
  const isStage1 = stage === 'stage1';
  const isRecert = stage === 'recertification';
  const recommendationOptions = AUDIT_REPORT_RECOMMENDATION_OPTIONS[stage] || AUDIT_REPORT_RECOMMENDATION_OPTIONS.stage2;
  const recommendationTitle = AUDIT_REPORT_RECOMMENDATION_TITLE[stage];
  const findingColumns = isStage1 ? AUDIT_REPORT_FINDINGS_COLUMNS.stage1 : AUDIT_REPORT_FINDINGS_COLUMNS.default;
  const findingRows = isStage1 ? [['Current Activity', 'findingsSummary']] : [['Previous Activity', 'previousActivity'], ['Current Activity', 'findingsSummary']];
  const summarySections = getAuditReportSummarySections(standardKey);

  const setTeamField = (roleKey, field, value) =>
    setFormData((p) => ({ ...p, auditTeam: { ...(p.auditTeam || {}), [roleKey]: { ...(p.auditTeam?.[roleKey] || {}), [field]: value } } }));
  const toggleActivity = (key) =>
    setFormData((p) => ({ ...p, auditActivity: { ...(p.auditActivity || {}), [key]: !p.auditActivity?.[key] } }));
  const setActivityText = (key, value) =>
    setFormData((p) => ({ ...p, auditActivity: { ...(p.auditActivity || {}), [key]: value } }));

  // Auto-fetch findings statistics from backend
  React.useEffect(() => {
    if (!projectId) return;
    api.get(`/audit/findings/projects/${projectId}/statistics`)
      .then(res => {
        const s = res?.data?.data || res?.data || {};
        if (s.majorIssued !== undefined || s.minorIssued !== undefined) {
          setFormData(p => ({
            ...p,
            findingsSummary: {
              majorIssued: s.majorIssued ?? p.findingsSummary?.majorIssued ?? 0,
              majorClosed: s.majorClosed ?? p.findingsSummary?.majorClosed ?? 0,
              minorIssued: s.minorIssued ?? p.findingsSummary?.minorIssued ?? 0,
              minorClosed: s.minorClosed ?? p.findingsSummary?.minorClosed ?? 0,
              openNCs: s.openNCs ?? p.findingsSummary?.openNCs ?? 0,
              aocMajorIssued: s.aocMajorIssued ?? p.findingsSummary?.aocMajorIssued ?? 0,
              aocMinorIssued: s.aocMinorIssued ?? p.findingsSummary?.aocMinorIssued ?? 0,
            },
          }));
        }
      })
      .catch(() => {}); // non-critical, silently ignore
  }, [projectId]); // eslint-disable-line

  // Build PART list dynamically — numbering & presence follow the stage rules
  // transcribed from the released FAPP-05 templates (Stage 1 has 6 parts; Stage
  // 2/Surveillance have 7; Recertification adds an 8th "Other / Additional
  // Lead Auditor Recommendation" part before Acknowledgment).
  const parts = [
    {
      key: 'general',
      title: 'General Audit Data',
      body: (
        <>
          <Row>
            <Col md={6}>
              <FG label="Report Number" required hint="Unique identifier for this audit report">
                <Form.Control size="sm" className="form-control-enhanced" value={f.reportNumber || ''} onChange={(e) => set('reportNumber', e.target.value)} placeholder="e.g. RPT-2026-001" />
              </FG>
            </Col>
            <Col md={6}>
              <FG label="Audit Type" required hint="Classification of this audit engagement">
                <FormFieldSelect value={f.auditType || ''} onChange={(e) => set('auditType', e.target.value)}>
                  <option value="">-- Select Audit Type --</option>
                  {auditTypeOptions.map((o) => <option key={o}>{o}</option>)}
                </FormFieldSelect>
              </FG>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FG label="Audit Date From" required hint="Start date of the audit engagement">
                <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.auditDateFrom || ''} onChange={(e) => set('auditDateFrom', e.target.value)} />
              </FG>
            </Col>
            <Col md={6}>
              <FG label="Audit Date To" required hint="End date of the audit engagement">
                <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.auditDateTo || ''} onChange={(e) => set('auditDateTo', e.target.value)} />
              </FG>
            </Col>
          </Row>
          <FG label="Applicable Standards" autoPopulated={!!f.applicableStandards} hint="Standards against which the audit will be conducted">
            {standardSelectOptions.length > 0 ? (
              <MultiValueSelect
                value={f.applicableStandards}
                options={standardSelectOptions}
                onChange={(value) => set('applicableStandards', value)}
                placeholder="Select applicable standards"
                noOptionsMessage={() => 'No standards found for this audit scope'}
              />
            ) : (
              <Form.Control size="sm" className="form-control-enhanced" value={Array.isArray(f.applicableStandards) ? f.applicableStandards.join(', ') : (f.applicableStandards || '')} onChange={(e) => set('applicableStandards', e.target.value)} />
            )}
          </FG>
          <FG label="Scope of Certification" required autoPopulated={!!f.scopeOfCertification} hint="Products, services, or processes covered by the certification">
            <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={2} value={f.scopeOfCertification || ''} onChange={(e) => set('scopeOfCertification', e.target.value)} />
          </FG>
          <FG label="Location" autoPopulated={!!f.location} hint="Primary audit location / Head office">
            <Form.Control size="sm" className="form-control-enhanced" value={f.location || ''} onChange={(e) => set('location', e.target.value)} />
          </FG>

          <SectionHeader>Audit Activity</SectionHeader>
          {AUDIT_REPORT_ACTIVITY_OPTIONS.map(([key, label]) => (
            <div key={key} className="d-flex align-items-center gap-2 mb-2">
              <Form.Check type="checkbox" id={`activity-${key}`} checked={!!f.auditActivity?.[key]} onChange={() => toggleActivity(key)} label={label} />
              {(key === 'surveillance' || key === 'changeOfScope' || key === 'specialSurveillance' || key === 'other') && (
                <Form.Control
                  size="sm"
                  className="form-control-enhanced"
                  style={{ maxWidth: 260 }}
                  value={f.auditActivity?.[`${key}Text`] || ''}
                  onChange={(e) => setActivityText(`${key}Text`, e.target.value)}
                  placeholder={key === 'surveillance' ? 'No.' : 'Specify'}
                  disabled={!f.auditActivity?.[key]}
                />
              )}
            </div>
          ))}

          <SectionHeader>Audit Objectives</SectionHeader>
          <p className="small text-muted">{AUDIT_REPORT_OBJECTIVES_INTRO}</p>
          <ul className="small text-muted">
            {AUDIT_REPORT_OBJECTIVES_BULLETS.map((text, i) => <li key={i}>{text}</li>)}
          </ul>

          <SectionHeader>Audit Methodology</SectionHeader>
          <ul className="small text-muted">
            {AUDIT_REPORT_METHODOLOGY_BULLETS.map((text) => <li key={text}>{text}</li>)}
          </ul>

          <SectionHeader>Audit Team</SectionHeader>
          <FG label="Lead Auditor" required autoPopulated={!!f.leadAuditor} hint="Senior auditor responsible for this engagement">
            <FormFieldSelect value={f.leadAuditor || ''} onChange={(e) => set('leadAuditor', e.target.value)} disabled={loadingScope}>
              <option value="">-- Select --</option>
              {auditorOptions.map((a) => <option key={a.userId || a.id} value={a.userId || a.id}>{a.name || a.fullName}</option>)}
            </FormFieldSelect>
          </FG>
          {AUDIT_REPORT_TEAM_ROLES.filter(([roleKey]) => roleKey !== 'leadAuditor').map(([roleKey, roleLabel]) => (
            <Row key={roleKey} className="align-items-end mb-2">
              <Col md={6}>
                <FG label={roleLabel}>
                  <Form.Control size="sm" value={f.auditTeam?.[roleKey]?.name || ''} onChange={(e) => setTeamField(roleKey, 'name', e.target.value)} placeholder="Name" />
                </FG>
              </Col>
              <Col md={6}>
                <FG label="Audit-Day(s)">
                  <Form.Control size="sm" value={f.auditTeam?.[roleKey]?.days || ''} onChange={(e) => setTeamField(roleKey, 'days', e.target.value)} />
                </FG>
              </Col>
            </Row>
          ))}
          <Row className="align-items-end mb-2">
            <Col md={6}>
              <FG label="Interpreter (if needed)">
                <Form.Control size="sm" value={f.auditTeam?.interpreter?.name || ''} onChange={(e) => setTeamField('interpreter', 'name', e.target.value)} placeholder="Name" />
              </FG>
            </Col>
            <Col md={6}>
              <FG label="Affiliation">
                <Form.Control size="sm" value={f.auditTeam?.interpreter?.affiliation || ''} onChange={(e) => setTeamField('interpreter', 'affiliation', e.target.value)} />
              </FG>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: 'result',
      title: 'Result of Audit (Lead Auditor Recommendation)',
      body: (
        <Form.Group className="mb-3">
          {recommendationTitle && <p className="fw-semibold mb-3">{recommendationTitle}</p>}
          {recommendationOptions.map(([value, label]) => (
            <Form.Check key={value} className="mb-2" type="radio" name="leadAuditRec" id={value} label={label} value={value} checked={f.leadAuditorRecommendation === value} onChange={(e) => set('leadAuditorRecommendation', e.target.value)} />
          ))}
        </Form.Group>
      ),
    },
    {
      key: 'executive',
      title: 'Executive Summary',
      body: (
        <FG label="Executive Summary" required hint="Brief overview of audit findings and conclusions (100-300 words)">
          <Form.Control
            size="sm"
            className="form-control-enhanced textarea"
            as="textarea"
            rows={5}
            value={f.executiveSummary || ''}
            onChange={(e) => set('executiveSummary', e.target.value)}
            placeholder="We would like to thank all the audit participants..."
          />
        </FG>
      ),
    },
  ];

  // Stage 1 has no prior-cycle Strengths/Weaknesses review.
  if (!isStage1) {
    parts.push({
      key: 'strengths',
      title: 'Strengths and Weaknesses',
      body: (
        <Row>
          <Col md={6}>
            <FG label="Strengths" hint="Notable practices and capabilities observed">
              <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={4} value={f.strengths || ''} onChange={(e) => set('strengths', e.target.value)} />
            </FG>
          </Col>
          <Col md={6}>
            <FG label="Weaknesses" hint="Areas for improvement and development">
              <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={4} value={f.weakness || ''} onChange={(e) => set('weakness', e.target.value)} />
            </FG>
          </Col>
        </Row>
      ),
    });
  }

  parts.push({
    key: 'findings',
    title: 'Findings summary',
    body: (
      <>
        <div className="table-responsive mb-3">
          <table className="table table-sm table-bordered">
            <thead className="table-light">
              <tr>
                <th>CATEGORY</th>
                {findingColumns.map(([, label]) => <th key={label}>{label.toUpperCase()}</th>)}
              </tr>
            </thead>
            <tbody>
              {findingRows.map(([label, group]) => (
                <tr key={group}>
                  <td>{label}</td>
                  {findingColumns.map(([field]) => (
                    <td key={field}>
                      <Form.Control size="sm" type="number" min={0} value={f[group]?.[field] ?? 0} onChange={(e) => setFormData((p) => ({ ...p, [group]: { ...p[group], [field]: Number(e.target.value) } }))} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <FG label="Status of Previous Findings">
          <Form.Control size="sm" as="textarea" rows={3} value={f.statusOfPreviousFindings || ''} onChange={(e) => set('statusOfPreviousFindings', e.target.value)} />
        </FG>
        <FG label="Audit Result">
          <Form.Control size="sm" as="textarea" rows={3} value={f.auditResult || ''} onChange={(e) => set('auditResult', e.target.value)} />
        </FG>
        {!isStage1 && (
          <>
            <SectionHeader>NOTES to Non-Conformity</SectionHeader>
            <FG label="Expected timelines to address the non-conformity (a)" hint="Controlled document wording; read-only.">
              <Form.Control size="sm" as="textarea" rows={5} value={AUDIT_REPORT_NC_NOTES_A} readOnly className="bg-light" />
            </FG>
            <FG label="Expected response content (b)" hint="Controlled document wording; read-only.">
              <Form.Control size="sm" as="textarea" rows={10} value={AUDIT_REPORT_NC_NOTES_B} readOnly className="bg-light" />
            </FG>
          </>
        )}
      </>
    ),
  });

  parts.push({
    key: 'summary',
    title: 'Audit Summary',
    body: (
      <>
        <p className="text-muted small">The state of the management system is summarized below:</p>
        {summarySections.map(([field, label]) => (
          <FG key={field} label={label}>
            <Form.Control size="sm" as="textarea" rows={field === 'context' ? 5 : 3} value={f.auditSummarySections?.[field] || ''} onChange={(e) => setFormData((p) => ({ ...p, auditSummarySections: { ...(p.auditSummarySections || {}), [field]: e.target.value } }))} />
            {field === 'significantIssues' && isRecert && (
              <div className="mt-2 ps-2 border-start">
                <p className="small text-muted mb-2">Tick as applicable — re-calculation of man-days allocation should be conducted (CID form should be re-submitted for approval).</p>
                <Form.Check type="radio" name="sigIssues" id="sig-nil" className="mb-1" label="Nil / Not Applicable" checked={f.significantIssuesRecommendation === 'nil' || !f.significantIssuesRecommendation} onChange={() => set('significantIssuesRecommendation', 'nil')} />
                <Form.Check type="radio" name="sigIssues" id="sig-increase" className="mb-1" label="Increase of Audit Time recommended" checked={f.significantIssuesRecommendation === 'increase'} onChange={() => set('significantIssuesRecommendation', 'increase')} />
                <Form.Check type="radio" name="sigIssues" id="sig-reduce" className="mb-1" label="Reduction of Audit Time recommended" checked={f.significantIssuesRecommendation === 'reduce'} onChange={() => set('significantIssuesRecommendation', 'reduce')} />
              </div>
            )}
          </FG>
        ))}
      </>
    ),
  });

  // Recertification-only PART: "Other / Additional Lead Auditor Recommendation
  // (applicable only for Last Surveillance before Re-certification Audit)".
  if (isRecert) {
    parts.push({
      key: 'otherRecommendation',
      title: 'Other / Additional Lead Auditor Recommendation (applicable only for Last Surveillance before Re-certification Audit)',
      body: (
        <Form.Group>
          <Form.Check
            type="radio" name="otherRec" id="otherRec-none" className="mb-2"
            label="No other / additional recommendation"
            checked={f.otherRecommendation === 'none'}
            onChange={() => set('otherRecommendation', 'none')}
          />
          <Form.Check
            type="radio" name="otherRec" id="otherRec-stage1" className="mb-2"
            label="Stage-1 Audit / Activity shall be performed before Re-certification"
            checked={f.otherRecommendation === 'stage1Required'}
            onChange={() => set('otherRecommendation', 'stage1Required')}
          />
          {f.otherRecommendation === 'stage1Required' && (
            <Form.Control size="sm" className="mb-2" value={f.otherRecommendationReason || ''} onChange={(e) => set('otherRecommendationReason', e.target.value)} placeholder="Performance issues not resolved (e.g. repeated NC, customer complaints)" />
          )}
        </Form.Group>
      ),
    });
  }

  parts.push({
    key: 'acknowledgment',
    title: 'Acknowledgment',
    body: (
      <>
        <p className="small text-muted">The client acknowledges the content in the report and the attached nonconformities.</p>
        <Row>
          <Col md={6}>
            <FG label="Client's Representative Name">
              <Form.Control size="sm" value={f.clientRepName || ''} onChange={(e) => set('clientRepName', e.target.value)} placeholder="Name" />
            </FG>
          </Col>
          <Col md={6}>
            <FG label="Position / Title">
              <Form.Control size="sm" value={f.clientRepPosition || ''} onChange={(e) => set('clientRepPosition', e.target.value)} placeholder="Management Representative" />
            </FG>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FG label="Email">
              <Form.Control size="sm" type="email" value={f.clientEmail || ''} onChange={(e) => set('clientEmail', e.target.value)} placeholder="email@example.com" />
            </FG>
          </Col>
          <Col md={6}>
            <FG label="Phone / Mobile">
              <Form.Control size="sm" type="tel" value={f.clientPhone || ''} onChange={(e) => set('clientPhone', e.target.value)} placeholder="+62 812-3456-7890" />
            </FG>
          </Col>
        </Row>
        <FG label="Address & Contact Detail">
          <Form.Control size="sm" as="textarea" rows={3} value={f.clientAddressContact || ''} onChange={(e) => set('clientAddressContact', e.target.value)} placeholder="Full address and contact details" />
        </FG>
        <FG label="Report Acknowledged By (if different)">
          <Form.Control size="sm" value={f.reportAcknowledgedByDifferent || ''} onChange={(e) => set('reportAcknowledgedByDifferent', e.target.value)} placeholder="Leave blank when same as the representative above" />
        </FG>
      </>
    ),
  });

  return (
    <Accordion defaultActiveKey="0" className="accordion-enhanced mb-3">
      {parts.map((part, index) => (
        <Accordion.Item eventKey={String(index)} key={part.key}>
          <Accordion.Header>Part {index + 1} - {part.title}</Accordion.Header>
          <Accordion.Body>{part.body}</Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  );
};

// ── FAPP-08 Change Notice Form ─────────────────────────────────────────────
const ChangeNoticeForm = ({ formData: f, set }) => {
  const selectedScopeChanges = Array.isArray(f.scopeChanges)
    ? f.scopeChanges
    : Array.isArray(f.changesAffecting) ? f.changesAffecting : [];
  const selectedAdministrativeChanges = Array.isArray(f.administrativeChanges) ? f.administrativeChanges : [];
  const toggleArrayValue = (field, selected, option, checked) => {
    const next = checked ? Array.from(new Set([...selected, option])) : selected.filter((item) => item !== option);
    set(field, next);
  };

  return (
  <>
    <div className="template-aligned-card mb-3">
      <div>
        <div className="template-aligned-card__eyebrow">FAPP-08 Official Template</div>
        <div className="template-aligned-card__title">Change Notice Form (CNF)</div>
        <p className="template-aligned-card__copy mb-0">Structured around the official change notice sections: client data, scope/certification changes, administrative changes, and CBQA office-use decision.</p>
      </div>
      <Badge bg="light" text="dark" className="border">Rev.01</Badge>
    </div>

    <div className="form-section">
      <div className="form-section-title">Client and Certification Data</div>
      <Row>
        <Col md={6}>
          <FG label="Client Name" autoPopulated={!!f.clientCompany}>
            <Form.Control size="sm" className="form-control-enhanced" value={f.clientCompany || f.clientName || ''} onChange={(e) => { set('clientCompany', e.target.value); set('clientName', e.target.value); }} placeholder="Client company name" />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Client ID #">
            <Form.Control size="sm" className="form-control-enhanced" value={f.clientId || ''} onChange={(e) => set('clientId', e.target.value)} placeholder="Client ID" />
          </FG>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FG label="Address">
            <Form.Control size="sm" className="form-control-enhanced" as="textarea" rows={2} value={f.address || ''} onChange={(e) => set('address', e.target.value)} placeholder="Client address" />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Audit Criteria">
            <Form.Control size="sm" className="form-control-enhanced" value={f.auditCriteria || ''} onChange={(e) => set('auditCriteria', e.target.value)} placeholder="e.g. ISO 9001:2015" />
          </FG>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FG label="Notice Number">
            <Form.Control size="sm" className="form-control-enhanced" value={f.noticeNumber || ''} onChange={(e) => set('noticeNumber', e.target.value)} placeholder="CNF-2026-001" />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Date of effectivity of the change" hint="If different from the approval date">
            <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.effectiveDate || ''} onChange={(e) => set('effectiveDate', e.target.value)} />
          </FG>
        </Col>
      </Row>
    </div>

    <div className="form-section">
      <div className="form-section-title">Changes affecting the scope of certification(s)</div>
      <div className="change-option-grid mb-3">
        {FAPP08_SCOPE_CHANGE_OPTIONS.map((option) => (
          <label key={option} className={`change-option-chip ${selectedScopeChanges.includes(option) ? 'is-selected' : ''}`}>
            <Form.Check
              type="checkbox"
              className="m-0"
              label={option}
              checked={selectedScopeChanges.includes(option)}
              onChange={(e) => {
                toggleArrayValue('scopeChanges', selectedScopeChanges, option, e.target.checked);
                toggleArrayValue('changesAffecting', selectedScopeChanges, option, e.target.checked);
              }}
            />
          </label>
        ))}
      </div>
      <FG label="Details of change / New office-site address">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={3} value={f.changeDescription || f.detailsOfChange || ''} onChange={(e) => { set('changeDescription', e.target.value); set('detailsOfChange', e.target.value); }} />
      </FG>
      <Row>
        <Col md={6}><FG label="Previous Scope"><Form.Control size="sm" className="form-control-enhanced" as="textarea" rows={3} value={f.previousScope || f.previouseScope || ''} onChange={(e) => set('previousScope', e.target.value)} /></FG></Col>
        <Col md={6}><FG label="New Scope"><Form.Control size="sm" className="form-control-enhanced" as="textarea" rows={3} value={f.newScope || ''} onChange={(e) => set('newScope', e.target.value)} /></FG></Col>
      </Row>
    </div>

    <div className="form-section">
      <div className="form-section-title">Administrative changes</div>
      <div className="change-option-grid mb-3">
        {FAPP08_ADMINISTRATIVE_CHANGE_OPTIONS.map((option) => (
          <label key={option} className={`change-option-chip ${selectedAdministrativeChanges.includes(option) ? 'is-selected' : ''}`}>
            <Form.Check
              type="checkbox"
              className="m-0"
              label={option}
              checked={selectedAdministrativeChanges.includes(option)}
              onChange={(e) => toggleArrayValue('administrativeChanges', selectedAdministrativeChanges, option, e.target.checked)}
            />
          </label>
        ))}
      </div>
      <Row>
        <Col md={6}>
          <FG label="Client Contact Name">
            <Form.Control size="sm" className="form-control-enhanced" value={f.clientContactName || f.requestedBy || ''} onChange={(e) => { set('clientContactName', e.target.value); set('requestedBy', e.target.value); }} />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Title">
            <Form.Control size="sm" className="form-control-enhanced" value={f.clientContactTitle || ''} onChange={(e) => set('clientContactTitle', e.target.value)} />
          </FG>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FG label="Phone #">
            <Form.Control size="sm" className="form-control-enhanced" value={f.clientContactPhone || ''} onChange={(e) => set('clientContactPhone', e.target.value)} />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Fax #">
            <Form.Control size="sm" className="form-control-enhanced" value={f.clientContactFax || ''} onChange={(e) => set('clientContactFax', e.target.value)} />
          </FG>
        </Col>
      </Row>
      <FG label="Email Address">
        <Form.Control size="sm" className="form-control-enhanced" type="email" value={f.clientContactEmail || ''} onChange={(e) => set('clientContactEmail', e.target.value)} />
      </FG>
    </div>

    <div className="form-section">
      <div className="form-section-title">Originator and CBQA Office Use</div>
      <Row>
        <Col md={4}><FG label="Originator's Signature"><Form.Control size="sm" className="form-control-enhanced" value={f.originator || ''} onChange={(e) => set('originator', e.target.value)} /></FG></Col>
        <Col md={4}><FG label="Title / Role"><Form.Control size="sm" className="form-control-enhanced" value={f.originatorTitle || ''} onChange={(e) => set('originatorTitle', e.target.value)} placeholder="Auditor / Scheduler / Other" /></FG></Col>
        <Col md={4}><FG label="Date"><Form.Control size="sm" className="form-control-enhanced" type="date" value={f.noticeDate || ''} onChange={(e) => set('noticeDate', e.target.value)} /></FG></Col>
      </Row>
      <FG label="New certificate(s) required">
        <div className="d-flex gap-4">
          <Form.Check type="radio" name="newCertificateRequired" id="new-cert-yes" label="Yes" checked={f.newCertificateRequired === 'yes'} onChange={() => set('newCertificateRequired', 'yes')} />
          <Form.Check type="radio" name="newCertificateRequired" id="new-cert-no" label="No" checked={f.newCertificateRequired === 'no' || !f.newCertificateRequired} onChange={() => set('newCertificateRequired', 'no')} />
        </div>
      </FG>
      <FG label="Results of Review / Decision">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={3} value={f.resultsReviewDecision || ''} onChange={(e) => set('resultsReviewDecision', e.target.value)} />
      </FG>
      <Row>
        <Col md={4}><FG label="Completed Administratively By"><Form.Control size="sm" className="form-control-enhanced" value={f.completedAdministrativelyBy || ''} onChange={(e) => set('completedAdministrativelyBy', e.target.value)} /></FG></Col>
        <Col md={4}><FG label="Approved by Technical Manager"><Form.Control size="sm" className="form-control-enhanced" value={f.approvedTechnicalManagerBy || f.approvedBy || ''} onChange={(e) => { set('approvedTechnicalManagerBy', e.target.value); set('approvedBy', e.target.value); }} /></FG></Col>
        <Col md={4}><FG label="Version"><Form.Control size="sm" className="form-control-enhanced" value={f.version || ''} onChange={(e) => set('version', e.target.value)} placeholder="e.g. 1.0" /></FG></Col>
      </Row>
      <FG label="Remarks">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={2} value={f.remarks || ''} onChange={(e) => set('remarks', e.target.value)} />
      </FG>
    </div>
  </>
  );
};

// ── FAPP-07 Finding Form (accepted / Corrective Action) ───────────────────
const CorrectiveActionFindingForm = ({ formData: f, set, auditorOptions, requirementOptions = [], requirementsLoading = false }) => (
  <>
    <div className="form-section">
      <div className="form-section-title">Finding Classification</div>
      <Row>
        <Col md={6}>
          <FG label="Finding Reference" hint="Reference number from the original finding (e.g. FAPP-07-001)">
            <Form.Control size="sm" className="form-control-enhanced" value={f.findingReference || ''} onChange={(e) => set('findingReference', e.target.value)} placeholder="e.g. FAPP-07-001" />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Finding Type" required hint="Severity classification of the nonconformity">
            <FormFieldSelect value={f.findingType || 'MAJOR'} onChange={(e) => set('findingType', e.target.value)}>
              <option value="MAJOR">Major NC</option>
              <option value="MINOR">Minor NC</option>
              <option value="OFI">Opportunity for Improvement</option>
            </FormFieldSelect>
          </FG>
        </Col>
      </Row>
      <FG label="Clause Reference" hint="Applicable clause(s) or annex from the active audit standard">
        <MultiValueSelect
          value={f.clauseReference}
          options={requirementOptions}
          onChange={(value) => set('clauseReference', value)}
          isLoading={requirementsLoading}
          placeholder="Select Clausul / Annex"
          noOptionsMessage={() => 'No clauses or annexes available from backend'}
        />
      </FG>
      <FG label="Finding Description" required hint="Describe the nonconformity or finding observed">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={3} value={f.findingDescription || ''} onChange={(e) => set('findingDescription', e.target.value)} placeholder="Describe the finding in detail..." />
      </FG>
    </div>

    <div className="form-section">
      <div className="form-section-title">Corrective Action Plan</div>
      <FG label="Corrective Action Planned" required hint="Action plan proposed by the auditee to address this finding">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={3} value={f.correctiveActionPlanned || ''} onChange={(e) => set('correctiveActionPlanned', e.target.value)} placeholder="Describe the planned corrective action..." />
      </FG>
      <FG label="Corrective Action Implemented" hint="Actual corrective action taken after closure">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={3} value={f.correctiveActionImplemented || ''} onChange={(e) => set('correctiveActionImplemented', e.target.value)} placeholder="Describe what was actually implemented..." />
      </FG>
      <FG label="Evidence of Completion" hint="Objective evidence confirming the corrective action was implemented">
        <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={2} value={f.evidenceOfCompletion || ''} onChange={(e) => set('evidenceOfCompletion', e.target.value)} placeholder="e.g. Updated procedure, training records, screenshots..." />
      </FG>
    </div>

    <div className="form-section">
      <div className="form-section-title">Timeline & Verification</div>
      <Row>
        <Col md={4}>
          <FG label="Target Date" hint="Agreed completion deadline">
            <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.targetDate || ''} onChange={(e) => set('targetDate', e.target.value)} />
          </FG>
        </Col>
        <Col md={4}>
          <FG label="Completion Date" hint="Actual date corrective action was completed">
            <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.completionDate || ''} onChange={(e) => set('completionDate', e.target.value)} />
          </FG>
        </Col>
        <Col md={4}>
          <FG label="Status" required hint="Current lifecycle status of this finding">
            <FormFieldSelect value={f.status || 'IN_PROGRESS'} onChange={(e) => set('status', e.target.value)}>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="OVERDUE">Overdue</option>
            </FormFieldSelect>
          </FG>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FG label="Verified By" hint="Auditor who verified the corrective action is effective">
            <FormFieldSelect value={f.verifiedBy || ''} onChange={(e) => set('verifiedBy', e.target.value)}>
              <option value="">-- Select --</option>
              {auditorOptions.map((a) => <option key={a.userId || a.id} value={a.userId || a.id}>{a.name || a.fullName}</option>)}
            </FormFieldSelect>
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Remarks" hint="Additional notes or observations">
            <Form.Control size="sm" className="form-control-enhanced textarea" as="textarea" rows={1} value={f.remarks || ''} onChange={(e) => set('remarks', e.target.value)} />
          </FG>
        </Col>
      </Row>
    </div>
  </>
);

// ── FTRD-01 Technical Review ───────────────────────────────────────────────
const TechnicalReviewForm = ({ formData: f, set, setFormData, auditorOptions = [], standardSelectOptions = [], scopeKey = '' }) => {
  const isStage1Scope = String(scopeKey || '').endsWith(':s1');
  // Keep the row component identity stable while its parent state changes.
  // Recreating this component on every keystroke remounts the textarea and
  // causes manually entered technical-review notes to lose focus.
  const setFormDataRef = useRef(setFormData);
  setFormDataRef.current = setFormData;
  const ChecklistRow = useCallback(({ label, partKey, itemKey, value, details }) => {
    const handleValueChange = (newVal) => {
      setFormDataRef.current((p) => ({
        ...p,
        [partKey]: { ...p[partKey], [itemKey]: { ...p[partKey][itemKey], value: newVal } }
      }));
    };
    const handleDetailsChange = (newDetails) => {
      setFormDataRef.current((p) => ({
        ...p,
        [partKey]: { ...p[partKey], [itemKey]: { ...p[partKey][itemKey], details: newDetails } }
      }));
    };
    return (
      <div className="checklist-item p-3">
        <div className="checklist-label">{label}</div>
        <div className="checklist-options">
          <label className="checklist-radio">
            <input
              type="radio"
              name={`${partKey}_${itemKey}`}
              value="YES"
              checked={value === 'YES'}
              onChange={(e) => handleValueChange(e.target.value)}
            />
            YES
          </label>
          <label className="checklist-radio">
            <input
              type="radio"
              name={`${partKey}_${itemKey}`}
              value="NO"
              checked={value === 'NO'}
              onChange={(e) => handleValueChange(e.target.value)}
            />
            NO
          </label>
          <label className="checklist-radio">
            <input
              type="radio"
              name={`${partKey}_${itemKey}`}
              value="N/A"
              checked={value === 'N/A'}
              onChange={(e) => handleValueChange(e.target.value)}
            />
            N/A
          </label>
        </div>
        <div className="checklist-details">
          <Form.Control
            size="sm"
            as="textarea"
            rows={2}
            value={details || ''}
            onChange={(e) => handleDetailsChange(e.target.value)}
            placeholder="Details / Comments"
            className="form-control-enhanced textarea"
          />
        </div>
      </div>
    );
  }, []);

  return (
    <>
      {isStage1Scope && (
        <div className="form-section">
          <div className="form-section-title">Stage 1 Decision</div>
          <FG
            label="Decision"
            required
            hint="Stage 2 remains locked until the Stage 1 technical reviewer selects Continue to Stage 2."
          >
            <FormFieldSelect
             
             
              value={f.decision || ''}
              onChange={(e) => set('decision', e.target.value)}
            >
              <option value="">-- Select --</option>
              <option value="Continue to Stage 2">Continue to Stage 2</option>
              <option value="Need Revision">Need Revision</option>
            </FormFieldSelect>
          </FG>
          {f.decision === 'Continue to Stage 2' && (
            <Alert variant="success" className="py-2 mb-0">
              Saving this decision completes Stage 1 and unlocks Stage 2.
            </Alert>
          )}
          {f.decision === 'Need Revision' && (
            <Alert variant="warning" className="py-2 mb-0">
              Clicking Save will ask for revision notes and send the audit back to the Auditor. Stage 2 remains locked until the decision is changed to Continue to Stage 2.
            </Alert>
          )}
        </div>
      )}
      <div className="form-section">
        <div className="form-section-title">Review Context</div>
        <Row>
          <Col md={6}>
            <FG label="Reviewer" autoPopulated={!!f.reviewer} hint="Name of technical reviewer">
              {auditorOptions.length > 0 ? (
                <FormFieldSelect value={f.reviewer || ''} onChange={(e) => set('reviewer', e.target.value)}>
                  <option value="">-- Select --</option>
                  {auditorOptions.map((a) => { const displayName = getAuditorDisplayName(a); return <option key={a.userId || a.id} value={displayName}>{displayName}</option>; })}
                </FormFieldSelect>
              ) : (
                <Form.Control size="sm" className="form-control-enhanced" value={f.reviewer || ''} onChange={(e) => set('reviewer', e.target.value)} placeholder="Technical Reviewer name" />
              )}
            </FG>
          </Col>
          <Col md={6}>
            <FG label="Review Date">
              <Form.Control size="sm" className="form-control-enhanced" type="date" value={f.reviewDate || ''} onChange={(e) => set('reviewDate', e.target.value)} />
            </FG>
          </Col>
        </Row>
        <FG label="Applicable Standards" autoPopulated={!!f.applicableStandards}>
          {standardSelectOptions.length > 0 ? (
            <MultiValueSelect
              value={f.applicableStandards}
              options={standardSelectOptions}
              onChange={(value) => set('applicableStandards', value)}
              placeholder="Select applicable standards"
              noOptionsMessage={() => 'No standards found for this scope'}
            />
          ) : (
            <Form.Control size="sm" className="form-control-enhanced" value={Array.isArray(f.applicableStandards) ? f.applicableStandards.join(', ') : (f.applicableStandards || '')} onChange={(e) => set('applicableStandards', e.target.value)} placeholder="e.g. ISO 9001:2015" />
          )}
        </FG>
        <FG label="Scope of Certification" autoPopulated={!!f.scopeOfCertification}>
          <Form.Control size="sm" className="form-control-enhanced" as="textarea" rows={2} value={f.scopeOfCertification || ''} onChange={(e) => set('scopeOfCertification', e.target.value)} placeholder="Scope description" />
        </FG>
      </div>
      <Accordion defaultActiveKey="0" className="accordion-enhanced mb-3">
      {/* Part 1: Technical Review & Items Reviewed */}
      <Accordion.Item eventKey="0">
        <Accordion.Header>Part 1 - Technical Review & Items Reviewed</Accordion.Header>
        <Accordion.Body style={{ padding: 0 }}>
          <ChecklistRow
            label="1. Package complete as per FAPP-05 - (Audit Report)"
            partKey="part1"
            itemKey="packageComplete"
            value={f.part1?.packageComplete?.value || ''}
            details={f.part1?.packageComplete?.details || ''}
          />
          <ChecklistRow
            label="2. Technical Review waived"
            partKey="part1"
            itemKey="technicalReviewWaived"
            value={f.part1?.technicalReviewWaived?.value || ''}
            details={f.part1?.technicalReviewWaived?.details || ''}
          />
          <ChecklistRow
            label="3. Audit man-days executed as per the approved assignment document"
            partKey="part1"
            itemKey="auditMandaysExecuted"
            value={f.part1?.auditMandaysExecuted?.value || ''}
            details={f.part1?.auditMandaysExecuted?.details || ''}
          />
          <ChecklistRow
            label="4. Audit team assigned as per the approved assignment document"
            partKey="part1"
            itemKey="auditTeamAssigned"
            value={f.part1?.auditTeamAssigned?.value || ''}
            details={f.part1?.auditTeamAssigned?.details || ''}
          />
          <ChecklistRow
            label="01 - Grading of findings"
            partKey="part1"
            itemKey="gradingOfFindings"
            value={f.part1?.gradingOfFindings?.value || ''}
            details={f.part1?.gradingOfFindings?.details || ''}
          />
          <ChecklistRow
            label="02 - Writing of findings"
            partKey="part1"
            itemKey="writingOfFindings"
            value={f.part1?.writingOfFindings?.value || ''}
            details={f.part1?.writingOfFindings?.details || ''}
          />
          <ChecklistRow
            label="03 - Verification of Nonconformities"
            partKey="part1"
            itemKey="verificationOfNC"
            value={f.part1?.verificationOfNC?.value || ''}
            details={f.part1?.verificationOfNC?.details || ''}
          />
          <ChecklistRow
            label="04 - Completeness of the recertification audit (R)"
            partKey="part1"
            itemKey="completenessRecertification"
            value={f.part1?.completenessRecertification?.value || ''}
            details={f.part1?.completenessRecertification?.details || ''}
          />
          <ChecklistRow
            label="05 - Audit records"
            partKey="part1"
            itemKey="auditRecords"
            value={f.part1?.auditRecords?.value || ''}
            details={f.part1?.auditRecords?.details || ''}
          />
          <ChecklistRow
            label="06 - Validity of certification"
            partKey="part1"
            itemKey="validityOfCertification"
            value={f.part1?.validityOfCertification?.value || ''}
            details={f.part1?.validityOfCertification?.details || ''}
          />
          <ChecklistRow
            label="07 - Audit of mandatory elements"
            partKey="part1"
            itemKey="auditMandatoryElements"
            value={f.part1?.auditMandatoryElements?.value || ''}
            details={f.part1?.auditMandatoryElements?.details || ''}
          />
          <ChecklistRow
            label="08 - Audit Planning"
            partKey="part1"
            itemKey="auditPlanning"
            value={f.part1?.auditPlanning?.value || ''}
            details={f.part1?.auditPlanning?.details || ''}
          />
          <ChecklistRow
            label="10 - Exclusions"
            partKey="part1"
            itemKey="exclusions"
            value={f.part1?.exclusions?.value || ''}
            details={f.part1?.exclusions?.details || ''}
          />
        </Accordion.Body>
      </Accordion.Item>

      {/* Part 2: Corrective Action Review & Approval */}
      <Accordion.Item eventKey="1">
        <Accordion.Header>Part 2 - Corrective Action Review & Approval of Recommendation</Accordion.Header>
        <Accordion.Body>
          <ChecklistRow
            label="1. Corrective action plans accepted (minor NCs)"
            partKey="part2"
            itemKey="capMinorAccepted"
            value={f.part2?.capMinorAccepted?.value || ''}
            details={f.part2?.capMinorAccepted?.details || ''}
          />
          <ChecklistRow
            label="2. Corrective actions accepted (Major NCs)"
            partKey="part2"
            itemKey="caMajorAccepted"
            value={f.part2?.caMajorAccepted?.value || ''}
            details={f.part2?.caMajorAccepted?.details || ''}
          />
          <ChecklistRow
            label="3. Approval of Lead Auditor's recommendation"
            partKey="part2"
            itemKey="approvalLeadAuditor"
            value={f.part2?.approvalLeadAuditor?.value || ''}
            details={f.part2?.approvalLeadAuditor?.details || ''}
          />
        </Accordion.Body>
      </Accordion.Item>

      {/* Part 3: Certification Authority Review */}
      <Accordion.Item eventKey="2">
        <Accordion.Header>Part 3 - Certification Authority Review</Accordion.Header>
        <Accordion.Body>
          <ChecklistRow
            label="1. Acceptability of results"
            partKey="part3"
            itemKey="acceptabilityOfResults"
            value={f.part3?.acceptabilityOfResults?.value || ''}
            details={f.part3?.acceptabilityOfResults?.details || ''}
          />
          <ChecklistRow
            label="2. Re-certification audits"
            partKey="part3"
            itemKey="recertificationAudits"
            value={f.part3?.recertificationAudits?.value || ''}
            details={f.part3?.recertificationAudits?.details || ''}
          />
          <ChecklistRow
            label="3. Current certification still valid"
            partKey="part3"
            itemKey="currentCertValid"
            value={f.part3?.currentCertValid?.value || ''}
            details={f.part3?.currentCertValid?.details || ''}
          />
          <ChecklistRow
            label="4. Lead Auditor's recommendation approved"
            partKey="part3"
            itemKey="leadAuditorApproved"
            value={f.part3?.leadAuditorApproved?.value || ''}
            details={f.part3?.leadAuditorApproved?.details || ''}
          />
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
    {shouldShowFinalCertificationDecision(scopeKey) && (
      <div className="form-section mt-3">
        <div className="form-section-title">Certification Decision</div>
        <FG label="Decision" required hint="Final certification decision by the technical reviewer / certification authority">
          <FormFieldSelect value={f.certificationDecision || ''} onChange={(e) => set('certificationDecision', e.target.value)}>
            <option value="">-- Select --</option>
            <option value="RECOMMEND_GRANT">Recommend Grant Certificate</option>
            <option value="RECOMMEND_CONTINUE_WITHOUT_CERTIFICATE">Recommend Continue to Next Cycle Without Certificate</option>
          </FormFieldSelect>
        </FG>
      </div>
    )}
    </>
  );
};

// ── FTRD-02 Certificate Review and Production ──────────────────────────────
// Detection is shared with the certificate renderer and the save validator
// (utils/isccStandards) so the form, the PDF and Save can never disagree about
// whether a standard is ISCC.
const isIsccCertificateStandard = isIsccStandard;

const resolveCertificateSiteBase = (prev, fallbackSiteLocations = []) => normalizeCertificateSiteLocations(
  Array.isArray(prev.siteLocations) && prev.siteLocations.length > 0
    ? prev.siteLocations
    : fallbackSiteLocations
);

export const updateCertificateSite = (prev, index, changes, fallbackSiteLocations = []) => ({
  ...prev,
  siteLocations: resolveCertificateSiteBase(prev, fallbackSiteLocations).map((site, siteIndex) => (
    siteIndex === index ? { ...site, ...changes } : site
  )),
});

// A new row is seeded with a name rather than left blank: normalizeCertificateSiteLocations
// filters out any site with no name/address at all (hasMeaningfulCertificateSite), so a truly
// empty row would be discarded on the very next render and Add would appear to do nothing.
export const addCertificateSite = (prev, fallbackSiteLocations = []) => {
  const sites = resolveCertificateSiteBase(prev, fallbackSiteLocations);
  const taken = new Set(sites.map((site) => String(site?.siteName || '').trim().toLowerCase()));
  let ordinal = sites.length + 1;
  while (taken.has(`site ${ordinal}`)) ordinal += 1;
  return {
    ...prev,
    siteLocations: [...sites, { siteName: `Site ${ordinal}`, address: '', siteScope: '', siteAdditionDate: '' }],
  };
};

export const removeCertificateSite = (prev, index, fallbackSiteLocations = []) => ({
  ...prev,
  siteLocations: resolveCertificateSiteBase(prev, fallbackSiteLocations)
    .filter((_, siteIndex) => siteIndex !== index),
});

// ISCC material and scope masters. AuditQ renders the Input/Output Material and
// Input/Output Scope cells as selects over these masters, not as free text, so the
// certificate can only ever carry approved material and scope names. Fetched once per
// mount and shared by every row. /md/iscc-scope/all returns a plain list; /md/iscc-material
// is paginated, so a large page size is requested rather than walking the pages.
const useIsccMasters = (enabled) => {
  const [masters, setMasters] = useState({ materials: [], scopes: [], loading: false, error: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setMasters((prev) => ({ ...prev, loading: true, error: null }));
    Promise.all([
      api.get('/md/iscc-material', { params: { page: 0, size: 500 } }),
      api.get('/md/iscc-scope/all'),
    ])
      .then(([materialRes, scopeRes]) => {
        if (cancelled) return;
        const materials = materialRes?.data?.content || materialRes?.data || [];
        const scopes = scopeRes?.data || [];
        setMasters({
          materials: Array.isArray(materials) ? materials : [],
          scopes: Array.isArray(scopes) ? scopes : [],
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        // A master that cannot be loaded must not lock the operator out of the form:
        // the cells fall back to free text (see MaterialCell) so work can continue.
        setMasters({ materials: [], scopes: [], loading: false, error: error?.message || 'Failed to load ISCC masters' });
      });
    return () => { cancelled = true; };
  }, [enabled]);
  return masters;
};

// One material / scope / text cell.
//
// `material` and `scope` columns are master-backed (AuditQ renders them as selects over
// the ISCC masters, never as free text). CreatableSelect rather than a plain Select on
// purpose: if a master is empty or its request failed, the operator can still type a
// value and keep working. The previous version silently swapped in a plain text input in
// that case, which looked exactly like no dropdown had been built at all.
const MaterialCell = ({ column, value, onChange, masters }) => {
  if (column.type === 'text') {
    return <AutoGrowTextarea value={value} onChange={onChange} />;
  }

  const options = (column.type === 'material'
    ? masters.materials.map((item) => item?.name)
    : masters.scopes.map((item) => [item?.code, item?.name].filter(Boolean).join(' - ')))
    .filter(Boolean)
    .map((label) => ({ value: label, label }));

  return (
    <CreatableSelect
      classNamePrefix="audit-material-select"
      styles={materialSelectStyles}
      // A value stored before the master changed is kept as its own option rather than
      // disappearing from the control and being lost on the next save.
      value={value ? { value, label: value } : null}
      options={options}
      onChange={(selected) => onChange({ target: { value: selected?.value || '' } })}
      isLoading={masters.loading}
      menuPlacement="auto"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      placeholder={column.type === 'material' ? 'Select material…' : 'Select scope…'}
      formatCreateLabel={(input) => `Use "${input}"`}
      noOptionsMessage={() => (masters.loading
        ? 'Loading ISCC masters…'
        : 'No ISCC master entries — type to enter a value')}
    />
  );
};

// Material rows hold values as long as a full GHG-methodology sentence, which a
// single-line <input> clips: the operator can neither read back what is stored
// nor check it against the certificate. This grows to fit its content instead,
// so every cell shows its whole value and the row gets taller rather than the
// text getting hidden.
const AutoGrowTextarea = ({ value, onChange, onKeyDown, ...rest }) => {
  const ref = useRef(null);
  // Height is measured, not computed from character count: wrapping depends on
  // the rendered column width, which varies with the viewport.
  const resize = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, []);

  // Re-measure on value changes from outside this cell too (seeding from the
  // proposal, switching standard tabs), not only on keystrokes.
  useLayoutEffect(resize, [value, resize]);

  return (
    <Form.Control
      {...rest}
      as="textarea"
      rows={1}
      ref={ref}
      className="audit-material-cell-input"
      value={value}
      onChange={(event) => {
        onChange(event);
        resize();
      }}
      onKeyDown={(event) => {
        // Wrapping here is presentation only. These values are single-line
        // fields that feed the certificate renderer, and a textarea would
        // otherwise let Enter put a literal newline into the stored value --
        // something the <input> this replaced could never do.
        if (event.key === 'Enter') event.preventDefault();
        if (onKeyDown) onKeyDown(event);
      }}
    />
  );
};

// The three signer selects were verbatim copies of each other; only the field
// name and label ever differed.
const CERTIFICATE_SIGNER_FIELDS = [
  { field: 'producedBy', label: 'Produced By' },
  { field: 'reviewedBy', label: 'Reviewed By' },
  { field: 'approvedBy', label: 'Approved By' },
];

const CertificateReviewProductionForm = ({ formData: f, fieldErrors = {}, set, setFormData, auditorOptions, projectMeta, auditSchedule, scopeLabel = '' }) => {
  const selectedStandards = Array.isArray(f.applicableStandards)
    ? f.applicableStandards
    : String(f.applicableStandards || '').split(',').map((value) => value.trim()).filter(Boolean);
  const selectedStandardLabels = selectedStandards;
  const isIsccCertificate = selectedStandards.length > 0
    ? selectedStandardLabels.some(isIsccCertificateStandard)
    : false;
  const normalizedFtrd02 = normalizeFtrd02FormData(f, projectMeta, auditSchedule, scopeLabel, { seedDefaults: false });
  const certificateSites = normalizedFtrd02.siteLocations;
  const materialRows = Array.isArray(f.isccMaterials) ? f.isccMaterials : normalizedFtrd02.isccMaterials;
  const isIsccPlusCertificate = selectedStandardLabels.some(isIsccPlusStandard);
  const annexRows = Array.isArray(f.annex1Materials) ? f.annex1Materials : [];

  // The Material table is scheme-specific -- ISCC EU, Japan-FIT, CORSIA and the CORSIA
  // low-LUC variant each have their own columns (see utils/isccMaterialSchemes, ported
  // from AuditQ's certificate_production_draft). The first ISCC standard on the
  // certificate decides the layout; a grouped FTRD-02 edits one standard per tab, so
  // there is only ever one.
  const isccStandardLabel = selectedStandardLabels.find(isIsccCertificateStandard) || '';
  const materialColumns = resolveMaterialColumns(isccStandardLabel, {
    addOn: f.isccAddOn || projectMeta?.isccAddOn || '',
    scopes: [f.certificateScope, scopeLabel, ...(certificateSites || []).map((site) => site?.siteScope)],
  });
  // Annex I is ISCC PLUS only AND only when the operator ticks it -- it is a stored
  // decision, not something derived from the scheme.
  const annex1Available = supportsAnnex1(isccStandardLabel);
  const annex1Enabled = isAnnex1Active(isccStandardLabel, f.isccAnnex1 === true);

  const isccMasters = useIsccMasters(isIsccCertificate);
  // "{code} - {name}", the label AuditQ prints for a scope.
  const isccScopeOptions = isccMasters.scopes
    .map((scope) => [scope?.code, scope?.name].filter(Boolean).join(' - '))
    .filter(Boolean)
    .map((label) => ({ value: label, label }));
  const rowTemplate = (annex) => buildEmptyMaterialRow(annex ? ANNEX_1_COLUMNS : materialColumns);
  const updateMaterialRow = (field, index, value, annex = false) => {
    const key = annex ? 'annex1Materials' : 'isccMaterials';
    setFormData((prev) => {
      const rows = Array.isArray(prev[key]) ? prev[key] : (annex ? annexRows : materialRows);
      const nextRows = rows.length > 0 ? rows : [rowTemplate(annex)];
      return { ...prev, [key]: nextRows.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row) };
    });
  };
  const addMaterialRow = (annex = false) => setFormData((prev) => ({
    ...prev,
    [annex ? 'annex1Materials' : 'isccMaterials']: [
      ...(Array.isArray(prev[annex ? 'annex1Materials' : 'isccMaterials'])
        ? prev[annex ? 'annex1Materials' : 'isccMaterials']
        : (annex ? annexRows : materialRows)),
      rowTemplate(annex),
    ],
  }));
  const removeMaterialRow = (index, annex = false) => setFormData((prev) => {
    const key = annex ? 'annex1Materials' : 'isccMaterials';
    return { ...prev, [key]: (Array.isArray(prev[key]) ? prev[key] : []).filter((_, rowIndex) => rowIndex !== index) };
  });

  return (
  <>
    <SectionHeader>Certificate Core</SectionHeader>
    <Row>
      <Col md={6}>
        <FG label="Certificate Number" required fieldKey="certificateNumber" error={fieldErrors.certificateNumber} autoPopulated={!!f.certificateNumber} hint="Use final certificate number or draft numbering for review.">
          <Form.Control isInvalid={!!fieldErrors.certificateNumber} value={f.certificateNumber || ''} onChange={(e) => set('certificateNumber', e.target.value)} placeholder="Auto-generated or manual" />
        </FG>
      </Col>
      <Col md={6}>
        <FG label="Client / Company Name" required fieldKey="clientName" error={fieldErrors.clientName} autoPopulated={!!f.clientName}>
          <Form.Control isInvalid={!!fieldErrors.clientName} value={f.clientName || ''} onChange={(e) => set('clientName', e.target.value)} />
        </FG>
      </Col>
    </Row>
    <FG label="Sites" hint="Editable certificate appendix data. Site addition date and site scope are specific to FTRD-02.">
      {certificateSites.length === 0 && (
        <div className="text-muted small border rounded p-3 mb-2">
          No sites on this certificate yet. Add the sites that should appear in the certificate appendix.
        </div>
      )}
      {certificateSites.map((site, index) => (
        <div className="border rounded p-2 mb-2" key={`${site?.id || site?.siteName || 'site'}-${index}`}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="dynamic-row-index">Site {index + 1}</span>
            <Button
              variant="outline-danger"
              onClick={() => setFormData((prev) => removeCertificateSite(prev, index, certificateSites))}
              title={`Remove site ${index + 1}`}
            >
              <Trash2 size={16} />
            </Button>
          </div>
          <Row>
            <Col md={4}><Form.Label className="small mb-1" htmlFor={`site-${index}-name`}>Site Name / Location</Form.Label><Form.Control id={`site-${index}-name`} value={site?.siteName || ''} onChange={(e) => setFormData((prev) => updateCertificateSite(prev, index, { siteName: e.target.value }, certificateSites))} /></Col>
            <Col md={4}><Form.Label className="small mb-1" htmlFor={`site-${index}-added`}>Site Addition Date</Form.Label><Form.Control id={`site-${index}-added`} type="date" value={site?.siteAdditionDate || ''} onChange={(e) => setFormData((prev) => updateCertificateSite(prev, index, { siteAdditionDate: e.target.value }, certificateSites))} /></Col>
            <Col md={4}><Form.Label className="small mb-1" htmlFor={`site-${index}-scope`}>Site Scope</Form.Label><Form.Control id={`site-${index}-scope`} value={site?.siteScope || ''} onChange={(e) => setFormData((prev) => updateCertificateSite(prev, index, { siteScope: e.target.value }, certificateSites))} /></Col>
          </Row>
          <Form.Label className="small mb-1 mt-2" htmlFor={`site-${index}-address`}>Site Address</Form.Label>
          <Form.Control id={`site-${index}-address`} as="textarea" rows={2} value={site?.address || ''} onChange={(e) => setFormData((prev) => updateCertificateSite(prev, index, { address: e.target.value }, certificateSites))} />
        </div>
      ))}
      <Button
        variant="outline-primary"
        onClick={() => setFormData((prev) => addCertificateSite(prev, certificateSites))}
      >
        <Plus size={16} className="me-1" />Add site
      </Button>
    </FG>
    {/* AuditQ hides the free-text Scope input for an ISCC certificate and replaces it with
        a searchable multi-select over the ISCC scope master, labelled "{code} - {name}",
        then prints the selections as a list on the certificate
        (certificate_production_draft.blade.php:1289-1307, detail_iscc.blade.php:85).

        Deliberately bound to certificateScope itself rather than a new field: it stays a
        newline-joined STRING, so save validation, the issuance payload, the PDF renderer
        and the per-standard readiness gate all keep working untouched, and a scope saved
        as free text before this existed round-trips into chips via splitStoredMultiValue. */}
    {isIsccCertificate ? (
      <FG label="Certificate Scope" required fieldKey="certificateScope" error={fieldErrors.certificateScope} hint="Select the ISCC scopes for this certificate. They are printed as a list, one per line.">
        <MultiValueSelect
          value={f.certificateScope || ''}
          options={isccScopeOptions}
          onChange={(values) => set('certificateScope', values.join('\n'))}
          isLoading={isccMasters.loading}
          placeholder="Select ISCC scope(s)…"
          noOptionsMessage={() => (isccMasters.loading
            ? 'Loading ISCC scopes…'
            : 'No ISCC scope master data configured')}
        />
      </FG>
    ) : (
      <FG label="Certificate Scope" required fieldKey="certificateScope" error={fieldErrors.certificateScope} hint="Used directly in live certificate preview.">
        <Form.Control isInvalid={!!fieldErrors.certificateScope} as="textarea" rows={2} value={f.certificateScope || ''} onChange={(e) => set('certificateScope', e.target.value)} />
      </FG>
    )}
    {isIsccCertificate && (
      <>
        <SectionHeader>Material</SectionHeader>
        <div className="small text-muted mb-2">
          Columns follow the {isccStandardLabel || 'ISCC'} certificate layout. Input/Output Material and
          Scope are chosen from the ISCC masters.
        </div>
        {isccMasters.error ? (
          <div className="small text-danger mb-2">
            ISCC masters could not be loaded ({isccMasters.error}). The Material and Scope dropdowns are
            empty — you can still type a value into them.
          </div>
        ) : (!isccMasters.loading && isccMasters.materials.length === 0 && isccMasters.scopes.length === 0) ? (
          // Loaded successfully but empty. Worth saying out loud: an empty dropdown is
          // indistinguishable from a broken one, and this used to degrade to a plain text
          // input with no explanation at all.
          <div className="small text-warning mb-2">
            No ISCC material or scope master data is configured, so the dropdowns have nothing to offer —
            type the values you need.
          </div>
        ) : null}
        <div className="table-responsive border rounded mb-2">
          <Table bordered hover size="sm" className="mb-0 audit-material-table">
            <thead className="table-light"><tr>
              {materialColumns.map((column) => <th key={column.field} className="small">{column.label}</th>)}
              <th className="small audit-material-table__action" />
            </tr></thead>
            <tbody>
              {(materialRows.length > 0 ? materialRows : [rowTemplate(false)]).map((row, index) => (
                <tr key={`material-${index}`}>
                  {materialColumns.map((column) => (
                    <td key={column.field} className="p-1">
                      <MaterialCell
                        column={column}
                        value={row?.[column.field] || ''}
                        onChange={(e) => updateMaterialRow(column.field, index, e.target.value)}
                        masters={isccMasters}
                      />
                    </td>
                  ))}
                  <td className="p-1"><Button variant="outline-danger" onClick={() => removeMaterialRow(index)} title="Remove material"><Trash2 size={16} /></Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <Button variant="outline-primary" className="mb-2" onClick={() => addMaterialRow()}><Plus size={16} className="me-1" />Add material</Button>
        {annex1Available && (
          <>
            <SectionHeader>Annex I Material</SectionHeader>
            {/* AuditQ gates Annex I on `is_iscc_plus && iscc_annex_1` -- being an ISCC PLUS
                certificate offers the annex, but the operator decides whether it is issued
                with one. Storing that decision is what drives isccAnnex1Required downstream. */}
            <Form.Check
              type="checkbox"
              id="iscc-annex-1"
              className="mb-2"
              label="Include Annex I on this ISCC PLUS certificate"
              checked={f.isccAnnex1 === true}
              onChange={(e) => set('isccAnnex1', e.target.checked)}
            />
            {annex1Enabled && (
              <>
                <div className="small text-muted mb-2">
                  Annex I has its own column set — Add-ons, Raw material category, SAI FSA and FEFAC.
                </div>
                <div className="table-responsive border rounded mb-2">
                  <Table bordered hover size="sm" className="mb-0 audit-material-table">
                    <thead className="table-light"><tr>
                      {ANNEX_1_COLUMNS.map((column) => <th key={column.field} className="small">{column.label}</th>)}
                      <th className="small audit-material-table__action" />
                    </tr></thead>
                    <tbody>
                      {annexRows.length === 0 && (
                        <tr>
                          <td colSpan={ANNEX_1_COLUMNS.length + 1} className="text-muted small text-center py-3">
                            No Annex I materials yet.
                          </td>
                        </tr>
                      )}
                      {annexRows.map((row, index) => (
                        <tr key={`annex-material-${index}`}>
                          {ANNEX_1_COLUMNS.map((column) => (
                            <td key={column.field} className="p-1">
                              <MaterialCell
                                column={column}
                                value={row?.[column.field] || ''}
                                onChange={(e) => updateMaterialRow(column.field, index, e.target.value, true)}
                                masters={isccMasters}
                              />
                            </td>
                          ))}
                          <td className="p-1"><Button variant="outline-danger" onClick={() => removeMaterialRow(index, true)} title="Remove Annex I material"><Trash2 size={16} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <Button variant="outline-primary" className="mb-2" onClick={() => addMaterialRow(true)}><Plus size={16} className="me-1" />Add Annex I material</Button>
              </>
            )}
          </>
        )}
      </>
    )}
    {/* Sub-scope, its Scope Verification follow-up, and NACE/EAC are ISO concepts:
        an ISCC certificate has no sub-scope and carries ISCC scope codes rather than
        NACE/EAC industry classification. Certification Decision was removed outright --
        it is not an FTRD-02 field for either scheme (FTRD-01 owns the technical-review
        decision, with its own RECOMMEND_* values). */}
    {!isIsccCertificate && (
      <>
        <Row>
          <Col md={6}>
            <FG label="Applicable for Sub Scope">
              <FormFieldSelect value={f.applicableForSubScope || 'No'} onChange={(e) => set('applicableForSubScope', e.target.value)}>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </FormFieldSelect>
            </FG>
          </Col>
        </Row>

        {String(f.applicableForSubScope || 'No').toLowerCase() === 'yes' && (
          <>
            <SectionHeader>Scope Verification</SectionHeader>
            <FG label="Verification of Scope" hint="Summarize verification result before certificate issuance.">
              <Form.Control as="textarea" rows={2} value={f.verificationOfScope || ''} onChange={(e) => set('verificationOfScope', e.target.value)} />
            </FG>
          </>
        )}
        <FG label="NACE / EAC Code(s)" autoPopulated={!!f.naceCode} hint="Industry classification codes applicable to this certification">
          <Form.Control value={f.naceCode || ''} onChange={(e) => set('naceCode', e.target.value)} placeholder="e.g. C28, E36" />
        </FG>
      </>
    )}
    <SectionHeader>Certificate Date</SectionHeader>
    {isIsccCertificate ? (
      <Row>
        <Col md={6}>
          <FG label="Valid From">
            <Form.Control type="date" value={f.validFrom || ''} onChange={(e) => set('validFrom', e.target.value)} />
          </FG>
        </Col>
        <Col md={6}>
          <FG label="Valid To" fieldKey="validTo" error={fieldErrors.validTo}>
            <Form.Control isInvalid={!!fieldErrors.validTo} type="date" value={f.validTo || ''} onChange={(e) => set('validTo', e.target.value)} />
          </FG>
        </Col>
      </Row>
    ) : (
      <>
        <Row>
          <Col md={6}>
            <FG label="Initial Date">
              <Form.Control type="date" value={f.initialDate || ''} onChange={(e) => set('initialDate', e.target.value)} />
            </FG>
          </Col>
          <Col md={6}>
            <FG label="Latest Certification Date">
              <Form.Control type="date" value={f.latestCertificationDate || ''} onChange={(e) => set('latestCertificationDate', e.target.value)} />
            </FG>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FG label="Issue Date">
              <Form.Control type="date" value={f.issueDate || ''} onChange={(e) => set('issueDate', e.target.value)} />
            </FG>
          </Col>
          <Col md={6}>
            <FG label="Expired Date" fieldKey="expiredDate" error={fieldErrors.expiredDate}>
              <Form.Control isInvalid={!!fieldErrors.expiredDate} type="date" value={f.expiredDate || ''} onChange={(e) => set('expiredDate', e.target.value)} />
            </FG>
          </Col>
        </Row>
      </>
    )}

    <SectionHeader>Issuance Roles & Signature</SectionHeader>
    <Row>
      {CERTIFICATE_SIGNER_FIELDS.map(({ field, label }) => (
        <Col md={4} key={field}>
          <FG label={label} autoPopulated={!!f[field]}>
            <FormFieldSelect value={f[field] || ''} onChange={(e) => set(field, e.target.value)}>
              <option value="">-- Select --</option>
              {auditorOptions.map((auditor) => {
                const displayName = getAuditorDisplayName(auditor);
                return <option key={auditor.userId || auditor.id} value={displayName}>{displayName}</option>;
              })}
            </FormFieldSelect>
          </FG>
        </Col>
      ))}
    </Row>
    <Row>
      <Col md={6}>
        <FG label="Signature Title" hint="Printed below the signer name on certificate draft.">
          <Form.Control value={f.signatureTitle || ''} onChange={(e) => set('signatureTitle', e.target.value)} placeholder="Certification Manager" />
        </FG>
      </Col>
    </Row>
    {/* ISCC certificates carry no remarks block. */}
    {!isIsccCertificate && (
      <FG label="Remarks">
        <Form.Control as="textarea" rows={2} value={f.remarks || ''} onChange={(e) => set('remarks', e.target.value)} />
      </FG>
    )}
  </>
  );
};

export default DocumentFormModal;
